// ===== Packages =====

import React, {
    useRef,
    useMemo,
    useState,
    useEffect,
    useCallback,
    useLayoutEffect,
}                                       from 'react';
import { createPortal }                 from 'react-dom';
import styled, { keyframes }           from 'styled-components';
import { ReactSVG }                     from 'react-svg';
import { useDropzone }                  from 'react-dropzone';
import { v4 as uuidv4 }                 from 'uuid';
// eslint-disable-next-line import/no-extraneous-dependencies
import mime                             from 'mime';
import {
    useFocused,
    useSelected,
}                                       from 'slate-react';
import {
    ref,
    getStorage,
    getDownloadURL,
    StorageError,
    UploadTaskSnapshot,
    StorageReference,
}                                       from 'firebase/storage';
import {
    onAuthStateChanged,
}                                       from 'firebase/auth';
import {
    doc,
    getFirestore,
    onSnapshot,
    Unsubscribe,
}                                       from 'firebase/firestore';
import ShortUniqueId                    from 'short-unique-id';

// ===== Firebase =====

import fire                             from '../../../fire';

// ===== Components =====

import { Transition }                   from '../../Transition';
import Modal                            from '../../Modal';
import Tooltip                          from '../../Tooltip';
import {
    Button,
    GenericModal,
    RecordingLoader,
}                                       from '../helpers';
import PlaybackSpeedButtonLabel          from '../../PlaybackSpeedButtonLabel';
import Spinner                          from '../../Spinner';

// ===== Constants =====

import {
    AUDIO_NOTE_GENERAL_ERROR,
    AUDIO_NOTE_BROWSER_NOT_SUPPORTED,
    AUDIO_NOTE_PERMISSION_DENIED_ERROR,
    AUDIO_NOTE_RECORDING_DEVICE_BUSY,
    AUDIO_NOTE_RECORDING_DEVICE_NOT_FOUND,
    AUDIO_NOTE_UNSUPPORTED_FILE_TYPE,
    AUDIO_NOTE_FETCH_FILE_ERROR,
    AUDIO_NOTE_FETCH_POST_AUDIO_ERROR,
}                                       from '../../../constants/notificationMessages';
import {
    BODY_FONT_SIZE,
    DEFAULT_FONT_SIZE,
    MILLISECONDS_IN_A_SECOND,
    EDITOR_SELECTION_LIGHTNESS_VALUE,
    BUTTON_CONTAINER_LIGHTNESS_VALUE,
    EDITOR_SELECTION_BORDER_THICKNESS,
    HOVER_TARGET_CLASSNAME,
    CURSOR_Z_INDEX,
    DEFAULT_AUDIO_VOLUME,
    EXTENDED_SNACKBAR_VISIBLE_DURATION,
    SUPPORT_EMAIL_ADDRESS,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
}                                       from '../../../constants/generalConstants';
import KEYCODE                          from '../../../constants/keycodes';
import CURSOR_SIGN                      from '../../../constants/cursorSigns';
import FONT_TYPE                        from '../../../constants/fontType';
import {
    TOOLTIP_TRANSITION_DURATION,
}                                       from '../helpers/constants';

// ===== Enums =====

import {
    AUDIO_NOTE_RECORDING_STATE,
    AUDIO_NOTE_REMOVAL_MODAL_MODE,
    AUDIO_NOTE_PENDING_SAVE_STEP,
    AUDIO_TRANSCRIPTION_JOB_STATUS,
    TOOLTIP_TYPE,
    BUTTON_TYPE,
    AUDIO_NOTE_SKIP_DIRECTION,
    MEDIA_TYPE,
    STORAGE_ENTITY,
    STORAGE_ERROR_CODE,
    FIRESTORE_COLLECTION,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    EDITOR_CONTEXT_TYPE,
    USER_ACTION_TYPE,
}                                       from '../../../enums';

// ===== Interfaces =====

import {
    ICoord,
    IAudioNoteData,
    IAudioNotePendingSaveState,
    IReadOnlyAudioNotePlaybackState,
    IMediaItem,
    IPartialUserItem,
    IEditorAudioNoteItem,
    ISnackbarItem,
    ITranscriptionWord,
}                                       from '../../../interfaces';

// ===== Services =====

import {
    moveCursorToEnd,
    setColorLightness,
    uploadToCloudStorage,
    getStorageErrorMessage,
    getMediaStorageBucket,
    playAudio,
    recordUserAction,
    fontColorDiscriminator,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    createMedia,
    updateMedia,
    requestGeneratedPostAudioNoteSpeech,
    getEditorAudioNoteItem,
    roundToNDecimals,
    transcodeAudioForUpload,
    isMobile,
    isTestMode,
    hexToRgb,
    rgbToHsl,
    validHexCode,
    sanitizeAudioNoteDescription,
    resolveAudioNoteDetailsSource,
    getTranscriptionWordTimestampMs,
    resolveActiveTranscriptionWordIndex,
    formatAudioTimestamp,
    getNextAudioPlaybackSpeed,
}                                       from '../../../services';
import trimAudioNoteTranscriptToSourceText
    from '../../../services/trimAudioNoteTranscriptToSourceText';
import shouldPlayPreviewAudioEndPrompt
    from '../../../services/shouldPlayPreviewAudioEndPrompt';
import {
    AUDIO_NOTE_RECORDING_WAVEFORM_CONFIG,
    buildAudioWaveformBarsFromChannelData,
    buildAudioWaveformBarsFromSampleLevels,
    buildScrollingAudioWaveformBarsFromCapturedBars,
    buildScrollingAudioWaveformBarsFromSampleLevels,
    buildDeterministicAudioWaveformBars,
    buildSilentAudioWaveformBars,
    COMPLETED_AUDIO_NOTE_WAVEFORM_BUILD_OPTIONS,
    DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    DEFAULT_AUDIO_WAVEFORM_BAR_GAP_PX,
    DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT,
    DEFAULT_AUDIO_WAVEFORM_BAR_WIDTH_PX,
    fitAudioWaveformBarsToBarCount,
    getAudioWaveformSampleLevelFromFloatTimeDomainData,
    hasMeaningfulAudioWaveformBars,
    LIVE_AUDIO_NOTE_WAVEFORM_BUILD_OPTIONS,
    resolveAudioWaveformBarCountFromWidth,
    resolveAudioWaveformSampleIntervalMs,
}                                       from '../../../services/buildAudioWaveformBars';

// ===== Hooks =====

import {
    usePortal,
    useKeyPress,
    useInterval,
    useTimeout,
    useEventListener,
    useRemovePasteStyling,
    useLatest,
}                                       from '../../../hooks';
import {
    getPlayableAudioStoragePath,
    inferAudioMimeTypeFromUrl,
    isHttpAudioUrl,
    isProtectedPostAudioStoragePath,
    normalizeRecordedAudioBlobMimeType,
    shouldResolveRemotePlaybackUrl,
    resolveAudioPlaybackUrl,
}                                       from './audioNotePlayback';
import {
    primeAudioElementForAsyncPlaybackStart,
    waitForAudioPlaybackStart,
}                                       from './audioNotePlaybackGesture';
import {
    resolveHydratedAudioLengthMs,
    resolvePersistedAudioNoteDurationMs,
    resolveAudioNotePlaybackFilePath,
    shouldRenderAudioNoteActionRow,
    shouldRenderAudioNotePlaybackChrome,
    shouldRenderAudioNotePlaybackControls,
    shouldTreatAudioNoteAsNew,
}                                       from './audioNoteReadOnlyState';
import {
    shouldDecodeAudioNoteWaveformInBrowser,
    shouldDecodeSavedAudioNoteWaveformInBrowser,
}                                       from './audioNoteWaveformDecodePolicy';
import {
    getAudioNoteTranscriptionStatusLabel,
    isAudioNoteTranscriptionStatusFailure,
    isAudioNoteTranscriptionStatusPending,
    isAudioNoteTranscriptionStatusStalled,
    shouldShowAudioNoteTranscriptionStatus,
}                                       from './audioNoteTranscriptionStatus';
import {
    resolveMostRecentAudioNoteTranscriptionJob,
}                                       from './audioNoteTranscriptionJobMerge';
import {
    createPendingAudioNoteSaveState,
    isPendingAudioNoteSaveComplete,
    markPendingAudioNoteSaveStepComplete,
}                                       from './audioNoteSaveState';
import {
    ANNOTATION_AUDIO_NOTE_WIDTH_PX,
    MIN_AUDIO_NOTE_INLINE_PLAYBACK_META_WIDTH_PX,
    READ_ONLY_ANNOTATION_AUDIO_NOTE_WIDTH_PX,
    resolveAudioNoteLayout,
    resolveAnnotationAudioNoteWidthPx,
}                                       from './audioNoteLayout';

// ===== Sounds =====

import InputClick                       from '../../../sounds/button_click.mp3';

// ===== Icons =====

import SaveIcon                         from '../../../images/editor/floppy-disk.svg';
import TrashIcon                        from '../../../images/editor/trash.svg';
import PlayIcon                         from '../../../images/editor/play-fill.svg';
import PauseIcon                        from '../../../images/editor/pause-fill.svg';
import SpinnerIcon                      from '../../../images/editor/spinner.svg';
import StopIcon                         from '../../../images/editor/stop-fill.svg';
import MicrophoneIcon                   from '../../../images/editor/mic.svg';
import RedoIcon                         from '../../../images/editor/redo.svg';
import PencilIcon                       from '../../../images/editor/pencil.svg';
import ImportIcon                       from '../../../images/editor/upload.svg';
import DownloadIcon                     from '../../../images/editor/download-arrow.svg';
import SkipForwardIcon                  from '../../../images/editor/skip-forward-15.svg';
import SkipBackwardIcon                 from '../../../images/editor/skip-backward-15.svg';
import TextIcon                         from '../../../images/editor/text.svg';
import FileIcon                         from '../../../images/file.svg';
import InfoIcon                         from '../../../images/info.svg';
import SpeakerIcon                      from '../../../images/8-bit_speaker.svg';

// ===== Styles =====

import { theme as themeObj }            from '../../../themes/theme-context';
import {
    DropzoneInput,
    basicEditorFontStyles,
}                                       from '../../../styles';

// ===== Functions =====

export const getDefaultAudioTitle = (): string => {
    const now = new Date();
    const defaultTitle = `New Audio Note [${
        now.getUTCFullYear()
    }-${
        now.getUTCMonth() + 1 > 9 ? now.getUTCMonth() + 1 : `0${now.getUTCMonth() + 1}`
    }-${
        now.getUTCDate()
    } ${
        now.getUTCHours() > 9 ? now.getUTCHours() : `0${now.getUTCHours()}`
    }:${
        now.getUTCMinutes() > 9 ? now.getUTCMinutes() : `0${now.getUTCMinutes()}`
    }:${
        now.getUTCSeconds() > 9 ? now.getUTCSeconds() : `0${now.getUTCSeconds()}`
    }]`;
    return defaultTitle;
};

/**
 * Audio-note preview bugs are hard to reason about from UI symptoms alone.
 * Keep these logs opt-in so local verification is easy without permanently
 * adding console noise to read-only playback.
 */
const shouldLogAudioNotePreviewDebug = (): boolean => (
    process.env.REACT_APP_ENABLE_PREVIEW_DEBUG_LOGS === 'true'
    || (
        process.env.REACT_APP_ENABLE_AUDIO_NOTE_PREVIEW_DEBUG_LOGS === 'true'
    )
);

const createAudioWaveformTimeDomainData = (
    analyserNode: AnalyserNode,
): Parameters<AnalyserNode['getFloatTimeDomainData']>[0] => (
    new Float32Array(analyserNode.fftSize) as Parameters<AnalyserNode['getFloatTimeDomainData']>[0]
);

/**
 * Keep preview debugging behind a narrow helper so we can remove it cleanly
 * once the anonymous read-only flow is stable again.
 */
const logAudioNotePreviewDebug = (
    label: string,
    payload: Record<string, unknown>,
): void => {
    if (!shouldLogAudioNotePreviewDebug()) {
        return;
    }

    // eslint-disable-next-line no-console
    console.info(`[AudioNotePreview] ${label}`, payload);
};

/**
 * Audio-note durations are persisted as milliseconds so the UI can show the
 * total length before the browser has loaded media metadata.
 */
const sanitizeAudioNoteDurationMs = (durationMs?: number | null): number | undefined => {
    if (!Number.isFinite(durationMs)) {
        return undefined;
    }

    const sanitizedDurationMs = Math.max(0, Math.round(durationMs || 0));
    return sanitizedDurationMs > 0
        ? sanitizedDurationMs
        : undefined;
};

// ===== Initialize Firebase =====

const { auth, app } = fire();

// ===== Resources =====

// 1) Accurate JavaScript Timer: https://medium.com/@olinations/stopwatch-script-that-keeps-accurate-time-a9b78f750b33
// 2) List of Mime Types: http://help.dottoro.com/lapuadlp.php
// 3) Useful Link: https://github.com/GersonRosales/Record-Audios-and-Videos-with-getUserMedia/blob/master/example_1.html
// 4) Errors while playing audio: https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
// 5) Safari Considerations:
// https://developer.apple.com/library/archive/documentation/AudioVideo/Conceptual/Using_HTML5_Audio_Video/Device-SpecificConsiderations/Device-SpecificConsiderations.html
// 5.1) NOTE: Safari on Desktop (and presumebly iOS) cannot play webm audio
// - See: https://discussions.apple.com/thread/8309003
// - See: https://www.reddit.com/r/apple/comments/40b3y3/this_is_how_you_can_play_webm_in_safari/
// 5.2) NOTE: Safari on Desktop does not support navigator.getUserMedia and thus cant record. Not yet tested in iOS
// - More on Safari support: https://stackoverflow.com/questions/42221646/voice-recording-on-iphone-by-using-safari-and-html5
// - Determining if we can record: https://stackoverflow.com/questions/30047056/is-it-possible-to-check-if-the-user-has-a-camera-and-microphone-and-if-the-permi
// Useful Resource on Audio: https://kaliatech.github.io/web-audio-recording-tests/dist/#/
// - There is a way to do visualizations and different encodings
// 6) Detecting playable audio formats: https://davidwalsh.name/detect-supported-audio-formats-javascript

const READ_ONLY_AUDIO_NOTE_MIN_VISIBLE_RATIO = 0.5;
const READ_ONLY_AUDIO_NOTE_MIN_VISIBLE_PIXELS = 2 * BODY_FONT_SIZE;
/**
 * Read-only transcript highlighting is driven by React state instead of the
 * raw audio element clock, so give the next word a modest head start to offset
 * render cadence and avoid visually lagging the spoken audio.
 */
const READ_ONLY_AUDIO_NOTE_TRANSCRIPT_HIGHLIGHT_LEAD_MS = 150;
const READ_ONLY_AUDIO_NOTE_TRANSCRIPT_TRAILING_HOLD_MS = 150;
const POST_CONTENT_DICTATION_SOURCE_SYNC_DEBOUNCE_MS = 2500;

/**
 * Treat a read-only audio note as visible only when a meaningful portion of
 * the inline player remains inside the reader viewport. This makes the
 * floating mini-player appear once the note is mostly scrolled away instead of
 * waiting for the older loose IntersectionObserver ratio heuristic.
 */
export const resolveReadOnlyAudioNoteVisibilityFromRects = (params: {
    noteTop: number,
    noteBottom: number,
    noteHeight: number,
    rootTop: number,
    rootBottom: number,
}): boolean => {
    const {
        noteTop,
        noteBottom,
        noteHeight,
        rootTop,
        rootBottom,
    } = params;
    const visibleTop = Math.max(noteTop, rootTop);
    const visibleBottom = Math.min(noteBottom, rootBottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);
    const requiredVisibleHeight = Math.max(
        READ_ONLY_AUDIO_NOTE_MIN_VISIBLE_PIXELS,
        noteHeight * READ_ONLY_AUDIO_NOTE_MIN_VISIBLE_RATIO,
    );

    return visibleHeight >= requiredVisibleHeight;
};

/**
 * IntersectionObserver remains a useful coarse trigger, but we still reduce
 * the final decision to explicit geometry so scroll-container edge cases use
 * the same visibility rule as manual scroll/resize measurements.
 */
export const resolveReadOnlyAudioNoteVisibility = (
    entry: IntersectionObserverEntry,
): boolean => {
    if (!entry.isIntersecting) {
        return false;
    }

    const fallbackViewportBottom = typeof window === 'undefined'
        ? 0
        : window.innerHeight;
    return resolveReadOnlyAudioNoteVisibilityFromRects({
        noteTop: entry.boundingClientRect.top,
        noteBottom: entry.boundingClientRect.bottom,
        noteHeight: entry.boundingClientRect.height,
        rootTop: entry.rootBounds?.top ?? 0,
        rootBottom: entry.rootBounds?.bottom ?? fallbackViewportBottom,
    });
};

AudioNote.defaultProps = {
    buttonWidth: undefined,
    readOnly: false,
    isAuthor: false,
    uploaded: false,
    userIsAdmin: false,
    autoRecord: false,
    handleAutoRecord: null,
    revealOptionsAbove: false,
    autoRecordIdentifier: null,
    small: false,
    setAudioNoteDirtyState: undefined,
    setAudioNoteSavingState: undefined,
    requestEditorDismissalSuppression: undefined,
    viewportRootRef: null,
    onReadOnlyPlaybackStateChange: undefined,
    isPostContentDictation: false,
    durationMs: undefined,
    waveformBars: undefined,
    postContentTranscriptText: '',
    readOnlyUsesPreviewSource: false,
    previewAudioEndPromptText: '',
    chapterId: undefined,
    sectionId: undefined,
};

interface IReadOnlyTranscriptProps {
    visible: boolean,
    hasTranscript: boolean,
    transitionDuration: number,
    transcriptWords: ITranscriptionWord[],
    transcriptPhrases: string[],
    activeTranscriptWordIndex: number | null,
    highlightingEnabled: boolean,
    color: string,
    onTranscriptWordMouseEnter: (e: React.MouseEvent | React.TouchEvent) => void,
    onTranscriptWordMouseLeave: (e: React.MouseEvent | React.TouchEvent) => void,
    onTranscriptWordPress: (word: ITranscriptionWord) => void,
}

/**
 * Large word-timed transcripts can contain thousands of spans. Keep that list
 * behind a memo boundary so the full transcript tree does not re-render on
 * every playback clock tick while the active word index stays the same.
 */
const ReadOnlyTranscript = React.memo(({
    visible,
    hasTranscript,
    transitionDuration,
    transcriptWords,
    transcriptPhrases,
    activeTranscriptWordIndex,
    highlightingEnabled,
    color,
    onTranscriptWordMouseEnter,
    onTranscriptWordMouseLeave,
    onTranscriptWordPress,
}: IReadOnlyTranscriptProps): JSX.Element | null => {
    const transcriptWordRefs = useRef<Array<HTMLSpanElement | null>>([]);
    const previouslyHighlightedTranscriptWordIndexRef = useRef<number | null>(null);
    const transcriptWordItems = useMemo(() => {
        if (!visible || !hasTranscript) {
            return [];
        }

        return transcriptWords.map((word) => {
            const wordStartTimestamp = getTranscriptionWordTimestampMs({
                word,
                edge: 'start',
            });

            return {
                key: `word=${word.word},time=${wordStartTimestamp}`,
                label: `${word.word} `,
                word,
            };
        });
    }, [
        hasTranscript,
        transcriptWords,
        visible,
    ]);
    const updateTranscriptWordHighlight = useCallback((
        index: number | null,
        highlighted: boolean,
    ): void => {
        if (index == null || index < 0) {
            return;
        }

        const transcriptWordNode = transcriptWordRefs.current[index];
        if (!transcriptWordNode) {
            return;
        }

        transcriptWordNode.dataset.highlighted = highlighted ? 'true' : 'false';
    }, []);

    useEffect(() => {
        const previousIndex = previouslyHighlightedTranscriptWordIndexRef.current;

        if (!highlightingEnabled) {
            updateTranscriptWordHighlight(previousIndex, false);
            previouslyHighlightedTranscriptWordIndexRef.current = null;
            return;
        }

        if (previousIndex === activeTranscriptWordIndex) {
            return;
        }

        updateTranscriptWordHighlight(previousIndex, false);
        updateTranscriptWordHighlight(activeTranscriptWordIndex, true);
        previouslyHighlightedTranscriptWordIndexRef.current = activeTranscriptWordIndex;
    }, [
        activeTranscriptWordIndex,
        highlightingEnabled,
        transcriptWordItems,
        updateTranscriptWordHighlight,
    ]);

    const transcriptWordNodes = useMemo(() => (
        transcriptWordItems.map((transcriptWordItem, index) => (
            <TranscriptWord
                key={transcriptWordItem.key}
                ref={(node) => {
                    transcriptWordRefs.current[index] = node;
                }}
                className={HOVER_TARGET_CLASSNAME}
                color={color}
                data-highlighted="false"
                {...getTouchMouseEventHandlers({
                    onMouseEnter: onTranscriptWordMouseEnter,
                    onMouseLeave: onTranscriptWordMouseLeave,
                })}
                {...getPressEventHandlers({
                    onMouseDown: (e) => {
                        /**
                         * Treat transcript words like playback
                         * controls, not editable text, so Slate
                         * never tries to resolve selection
                         * points from this subtree.
                         */
                        e.preventDefault();
                        e.stopPropagation();
                        onTranscriptWordPress(transcriptWordItem.word);
                    },
                })}
            >
                {transcriptWordItem.label}
            </TranscriptWord>
        ))
    ), [
        color,
        onTranscriptWordMouseEnter,
        onTranscriptWordMouseLeave,
        onTranscriptWordPress,
        transcriptWordItems,
    ]);

    if (!visible || !hasTranscript) {
        return null;
    }

    return (
        <TranscriptContainer
            visible={visible}
            transitionDuration={transitionDuration}
            contentEditable={false}
            suppressContentEditableWarning
        >
            {transcriptWordItems.length > 0
                ? transcriptWordNodes
                : transcriptPhrases.map((phrase) => (
                    <TranscriptWord
                        // Phrase-only fallback when word timing data is unavailable.
                        key={`phrase-${phrase}`}
                        className={HOVER_TARGET_CLASSNAME}
                        color={color}
                        data-highlighted="false"
                    >
                        {`${phrase} `}
                    </TranscriptWord>
                ))}
        </TranscriptContainer>
    );
});

const getAudioContextConstructor = (): typeof AudioContext | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const windowWithWebkitAudioContext = window as Window & typeof globalThis & {
        webkitAudioContext?: typeof AudioContext,
    };

    return windowWithWebkitAudioContext.AudioContext
        || windowWithWebkitAudioContext.webkitAudioContext
        || null;
};

const InlineWaveform = React.memo(({
    bars,
    color,
    containerRef,
    recording,
    playedBarCount,
}: {
    bars: number[],
    color: string,
    containerRef: React.RefObject<HTMLDivElement>,
    recording: boolean,
    playedBarCount: number,
}): React.ReactElement | null => {
    if (bars.length === 0) {
        return null;
    }

    const barCountByRoundedHeight = new Map<string, number>();
    const waveformBarItems = bars.map<{
        key: string,
        height: number,
        played: boolean,
    }>((barHeight, barIndex) => {
        const roundedHeight = barHeight.toFixed(4);
        const nextMatchingBarCount = (barCountByRoundedHeight.get(roundedHeight) || 0) + 1;

        barCountByRoundedHeight.set(roundedHeight, nextMatchingBarCount);

        return {
            key: `${roundedHeight}-${nextMatchingBarCount}`,
            height: barHeight,
            played: !recording && barIndex < playedBarCount,
        };
    });

    return (
        <InlineWaveformViewport
            aria-hidden="true"
            ref={containerRef}
            recording={recording}
        >
            <InlineWaveformContainer
                recording={recording}
            >
                {waveformBarItems.map((waveformBarItem) => (
                    <InlineWaveformBar
                        key={waveformBarItem.key}
                        color={color}
                        height={waveformBarItem.height}
                        played={waveformBarItem.played}
                        recording={recording}
                    />
                ))}
            </InlineWaveformContainer>
        </InlineWaveformViewport>
    );
});

interface Props {
    customCursorActive: boolean,
    id: string,
    user: IPartialUserItem | null,
    userIsAdmin?: boolean,
    small?: boolean,
    buttonWidth?: number,
    hasSound: boolean,
    filePath: string | null,
    editorType: EDITOR_CONTEXT_TYPE,
    title: string,
    color: string,
    isAuthor?: boolean,
    uploaded?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement,
    readOnly?: boolean,
    parentRef: HTMLElement | undefined,
    viewportRootRef?: React.RefObject<HTMLDivElement | null> | null,
    attributes: any,
    currentSessionId: string | null,
    postId: string | undefined,
    chapterId?: string,
    sectionId?: string,
    autoRecord?: boolean,
    description: string,
    isPostContentDictation?: boolean,
    durationMs?: number,
    waveformBars?: number[],
    postContentTranscriptText?: string,
    readOnlyUsesPreviewSource?: boolean,
    previewAudioEndPromptText?: string,
    updateAudioNote: (data: IAudioNoteData) => void,
    handleAutoRecord?: ((handle: boolean) => void) | null,
    revealOptionsAbove?: boolean,
    autoRecordIdentifier?: string | null,
    isRecordingAudioNote: boolean,
    setIsRecordingAudioNote: React.Dispatch<React.SetStateAction<boolean>>,
    setAudioNoteDirtyState?: (audioNoteId: string, isDirty: boolean) => void,
    setAudioNoteSavingState?: (audioNoteId: string, isSaving: boolean) => void,
    broadcastHeightAdjusted: () => void,
    uploadingMedia: Map<string, IMediaItem>,
    setUploadingMedia: (mediaItem: IMediaItem) => void,
    updateUploadingMedia: (mediaItem: IMediaItem) => void,
    removeUploadingMedia: (id: string) => void,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    requestEditorDismissalSuppression?: () => void,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    onReadOnlyPlaybackStateChange?: (
        audioNoteId: string,
        playbackState: IReadOnlyAudioNotePlaybackState | null,
    ) => void,
}
function AudioNote({
    customCursorActive,
    id,
    user,
    userIsAdmin = false,
    small,
    buttonWidth,
    hasSound,
    filePath = null,
    editorType,
    title = '',
    color,
    isAuthor = false,
    uploaded = false,
    children,
    readOnly = false,
    parentRef = undefined,
    viewportRootRef = null,
    attributes,
    currentSessionId,
    postId,
    chapterId = undefined,
    sectionId = undefined,
    autoRecord = false,
    description = '',
    isPostContentDictation = false,
    durationMs: initialStoredDurationMs = undefined,
    waveformBars: initialStoredWaveformBars = [],
    postContentTranscriptText = '',
    readOnlyUsesPreviewSource = false,
    previewAudioEndPromptText = '',
    updateAudioNote,
    handleAutoRecord = null,
    autoRecordIdentifier = null,
    isRecordingAudioNote,
    setIsRecordingAudioNote,
    setAudioNoteDirtyState,
    setAudioNoteSavingState,
    broadcastHeightAdjusted,
    uploadingMedia,
    setUploadingMedia,
    updateUploadingMedia,
    removeUploadingMedia,
    onCursorEnter,
    onCursorLeave,
    requestEditorDismissalSuppression = undefined,
    setInputFocused,
    setSnackbarData,
    onReadOnlyPlaybackStateChange = undefined,
}: Props): JSX.Element {
    // ===== General Constants =====

    const ACTION_CONTAINER_PADDING_LEFT = 0.5 * BODY_FONT_SIZE;
    const ACTION_CONTAINER_PADDING_RIGHT = 0 * BODY_FONT_SIZE;
    const DEFAULT_AUDIO_NOTE_BUTTON_LENGTH = 2.5 * BODY_FONT_SIZE;
    const MAX_TIMESTAMP_WIDTH = 5.25 * BODY_FONT_SIZE;
    const PLAYBACK_SPEED_BUTTON_WIDTH = 3 * BODY_FONT_SIZE;
    const PROGRESS_SCRUBBER_ID = (elementId: string | null): string => (elementId ? `progress-scrubber id=${elementId}` : '');
    const PROGRESS_INDICATOR_ID = (elementId: string | null): string => (elementId ? `progress-indicator id=${elementId}` : '');
    const POTENTIAL_SCRUBBER_ID = (elementId: string | null): string => (elementId ? `potential-scrubber id=${elementId}` : '');
    const LEFT_TIME_FIGURE_ID = (elementId: string | null): string => (elementId ? `left-time-figure id=${elementId}` : '');
    const RIGHT_TIME_FIGURE_ID = (elementId: string | null): string => (elementId ? `right-time-figure id=${elementId}` : '');
    const TITLE_INPUT_ID = (elementId: string | null): string => (elementId ? `title-input id=${elementId}` : '');
    const DESCRIPTION_INPUT_ID = (elementId: string | null): string => (elementId ? `description-input id=${elementId}` : '');
    const PLAYBACK_CONTAINER_PADDING_LEFT = 0 * BODY_FONT_SIZE;
    const STATUS_ICON_MARGIN_LEFT = 0.625 * BODY_FONT_SIZE;
    const DESCRIPTION_FONT_SIZE = 0.95;
    const CONTAINER_PADDING_AUDIO_NOTE = 0.5 * BODY_FONT_SIZE;
    const TRANSCRIPT_AUTO_REVEAL_WORD_LIMIT = 100;
    // Snackbar message when create audio note error occurs
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_AUDIO_NOTE_SAVE_FAILURE = `Encountered a problem saving audio note. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Snackbar message when save local file upload error occurs
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_AUDIO_NOTE_LOCAL_FILE_UPLOAD_FAILURE = `Encountered a problem saving local file audio note. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    const showAudioNoteTooltips = !isMobile();

    // TODO don't use refs on things that will leave dom

    // ===== Refs =====

    const audioNodeRef = useRef<HTMLAudioElement>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const titleNodeRef = useRef<HTMLInputElement>(null);
    const descriptionNodeRef = useRef<HTMLDivElement>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const waveformLaneRef = useRef<HTMLDivElement>(null);
    const playbackMetaContainerRef = useRef<HTMLDivElement>(null);
    const titleInputRef = useRef<HTMLInputElement>(null);
    const descriptionInputRef = useRef<HTMLDivElement>(null);
    // avoid resolving URL multiple times
    const resolvingURLRef = useRef<Promise<string | null> | null>(null);
    const audioNoteMediaItemRef = useRef<IEditorAudioNoteItem | null>(null);
    const postContentDictationSourceSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastRequestedPostContentDictationSourceTextRef = useRef<string | null>(null);
    const showTranscriptRef = useRef<boolean>(false);
    const lastRequestedTranscriptStorageHydrationRef = useRef<string | null>(null);
    const shouldFocusDescriptionAfterRecordingRef = useRef<boolean>(false);
    const recordingStreamRef = useRef<MediaStream | null>(null);
    const recordingWaveformAudioContextRef = useRef<AudioContext | null>(null);
    const recordingWaveformSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const recordingWaveformAnalyserRef = useRef<AnalyserNode | null>(null);
    const recordingWaveformSinkGainNodeRef = useRef<GainNode | null>(null);
    const recordingWaveformAnimationFrameRef = useRef<number | null>(null);
    const recordingWaveformTimeDomainDataRef = useRef<
        ReturnType<typeof createAudioWaveformTimeDomainData> | null
    >(null);
    const recordedWaveformLevelSamplesRef = useRef<number[]>([]);
    const recordedWaveformDisplayBarsRef = useRef<number[]>([]);
    const recordingWaveformLastSampleAtRef = useRef<number | null>(null);
    const visibleWaveformBarCountRef = useRef<number>(DEFAULT_AUDIO_WAVEFORM_BAR_COUNT);
    const waveformSourceKeyRef = useRef<string | null>(null);
    const persistedEmbeddedDurationMs = resolvePersistedAudioNoteDurationMs({
        embeddedDurationMs: initialStoredDurationMs,
        embeddedFilePath: filePath,
        readOnly,
        readOnlyUsesPreviewSource,
        isPostContentDictation,
    });
    const persistedWaveformBarsRef = useRef<number[] | null>(Array.isArray(initialStoredWaveformBars)
        ? initialStoredWaveformBars
        : null);
    const persistedDurationMsRef = useRef<number | null>(
        sanitizeAudioNoteDurationMs(persistedEmbeddedDurationMs) ?? null,
    );
    /**
     * Older post content can still surface audio-note nodes without the
     * `isPostContentDictation` flag. If the media doc later hydrates with the
     * authoritative value, repair the local/node state once instead of letting a
     * stale `false` overwrite the backend on the next save.
     */
    const hasRepairedPostContentDictationFromMediaRef = useRef<boolean>(false);
    const hasLocallyToggledPostContentDictationRef = useRef<boolean>(false);

    // ----- Sound Clips

    const inputClickClip = useRef<HTMLAudioElement | null>(null);
    const fallbackWaveformSeed = useMemo(() => [
        id,
        filePath || 'new-audio-note',
        title || 'untitled',
    ].join(':'), [
        filePath,
        id,
        title,
    ]);

    // ===== State =====

    // ----- Editing Audio Notes -----

    // Indicates the value of the last media update version we've received to compare against new updates
    const [lastMediaPingVersion, setLastMediaPingVersion] = useState<number | undefined>(undefined);
    // Stores whether we have loaded an existing audio note
    const hasUnsavedAudioPath = !filePath || filePath === autoRecordIdentifier;
    const shouldInitializeAsNewAudioNote = shouldTreatAudioNoteAsNew({
        readOnly,
        filePath,
        autoRecordIdentifier,
    });
    const [isNewAudioNote, setIsNewAudioNote] = useState<boolean>(shouldInitializeAsNewAudioNote);
    // Stores whether audio was uploaded or not
    const [audioUploaded, setAudioUploaded] = useState<boolean>(uploaded);
    // Stores recording state dictacting available functionality
    const [status, setStatus] = useState<AUDIO_NOTE_RECORDING_STATE>(shouldInitializeAsNewAudioNote
        ? AUDIO_NOTE_RECORDING_STATE.idle
        : AUDIO_NOTE_RECORDING_STATE.pausedComplete);
    const playbackStatusRef = useRef<AUDIO_NOTE_RECORDING_STATE>(status);
    // Stores audio length in milliseconds (whereas Audio API stores seconds)
    const [audioLength, setAudioLength] = useState<number>(
        sanitizeAudioNoteDurationMs(persistedEmbeddedDurationMs) || 0,
    );
    const [audioNoteContainerWidth, setAudioNoteContainerWidth] = useState<number | null>(() => {
        if (editorType !== EDITOR_CONTEXT_TYPE.annotation) {
            return null;
        }

        return resolveAnnotationAudioNoteWidthPx(readOnly);
    });
    const [playbackMetaWidth, setPlaybackMetaWidth] = useState<number | null>(null);
    // Stores audio blob from recording session or audio import
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [visibleWaveformBarCount, setVisibleWaveformBarCount] = useState<number>(DEFAULT_AUDIO_WAVEFORM_BAR_COUNT);
    const [waveformBars, setWaveformBars] = useState<number[]>(() => fitAudioWaveformBarsToBarCount({
        bars: initialStoredWaveformBars,
        barCount: DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
    }));
    // Stores link with audio data
    const [audioURL, setAudioURL] = useState<string | null>(null);
    // Stores timestamp at which audio note started playing
    const [startTime, setStartTime] = useState<number | null>(null);
    // Stores intermission of audio note when it is paused
    const [savedTime, setSavedTime] = useState<number | null>(null);
    // Stores the current time lapsed in the audiio
    const [timeElapsed, setTimeElapsed] = useState<number>(0);
    // Stores time being pointed to when user is scrubbing the audio track
    const [potentialElapsed, setPotentialElapsed] = useState<number | null>(null);
    // Stores whether the time elapsed is being updated
    const [isUpdatingTime, setIsUpdatingTime] = useState<boolean>(false);
    // Stores whether audio note is being edited (this is different from readOnly).
    // It is equivalent to being in the post editor and not in the recording mode as opposed to the post reader
    const [isEditing, setIsEditing] = useState<boolean>(hasUnsavedAudioPath && !readOnly);
    // Position of title node relative to the viewport
    // Used by portal TitleInput to position it above
    // hidden node.
    const [tempId] = useState<string>(uuidv4());
    const [titlePos, setTitlePos] = useState<ICoord>({
        x: 0,
        y: 0,
    });
    // Width of stand-in title used by the
    // TitleInput
    const [titleWidth, setTitleWidth] = useState(0);
    // Position of description node relative to the viewport
    // Used by portal DescriptionInput to position it above
    // hidden node.
    const [descriptionPos, setDescriptionPos] = useState({
        left: 0,
        top: 0,
    });
    // Width of stand-in description used by the
    // DescriptionInput
    const [descriptionWidth, setDescriptionWidth] = useState(0);
    //  Width of Audio Note Button
    const [buttonLength] = useState(buttonWidth || DEFAULT_AUDIO_NOTE_BUTTON_LENGTH);
    // Stores the flag for spacebar presses
    const spacebarPress = useKeyPress(KEYCODE.enter);
    // Stores the current selection
    const [selection, setSelection] = useState< number>(0);
    // Stores Error Message
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // Store whether we have recording permissions
    const [hasRecordingPermissions, setHasRecordingPermissions] = useState<boolean>(false);
    // Stores last value of isRecordingAudioNote
    const [prevIsRecordingAudioNote, setPrevIsRecordingAudioNote] = useState<boolean | null>(null);
    // Indicates whether description input is focused to reveal caret
    const [descriptionInputFocused, setDescriptionInputFocused] = useState<boolean>(false);
    // Stores audio note uploading progress
    const [uploadingStateChangeEvent, setUploadingStateChangeEvent] = useState<{
        mediaItem: IMediaItem,
        snapshot: UploadTaskSnapshot,
        progress: number,
    } | null>(null);
    // Stores audio note completion event object
    const [uploadingCompleteEvent, setUploadingCompleteEvent] = useState<{
        mediaItem: IMediaItem,
        storageRef: StorageReference,
    } | null>(null);
    // Indicates whether we're currently saving a recorded/uploaded audio note
    const [isSavingAudioNote, setIsSavingAudioNote] = useState<boolean>(false);
    const [audioRemovalModalMode, setAudioRemovalModalMode] = useState<AUDIO_NOTE_REMOVAL_MODAL_MODE | null>(null);
    // Indicates whether user has pressed play and playback is still starting
    const [isStartingPlayback, setIsStartingPlayback] = useState<boolean>(false);
    // Recorded notes become playable only after MediaRecorder finishes emitting
    // the final blob. Keep the preview controls in a temporary loading state
    // during that short handoff so play cannot race ahead of the blob URL.
    const [isPreparingRecordedPreview, setIsPreparingRecordedPreview] = useState<boolean>(false);

    // ----- Saved Audio Note -----

    // Stores optional title of audio note
    const [audioTitle, setAudioTitle] = useState<string>(title);
    // Stores optional description of audio note
    const [audioDescription, setAudioDescription] = useState<string>(sanitizeAudioNoteDescription(description));
    const normalizedAudioDescription = sanitizeAudioNoteDescription(audioDescription);
    const [postContentDictationEnabled, setPostContentDictationEnabled] = useState<boolean>(isPostContentDictation);
    const [postContentSpeechGenerationRequested, setPostContentSpeechGenerationRequested] = useState<boolean>(false);
    const [authResolvedUserId, setAuthResolvedUserId] = useState<string | null>(() => auth.currentUser?.uid || null);
    const normalizedPreviewAudioEndPromptText = useMemo(() => (
        String(previewAudioEndPromptText || '').trim()
    ), [previewAudioEndPromptText]);
    const normalizedPostContentTranscriptText = useMemo(() => (
        String(postContentTranscriptText || '').trim()
    ), [postContentTranscriptText]);
    // Stores audio note media item
    const [audioNoteMediaItem, setAudioNoteMediaItem] = useState<IEditorAudioNoteItem | null>(null);
    const hydratedPostContentDictationEnabled = audioNoteMediaItem?.isPostContentDictation === true;
    const incomingPostContentDictationEnabled = (
        !hasLocallyToggledPostContentDictationRef.current
        && (
            isPostContentDictation
            || hydratedPostContentDictationEnabled
        )
    );
    const audioNoteDetailsSource = useMemo(() => resolveAudioNoteDetailsSource({
        slate: {
            title,
            description,
        },
        mediaItem: audioNoteMediaItem,
    }), [
        audioNoteMediaItem?.description,
        audioNoteMediaItem?.title,
        description,
        title,
    ]);
    const normalizedIncomingAudioDescription = sanitizeAudioNoteDescription(
        audioNoteDetailsSource.description,
    );
    const lastAudioNoteDetailsSourceRef = useRef({
        title: audioNoteDetailsSource.title || '',
        description: normalizedIncomingAudioDescription,
    });
    const shouldPlayReadOnlyPreviewAudioEndPrompt = useMemo(() => shouldPlayPreviewAudioEndPrompt({
        readOnly,
        readOnlyUsesPreviewSource,
        isPostContentDictation: postContentDictationEnabled,
        previewAudioEndPromptText: normalizedPreviewAudioEndPromptText,
    }), [
        normalizedPreviewAudioEndPromptText,
        postContentDictationEnabled,
        readOnly,
        readOnlyUsesPreviewSource,
    ]);
    const shouldUsePreviewDictationTranscript = (
        readOnly
        && readOnlyUsesPreviewSource
        && postContentDictationEnabled
        && normalizedPostContentTranscriptText.length > 0
    );
    const requiresAuthoritativePreviewMediaItem = (
        readOnly
        && readOnlyUsesPreviewSource
        && postContentDictationEnabled
    );
    const persistedHydrationDurationMs = resolvePersistedAudioNoteDurationMs({
        embeddedDurationMs: initialStoredDurationMs,
        embeddedFilePath: filePath,
        hydratedDurationMs: audioNoteMediaItem?.durationMs,
        readOnly,
        readOnlyUsesPreviewSource,
        isPostContentDictation: postContentDictationEnabled,
    });
    const shouldPersistPostContentDictation = (
        editorType === EDITOR_CONTEXT_TYPE.post
        && postContentDictationEnabled
    );
    const dictationSourceTextForSave = shouldPersistPostContentDictation
        ? normalizedPostContentTranscriptText
        : '';
    const generatedSpeechSourceTextForSave = normalizedPostContentTranscriptText;
    // Stores playback speed
    const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
    // Stores mime type of audio note
    const [mimeType, setMimeType] = useState<string>('');
    // Indicates whether transcript should be visible
    const [showTranscript, setShowTranscript] = useState<boolean>(false);
    // Keeps the floating reader mini-player hidden while the note itself is on screen.
    const [isVisibleInViewport, setIsVisibleInViewport] = useState<boolean>(true);
    // Tracks whether playback actually started at least once in this session.
    const [hasStartedPlayback, setHasStartedPlayback] = useState<boolean>(false);
    const readOnlyVisibilityMeasurementFrameRef = useRef<number | null>(null);
    const audioNoteLayoutMeasurementFrameRef = useRef<number | null>(null);
    const waveformBarMeasurementFrameRef = useRef<number | null>(null);
    const previewAudioEndPromptUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
    const previewAudioEndPromptRequestIdRef = useRef<number>(0);
    const playbackEndHandledRef = useRef<boolean>(false);

    /**
     * Keep the fixed-width waveform renderer pointed at the current lane size
     * without forcing every live-animation callback to recreate on resize.
     */
    useEffect(() => {
        visibleWaveformBarCountRef.current = visibleWaveformBarCount;
    }, [visibleWaveformBarCount]);

    /**
     * Saved-note waveform decoding can resolve after playback has already
     * started. Keep the currently rendered bars stable until playback stops so
     * the user does not see the waveform morph mid-listen.
     */
    useEffect(() => {
        playbackStatusRef.current = status;
    }, [status]);

    /**
     * These helpers centralize the measured bar-count dependency so every
     * waveform source uses the same visual geometry.
     */
    const buildSilentVisibleWaveformBars = useCallback((): number[] => (
        buildSilentAudioWaveformBars({
            barCount: visibleWaveformBarCountRef.current,
        })
    ), []);
    const buildDeterministicVisibleWaveformBars = useCallback((seed: string): number[] => (
        buildDeterministicAudioWaveformBars({
            seed,
            barCount: visibleWaveformBarCountRef.current,
        })
    ), []);
    const buildRecordedVisibleWaveformBars = useCallback((sampleLevels: number[]): number[] => (
        buildAudioWaveformBarsFromSampleLevels({
            sampleLevels,
            barCount: visibleWaveformBarCountRef.current,
            ...COMPLETED_AUDIO_NOTE_WAVEFORM_BUILD_OPTIONS,
        })
    ), []);
    const buildScrollingVisibleWaveformBars = useCallback((sampleLevels: number[]): number[] => (
        buildScrollingAudioWaveformBarsFromSampleLevels({
            sampleLevels,
            barCount: visibleWaveformBarCountRef.current,
            ...LIVE_AUDIO_NOTE_WAVEFORM_BUILD_OPTIONS,
        })
    ), []);
    const buildScrollingVisibleCapturedWaveformBars = useCallback((bars: number[]): number[] => (
        buildScrollingAudioWaveformBarsFromCapturedBars({
            bars,
            barCount: visibleWaveformBarCountRef.current,
        })
    ), []);
    const hydratePersistedAudioDurationMs = useCallback((durationMs?: number | null): void => {
        const sanitizedDurationMs = sanitizeAudioNoteDurationMs(durationMs);
        if (sanitizedDurationMs == null) {
            return;
        }

        persistedDurationMsRef.current = sanitizedDurationMs;
        setAudioLength((currentAudioLength) => (
            resolveHydratedAudioLengthMs({
                currentDurationMs: currentAudioLength,
                hydratedDurationMs: sanitizedDurationMs,
                readOnly,
            })
        ));
    }, [
        readOnly,
    ]);
    const resolveAudioNoteDurationMsForPersistence = useCallback((): number | undefined => {
        const mediaElementDurationMs = (
            audioNodeRef.current
            && Number.isFinite(audioNodeRef.current.duration)
            && audioNodeRef.current.duration > 0
        )
            ? audioNodeRef.current.duration * MILLISECONDS_IN_A_SECOND
            : undefined;

        return sanitizeAudioNoteDurationMs(mediaElementDurationMs)
            || sanitizeAudioNoteDurationMs(audioLength)
            || sanitizeAudioNoteDurationMs(audioNoteMediaItem?.durationMs)
            || sanitizeAudioNoteDurationMs(initialStoredDurationMs);
    }, [
        audioLength,
        audioNoteMediaItem?.durationMs,
        initialStoredDurationMs,
    ]);
    const preferredStoredWaveformBars = useMemo(() => {
        if (
            Array.isArray(initialStoredWaveformBars)
            && initialStoredWaveformBars.length > 0
        ) {
            return initialStoredWaveformBars;
        }

        const mediaItemWaveformBars = audioNoteMediaItem?.waveformBars;
        if (
            Array.isArray(mediaItemWaveformBars)
            && mediaItemWaveformBars.length > 0
        ) {
            return mediaItemWaveformBars;
        }

        return [];
    }, [
        audioNoteMediaItem?.waveformBars,
        initialStoredWaveformBars,
    ]);
    const buildPersistableWaveformBars = useCallback((
        candidateWaveformBars: number[] | null | undefined,
    ): number[] | undefined => {
        if (!Array.isArray(candidateWaveformBars) || candidateWaveformBars.length === 0) {
            return undefined;
        }

        const persistableWaveformBars = fitAudioWaveformBarsToBarCount({
            bars: candidateWaveformBars,
            barCount: DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
        });

        return hasMeaningfulAudioWaveformBars(persistableWaveformBars)
            ? persistableWaveformBars
            : undefined;
    }, []);

    // ----- Both Saved and Editing -----

    // Stores whether Slate element is focused
    const focused = useFocused();
    // Stores whether Slate element is selected
    const selected = useSelected();

    // keep the remote storage path around (don’t turn it into audioURL until fetched)
    const [remoteFilePath, setRemoteFilePath] = useState<string | null>(() => resolveAudioNotePlaybackFilePath({
        filePath,
        remoteFilePath: null,
        autoRecordIdentifier,
        readOnlyUsesPreviewSource,
        isPostContentDictation: postContentDictationEnabled,
        requireRemoteFilePath: requiresAuthoritativePreviewMediaItem,
    }));

    // revoke blob URLs cleanly (only revoke ones we created)
    const lastBlobUrlRef = useRef<string | null>(null);
    const recordedAudioChunksRef = useRef<Blob[]>([]);
    const pendingNewAudioNoteSaveRef = useRef<IAudioNotePendingSaveState | null>(null);
    const reportedDirtyAudioNoteRef = useRef<boolean>(false);
    const reportedSavingAudioNoteRef = useRef<boolean>(false);
    const embeddedPlaybackFilePath = requiresAuthoritativePreviewMediaItem
        ? null
        : filePath;
    const playableFilePath = getPlayableAudioStoragePath({
        filePath: embeddedPlaybackFilePath,
        remoteFilePath,
        autoRecordIdentifier,
    });
    const requiresBackendIssuedPlaybackUrl = (
        readOnly
        && (
            requiresAuthoritativePreviewMediaItem
            || isProtectedPostAudioStoragePath(filePath)
            || isProtectedPostAudioStoragePath(remoteFilePath)
            || isProtectedPostAudioStoragePath(audioNoteMediaItem?.filePath)
        )
    );
    const audioNotePlaybackFetchErrorMessage = requiresBackendIssuedPlaybackUrl
        ? AUDIO_NOTE_FETCH_POST_AUDIO_ERROR
        : AUDIO_NOTE_FETCH_FILE_ERROR;
    const shouldFetchPlaybackOnlyMediaItemFirst = (
        readOnly
        && requiresBackendIssuedPlaybackUrl
    );
    const shouldHydrateTranscriptAfterPlaybackOnlyFetch = (
        shouldFetchPlaybackOnlyMediaItemFirst
        && shouldUsePreviewDictationTranscript
    );

    /**
     * New notes are dirty once the user has captured/imported playable audio.
     * Existing notes only become dirty when edit mode diverges from the saved
     * title, description, or dictation settings.
     */
    const audioNoteIsDirty = (
        isEditing
        && (
            (
                isNewAudioNote
                && status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
            )
            || (
                !isNewAudioNote
                && (
                    audioTitle !== audioNoteDetailsSource.title
                    || normalizedAudioDescription !== normalizedIncomingAudioDescription
                    || postContentDictationEnabled !== isPostContentDictation
                )
            )
        )
    );

    /**
     * Local preview recordings use blob URLs. Revoke the previous preview URL
     * before swapping to a new one so redo/import flows cannot keep stale audio
     * alive in memory.
     */
    const setLocalPreviewUrl = useCallback((nextBlobUrl: string | null): void => {
        if (
            lastBlobUrlRef.current
            && lastBlobUrlRef.current !== nextBlobUrl
        ) {
            URL.revokeObjectURL(lastBlobUrlRef.current);
        }
        lastBlobUrlRef.current = nextBlobUrl;
        setAudioURL(nextBlobUrl);
    }, []);

    const stopRecordingWaveformAnimation = useCallback((): void => {
        if (recordingWaveformAnimationFrameRef.current == null) {
            return;
        }

        window.cancelAnimationFrame(recordingWaveformAnimationFrameRef.current);
        recordingWaveformAnimationFrameRef.current = null;
    }, []);

    /**
     * Release live recording analyser resources explicitly so microphone-driven
     * waveform updates do not outlive the active capture session.
     */
    const releaseRecordingWaveformResources = useCallback(async ({
        stopStream = false,
    }: {
        stopStream?: boolean,
    } = {}): Promise<void> => {
        stopRecordingWaveformAnimation();
        recordingWaveformSourceNodeRef.current?.disconnect();
        recordingWaveformAnalyserRef.current?.disconnect();
        recordingWaveformSinkGainNodeRef.current?.disconnect();
        recordingWaveformSourceNodeRef.current = null;
        recordingWaveformAnalyserRef.current = null;
        recordingWaveformSinkGainNodeRef.current = null;
        recordingWaveformTimeDomainDataRef.current = null;
        recordingWaveformLastSampleAtRef.current = null;

        if (recordingWaveformAudioContextRef.current) {
            await recordingWaveformAudioContextRef.current.close().catch(() => undefined);
            recordingWaveformAudioContextRef.current = null;
        }

        if (stopStream && recordingStreamRef.current) {
            recordingStreamRef.current.getTracks().forEach((track) => track.stop());
            recordingStreamRef.current = null;
        }
    }, [stopRecordingWaveformAnimation]);

    const startRecordingWaveformAnimation = useCallback((): void => {
        const analyserNode = recordingWaveformAnalyserRef.current;
        if (!analyserNode) {
            return;
        }

        const updateWaveform = (frameTime: number): void => {
            const liveAnalyserNode = recordingWaveformAnalyserRef.current;
            const timeDomainData = recordingWaveformTimeDomainDataRef.current;
            if (!liveAnalyserNode || !timeDomainData) {
                return;
            }

            liveAnalyserNode.getFloatTimeDomainData(timeDomainData);
            const sampleLevel = getAudioWaveformSampleLevelFromFloatTimeDomainData({
                timeDomainData,
            });
            const lastSampleAt = recordingWaveformLastSampleAtRef.current;
            const recordingWaveformSampleIntervalMs = resolveAudioWaveformSampleIntervalMs({
                visibleBarCount: visibleWaveformBarCountRef.current,
                visibleWindowMs: AUDIO_NOTE_RECORDING_WAVEFORM_CONFIG.visibleWindowMs,
            });

            if (
                lastSampleAt == null
                || frameTime - lastSampleAt >= recordingWaveformSampleIntervalMs
            ) {
                recordingWaveformLastSampleAtRef.current = frameTime;
                recordedWaveformLevelSamplesRef.current.push(sampleLevel);

                const nextVisibleWaveformBars = buildScrollingVisibleWaveformBars(
                    recordedWaveformLevelSamplesRef.current,
                );
                const nextWaveformBarHeight = nextVisibleWaveformBars[nextVisibleWaveformBars.length - 1]
                    || DEFAULT_AUDIO_WAVEFORM_MIN_BAR_HEIGHT;

                /**
                 * Freeze each live bar at the height it had when it was first
                 * captured so the older bars do not subtly re-shape themselves
                 * while they scroll left.
                 */
                recordedWaveformDisplayBarsRef.current.push(nextWaveformBarHeight);
                setWaveformBars(buildScrollingVisibleCapturedWaveformBars(
                    recordedWaveformDisplayBarsRef.current,
                ));
            }

            recordingWaveformAnimationFrameRef.current = window.requestAnimationFrame(updateWaveform);
        };

        stopRecordingWaveformAnimation();
        recordingWaveformAnimationFrameRef.current = window.requestAnimationFrame(updateWaveform);
    }, [
        buildScrollingVisibleCapturedWaveformBars,
        buildScrollingVisibleWaveformBars,
        stopRecordingWaveformAnimation,
    ]);

    /**
     * Mirror the live microphone signal into a lightweight analyser so the
     * inline waveform can react immediately while recording.
     */
    const connectRecordingWaveformStream = useCallback(async (stream: MediaStream): Promise<void> => {
        await releaseRecordingWaveformResources({ stopStream: true });
        const AudioContextConstructor = getAudioContextConstructor();
        if (!AudioContextConstructor) {
            setWaveformBars(buildSilentVisibleWaveformBars());
            recordingStreamRef.current = stream;
            return;
        }

        try {
            const audioContext = new AudioContextConstructor();
            const sinkGainNode = audioContext.createGain();
            const sourceNode = audioContext.createMediaStreamSource(stream);
            const analyserNode = audioContext.createAnalyser();

            analyserNode.fftSize = 512;
            analyserNode.smoothingTimeConstant = 0.55;
            sinkGainNode.gain.value = 0;

            sourceNode.connect(analyserNode);
            analyserNode.connect(sinkGainNode);
            sinkGainNode.connect(audioContext.destination);
            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }

            recordingStreamRef.current = stream;
            recordingWaveformAudioContextRef.current = audioContext;
            recordingWaveformSourceNodeRef.current = sourceNode;
            recordingWaveformAnalyserRef.current = analyserNode;
            recordingWaveformSinkGainNodeRef.current = sinkGainNode;
            recordingWaveformTimeDomainDataRef.current = createAudioWaveformTimeDomainData(
                analyserNode,
            );
            setWaveformBars(buildSilentVisibleWaveformBars());
            startRecordingWaveformAnimation();
        } catch (error) {
            recordingStreamRef.current = stream;
            setWaveformBars(buildSilentVisibleWaveformBars());
        }
    }, [
        buildSilentVisibleWaveformBars,
        releaseRecordingWaveformResources,
        startRecordingWaveformAnimation,
    ]);

    /**
     * Decode playable audio into compact bars that can be shown beside the
     * timer once recording or import has completed.
     */
    const decodeWaveformBarsFromArrayBuffer = useCallback(async (
        sourceArrayBuffer: ArrayBuffer,
        options: {
            barCount?: number,
        } = {},
    ): Promise<number[] | null> => {
        const AudioContextConstructor = getAudioContextConstructor();
        if (!AudioContextConstructor) {
            return null;
        }

        const audioContext = new AudioContextConstructor();

        try {
            const audioBuffer = await audioContext.decodeAudioData(sourceArrayBuffer);
            const decodedChannelData = audioBuffer.getChannelData(0);
            const resolvedBarCount = Number.isFinite(options.barCount)
                ? Math.max(1, Math.floor(options.barCount || DEFAULT_AUDIO_WAVEFORM_BAR_COUNT))
                : visibleWaveformBarCountRef.current;

            return buildAudioWaveformBarsFromChannelData({
                channelData: decodedChannelData,
                barCount: resolvedBarCount,
                ...COMPLETED_AUDIO_NOTE_WAVEFORM_BUILD_OPTIONS,
            });
        } catch (error) {
            return null;
        } finally {
            await audioContext.close().catch(() => undefined);
        }
    }, []);

    const decodeWaveformBarsFromBlob = useCallback(async (
        sourceBlob: Blob,
        options: {
            barCount?: number,
        } = {},
    ): Promise<number[] | null> => {
        try {
            const arrayBuffer = await sourceBlob.arrayBuffer();

            return decodeWaveformBarsFromArrayBuffer(arrayBuffer, options);
        } catch (error) {
            return null;
        }
    }, [decodeWaveformBarsFromArrayBuffer]);
    /**
     * Persist a canonical waveform envelope with the audio note so future
     * renders can show the real waveform immediately without waiting to decode.
     */
    const resolveWaveformBarsForPersistence = useCallback(async (): Promise<number[] | undefined> => {
        const recordedWaveformBars = buildAudioWaveformBarsFromSampleLevels({
            sampleLevels: recordedWaveformLevelSamplesRef.current,
            barCount: DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
            ...COMPLETED_AUDIO_NOTE_WAVEFORM_BUILD_OPTIONS,
        });
        if (hasMeaningfulAudioWaveformBars(recordedWaveformBars)) {
            return recordedWaveformBars;
        }

        const currentWaveformBars = buildPersistableWaveformBars(waveformBars);
        if (currentWaveformBars) {
            return currentWaveformBars;
        }

        const storedWaveformBars = buildPersistableWaveformBars(preferredStoredWaveformBars);
        if (storedWaveformBars) {
            return storedWaveformBars;
        }

        if (!audioBlob) {
            return undefined;
        }

        if (!shouldDecodeAudioNoteWaveformInBrowser({
            audioBlob,
            audioUploaded,
            durationMs: audioLength,
        })) {
            return undefined;
        }

        const decodedWaveformBars = await decodeWaveformBarsFromBlob(audioBlob, {
            barCount: DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
        });
        if (!decodedWaveformBars || !hasMeaningfulAudioWaveformBars(decodedWaveformBars)) {
            return undefined;
        }

        return decodedWaveformBars;
    }, [
        audioBlob,
        audioLength,
        audioUploaded,
        buildPersistableWaveformBars,
        decodeWaveformBarsFromBlob,
        preferredStoredWaveformBars,
        waveformBars,
    ]);

    const playbackSourceMimeType = useMemo(() => {
        if (mimeType) {
            return normalizeRecordedAudioBlobMimeType(mimeType);
        }

        return (
            inferAudioMimeTypeFromUrl(audioURL)
            || inferAudioMimeTypeFromUrl(playableFilePath)
            || inferAudioMimeTypeFromUrl(remoteFilePath)
        );
    }, [audioURL, mimeType, playableFilePath, remoteFilePath]);

    /**
     * Wait until React has committed the latest `<source>` child before we
     * force the media element to reload. This prevents write-mode playback
     * from calling `load()` while the audio element still has no rendered
     * source, which leaves it stuck in `NETWORK_NO_SOURCE`.
     */
    const waitForRenderedAudioSource = useCallback(async (): Promise<void> => {
        const waitForAnimationFrame = (): Promise<void> => new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
        });

        const waitForSourceMount = async (attemptsRemaining: number): Promise<void> => {
            await waitForAnimationFrame();

            const audioElement = audioNodeRef.current;
            const sourceElement = audioElement?.querySelector('source') as HTMLSourceElement | null;
            if (audioElement && sourceElement) {
                return;
            }

            if (attemptsRemaining <= 1) {
                return;
            }

            await waitForSourceMount(attemptsRemaining - 1);
        };

        await waitForSourceMount(5);
    }, []);

    /**
     * Playback clicks can arrive while read-only audio metadata is still
     * hydrating, which means the decision to mount the audio element may commit
     * a frame after the click handler starts. Wait briefly for that commit
     * before priming or playing so early clicks fail soft instead of racing a
     * missing ref.
     */
    const waitForMountedAudioElement = useCallback(async (): Promise<HTMLAudioElement | null> => {
        const waitForAnimationFrame = (): Promise<void> => new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
        });

        const waitForAudioElementMount = async (attemptsRemaining: number): Promise<HTMLAudioElement | null> => {
            if (audioNodeRef.current) {
                return audioNodeRef.current;
            }

            if (attemptsRemaining <= 0) {
                return null;
            }

            await waitForAnimationFrame();
            return waitForAudioElementMount(attemptsRemaining - 1);
        };

        const mountedAudioElement = await waitForAudioElementMount(10);
        return mountedAudioElement;
    }, []);

    /**
     * Keep the nested `<source>` node in sync with the current playback URL and
     * MIME type, then explicitly reload the media element. Older stable builds
     * relied on `load()` whenever the source changed; recent write-mode
     * regressions started once that behavior was removed.
     */
    const syncAudioElementSource = useCallback((
        nextAudioUrl: string | null,
        nextMimeType: string | null | undefined,
        options: {
            forceReload?: boolean,
        } = {},
    ): void => {
        if (!audioNodeRef.current) return;

        const audioElement = audioNodeRef.current;
        const sourceElement = audioElement.querySelector('source') as HTMLSourceElement | null;
        const normalizedMimeType = normalizeRecordedAudioBlobMimeType(nextMimeType);
        const {
            forceReload = false,
        } = options;

        if (!sourceElement) {
            if (!nextAudioUrl) {
                audioElement.removeAttribute('src');
                audioElement.load();
            }
            return;
        }

        const currentSourceUrl = sourceElement.getAttribute('src') ?? null;
        const currentSourceMimeType = sourceElement.getAttribute('type') ?? '';
        const shouldReloadAudioElement = forceReload || (
            audioElement.getAttribute('src') != null
            || currentSourceUrl !== nextAudioUrl
            || currentSourceMimeType !== normalizedMimeType
        );

        if (nextAudioUrl) {
            sourceElement.setAttribute('src', nextAudioUrl);
        } else {
            sourceElement.removeAttribute('src');
        }

        if (normalizedMimeType) {
            sourceElement.setAttribute('type', normalizedMimeType);
        } else {
            sourceElement.removeAttribute('type');
        }

        if (!shouldReloadAudioElement) {
            return;
        }

        audioElement.removeAttribute('src');
        audioElement.load();
    }, []);

    /**
     * Read the active `<source>` URL from the media element. React state can
     * still contain a valid playback URL after the element source was cleared
     * to release a browser media player, so replay needs to compare both.
     */
    const getAudioElementSourceUrl = useCallback((): string | null => {
        const audioElement = audioNodeRef.current;
        if (!audioElement) {
            return null;
        }

        const sourceElement = audioElement.querySelector('source') as HTMLSourceElement | null;
        return sourceElement?.getAttribute('src') || audioElement.getAttribute('src');
    }, []);

    /**
     * Browsers can keep playing the previously loaded media resource until
     * `load()` is called. Clearing the element explicitly prevents redo and
     * discard flows from replaying stale audio.
     */
    const clearAudioElementSource = useCallback((): void => {
        if (!audioNodeRef.current) return;
        audioNodeRef.current.pause();
        audioNodeRef.current.removeAttribute('src');
        const sourceElement = audioNodeRef.current.querySelector('source') as HTMLSourceElement | null;
        if (sourceElement) {
            sourceElement.removeAttribute('src');
        }
        audioNodeRef.current.load();
    }, []);
    const inactivePlaybackReleaseTimeoutRef = useRef<number | null>(null);

    /**
     * Keep the currently paused note warm for a short window so keyboard or
     * mini-player resume does not need to remount and refetch immediately.
     */
    const cancelInactivePlaybackSourceRelease = useCallback((): void => {
        if (inactivePlaybackReleaseTimeoutRef.current == null) {
            return;
        }

        window.clearTimeout(inactivePlaybackReleaseTimeoutRef.current);
        inactivePlaybackReleaseTimeoutRef.current = null;
    }, []);

    /**
     * Drop the active playback source once a note is no longer using it. This
     * keeps previously played notes from holding onto browser media players
     * across long posts with many audio notes.
     */
    const releaseInactivePlaybackSource = useCallback((): void => {
        cancelInactivePlaybackSourceRelease();
        clearAudioElementSource();
        setLocalPreviewUrl(null);
        setAudioURL(null);
    }, [cancelInactivePlaybackSourceRelease, clearAudioElementSource, setLocalPreviewUrl]);

    /**
     * Paused notes release their source after a short idle grace period so a
     * near-immediate resume stays responsive without pinning every player.
     */
    const scheduleInactivePlaybackSourceRelease = useCallback((): void => {
        cancelInactivePlaybackSourceRelease();
        inactivePlaybackReleaseTimeoutRef.current = window.setTimeout(() => {
            inactivePlaybackReleaseTimeoutRef.current = null;
            releaseInactivePlaybackSource();
        }, INACTIVE_PLAYBACK_SOURCE_RELEASE_GRACE_DURATION);
    }, [
        cancelInactivePlaybackSourceRelease,
        releaseInactivePlaybackSource,
    ]);

    /**
     * Audio notes can appear dozens of times on a single page. Lazily creating
     * the small input click sound avoids consuming a media player for every
     * note before the user actually interacts with one.
     */
    const getInputClickClip = useCallback((): HTMLAudioElement | null => {
        if (!hasSound) {
            return null;
        }

        if (!inputClickClip.current) {
            const clickClip = new Audio();
            clickClip.volume = DEFAULT_AUDIO_VOLUME;
            clickClip.src = InputClick;
            inputClickClip.current = clickClip;
        }

        return inputClickClip.current;
    }, [hasSound]);

    /**
     * Recreate a local playback URL from the stored blob when we previously
     * released the media player to free space.
     */
    const ensureLocalPlaybackUrl = useCallback((): string | null => {
        if (audioURL) {
            return audioURL;
        }

        if (!audioBlob) {
            return null;
        }

        const nextBlobUrl = URL.createObjectURL(audioBlob);
        setLocalPreviewUrl(nextBlobUrl);
        return nextBlobUrl;
    }, [audioBlob, audioURL, setLocalPreviewUrl]);

    /**
     * When playback restarts after releasing the media player, restore the
     * saved timestamp before we call `play()` so resume continues from the
     * correct position.
     */
    const restorePlaybackPosition = useCallback(async (): Promise<void> => {
        if (!audioNodeRef.current) {
            return;
        }

        const targetTimeSeconds = timeElapsed > 0
            ? timeElapsed / MILLISECONDS_IN_A_SECOND
            : 0;

        if (targetTimeSeconds === 0) {
            if (
                audioNodeRef.current.readyState > 0
                && audioNodeRef.current.currentTime !== 0
            ) {
                audioNodeRef.current.currentTime = 0;
            }
            return;
        }

        if (audioNodeRef.current.readyState === 0) {
            await new Promise<void>((resolve) => {
                if (!audioNodeRef.current) {
                    resolve();
                    return;
                }

                const audioElement = audioNodeRef.current;
                let settled = false;
                const finish = (): void => {
                    if (settled) {
                        return;
                    }

                    settled = true;
                    resolve();
                };

                const handleReady = (): void => {
                    audioElement.removeEventListener('loadedmetadata', handleReady);
                    audioElement.removeEventListener('loadeddata', handleReady);
                    finish();
                };

                audioElement.addEventListener('loadedmetadata', handleReady, { once: true });
                audioElement.addEventListener('loadeddata', handleReady, { once: true });
                window.setTimeout(() => {
                    audioElement.removeEventListener('loadedmetadata', handleReady);
                    audioElement.removeEventListener('loadeddata', handleReady);
                    finish();
                }, 1000);
            });
        }

        if (!audioNodeRef.current) {
            return;
        }

        const { duration } = audioNodeRef.current;
        const boundedTargetTimeSeconds = Number.isFinite(duration) && duration > 0
            ? Math.min(targetTimeSeconds, duration)
            : targetTimeSeconds;
        audioNodeRef.current.currentTime = boundedTargetTimeSeconds;
    }, [timeElapsed]);

    /**
     * Reset playback bookkeeping and release the media player when this note
     * is no longer actively playing. When pausing mid-track we keep the saved
     * elapsed time so a later play request can rebuild the source and resume.
     */
    const stopPlaybackAndRelease = useCallback((options: {
        resetElapsed: boolean,
    }): void => {
        const {
            resetElapsed,
        } = options;

        if (audioNodeRef.current) {
            audioNodeRef.current.pause();

            if (resetElapsed) {
                audioNodeRef.current.currentTime = 0;
            }
        }

        setIsStartingPlayback(false);
        setIsUpdatingTime(false);
        setStartTime(null);
        setSavedTime(null);
        setStatus(AUDIO_NOTE_RECORDING_STATE.pausedComplete);

        if (resetElapsed) {
            setTimeElapsed(0);
            releaseInactivePlaybackSource();
            return;
        }

        scheduleInactivePlaybackSourceRelease();
    }, [
        releaseInactivePlaybackSource,
        scheduleInactivePlaybackSourceRelease,
    ]);
    const cancelPreviewAudioEndPrompt = useCallback((): void => {
        previewAudioEndPromptRequestIdRef.current += 1;

        if (!previewAudioEndPromptUtteranceRef.current) {
            return;
        }

        previewAudioEndPromptUtteranceRef.current = null;

        if (
            typeof window === 'undefined'
            || typeof window.speechSynthesis === 'undefined'
        ) {
            return;
        }

        window.speechSynthesis.cancel();
    }, []);

    /**
     * Speak the access reminder locally in the browser so preview playback can
     * end with clear next-step copy without requiring backend audio synthesis.
     */
    const speakPreviewAudioEndPrompt = useCallback((): void => {
        if (
            !shouldPlayReadOnlyPreviewAudioEndPrompt
            || normalizedPreviewAudioEndPromptText.length === 0
        ) {
            return;
        }

        if (
            typeof window === 'undefined'
            || typeof window.speechSynthesis === 'undefined'
            || typeof SpeechSynthesisUtterance === 'undefined'
        ) {
            return;
        }

        cancelPreviewAudioEndPrompt();

        const requestId = previewAudioEndPromptRequestIdRef.current + 1;
        previewAudioEndPromptRequestIdRef.current = requestId;
        const utterance = new SpeechSynthesisUtterance(normalizedPreviewAudioEndPromptText);
        const clearIfCurrent = (): void => {
            if (previewAudioEndPromptRequestIdRef.current !== requestId) {
                return;
            }

            previewAudioEndPromptUtteranceRef.current = null;
        };

        utterance.onend = clearIfCurrent;
        utterance.onerror = clearIfCurrent;
        previewAudioEndPromptUtteranceRef.current = utterance;
        window.speechSynthesis.speak(utterance);
    }, [
        cancelPreviewAudioEndPrompt,
        normalizedPreviewAudioEndPromptText,
        shouldPlayReadOnlyPreviewAudioEndPrompt,
    ]);

    /**
     * Playback completion always resets the audio note. Preview dictation notes
     * then append the spoken auth or purchase reminder.
     */
    const handleAudioPlaybackEnded = useCallback((): void => {
        if (playbackEndHandledRef.current) {
            return;
        }

        if (playbackStatusRef.current !== AUDIO_NOTE_RECORDING_STATE.playing) {
            return;
        }

        playbackEndHandledRef.current = true;
        stopPlaybackAndRelease({ resetElapsed: true });
        speakPreviewAudioEndPrompt();
    }, [
        speakPreviewAudioEndPrompt,
        stopPlaybackAndRelease,
    ]);

    // Store file locally
    const onDropAccepted = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            // Get File local URL
            const file = acceptedFiles[0];
            const blob = file;
            const noteURL = URL.createObjectURL(blob);

            // Set Title
            setAudioTitle(file.name);
            // Save Blob, URL, and set to Pause Complete
            setAudioBlob(blob);
            setMimeType(blob.type);
            setLocalPreviewUrl(noteURL);
            setStatus(AUDIO_NOTE_RECORDING_STATE.pausedComplete);
            setIsPreparingRecordedPreview(false);
            setHasStartedPlayback(false);
            setTimeElapsed(0);
            recordedWaveformLevelSamplesRef.current = [];
            recordedWaveformDisplayBarsRef.current = [];
            persistedDurationMsRef.current = null;
            persistedWaveformBarsRef.current = null;
            waveformSourceKeyRef.current = null;
            setWaveformBars(buildSilentVisibleWaveformBars());
            setPostContentSpeechGenerationRequested(false);
            // Flag as uploaded audio note
            setAudioUploaded(true);
        }
    }, [
        buildSilentVisibleWaveformBars,
        setLocalPreviewUrl,
        syncAudioElementSource,
    ]);
    const {
        open: openFileSelector,
        getRootProps,
        getInputProps,
    } = useDropzone({
        noClick: true,
        noDrag: true,
        noKeyboard: true,
        accept: [
            'audio/x-m4a',
            'audio/mpeg',
            'audio/x-aiff',
            'audio/webm',
            'application/ogg',
            'audio/aiff',
            'audio/aifc',
            'audio/m4a',
            'audio/aac',
            'audio/flac',
            'audio/x-flac',
            'audio/mp3',
            'audio/mpeg3',
            'audio/mpg',
            'audio/x-mp3',
            'audio/x-mpeg',
            'audio/x-mpeg3',
            'audio/x-mpg',
            'application/x-ogg',
            'audio/x-ogg',
            'audio/wav',
            'audio/s-wav',
            'audio/wave',
            'audio/x-wav',
        ],
        onDropAccepted,
    });

    // ===== Animation Constants =====

    const TRANSCRIPT_TRANSITION_DURATION = 400;
    const PROGRESS_INDICATOR_DELAY_DURATION = 300;
    const INACTIVE_PLAYBACK_SOURCE_RELEASE_GRACE_DURATION = 8000;

    // ========== Methods ===========

    const fetchMediaItemLatest = useLatest({
        audioNoteMediaItem,
        showTranscript,
    });
    const isMountedRef = useRef<boolean>(true);
    const fetchMediaItemRequestIdsRef = useRef<{
        includeTranscript: number,
        playbackOnly: number,
    }>({
        includeTranscript: 0,
        playbackOnly: 0,
    });
    const fetchMediaItemPromisesRef = useRef<{
        includeTranscript: Promise<IEditorAudioNoteItem | null> | null,
        playbackOnly: Promise<IEditorAudioNoteItem | null> | null,
    }>({
        includeTranscript: null,
        playbackOnly: null,
    });
    const fetchMediaItemReplayRequestedRef = useRef<{
        includeTranscript: boolean,
        playbackOnly: boolean,
    }>({
        includeTranscript: false,
        playbackOnly: false,
    });
    const getTranscriptWordCount = (transcript: IEditorAudioNoteItem['transcript'] | null | undefined): number => {
        const transcriptData = transcript?.data;
        if (!Array.isArray(transcriptData)) {
            return 0;
        }

        // Prefer precise STT word offsets when present.
        const timedWordCount = transcriptData.reduce((count: number, phrase: any) => (
            count + (Array.isArray(phrase?.alternatives?.[0]?.words) ? phrase.alternatives[0].words.length : 0)
        ), 0);
        if (timedWordCount > 0) {
            return timedWordCount;
        }

        // Fallback for transcripts that only contain phrase text.
        return transcriptData.reduce((count: number, phrase: any) => {
            const transcriptText = phrase?.alternatives?.[0]?.transcript;
            if (typeof transcriptText !== 'string') return count;
            const phraseWordCount = transcriptText.trim().split(/\s+/).filter(Boolean).length;
            return count + phraseWordCount;
        }, 0);
    };
    /**
     * Waits one paint turn after transcript hydration so read-along highlights can
     * render before audio starts advancing.
     */
    const waitForTranscriptHydrationPaint = useCallback(async (): Promise<void> => {
        if (
            typeof window === 'undefined'
            || typeof window.requestAnimationFrame !== 'function'
        ) {
            return;
        }

        await new Promise<void>((resolve) => {
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => resolve());
            });
        });
    }, []);
    const fetchMediaItem = useCallback(async (
        options?: {
            includeTranscript?: boolean,
            bypassCachedPlaybackSource?: boolean,
        },
    ): Promise<IEditorAudioNoteItem | null> => {
        const includeTranscript = options?.includeTranscript ?? true;
        const bypassCachedPlaybackSource = options?.bypassCachedPlaybackSource === true;
        const requestLane = includeTranscript ? 'includeTranscript' : 'playbackOnly';

        // Playback-only refreshes only need an authoritative audio URL/path.
        // Skipping transcript fetches avoids rehydrating large transcript blobs
        // during a play click.
        if (!auth.currentUser) {
            logAudioNotePreviewDebug('fetchMediaItem:skip-no-auth-user', {
                id,
                includeTranscript,
                readOnly,
                readOnlyUsesPreviewSource,
                postContentDictationEnabled,
                authResolvedUserId,
            });
            return null;
        }
        const cur = fetchMediaItemLatest.current;
        const latestMediaItem = audioNoteMediaItemRef.current;
        if (
            latestMediaItem
            && !includeTranscript
            && !bypassCachedPlaybackSource
        ) {
            const cachedMediaItemHasUsablePlaybackSource = (
                !requiresBackendIssuedPlaybackUrl
                || isHttpAudioUrl(latestMediaItem.resolvedFileURL)
            );

            if (cachedMediaItemHasUsablePlaybackSource) {
                return latestMediaItem;
            }
        }
        /**
         * Keep playback clicks on their own request lane. Preview transcript
         * hydration can take long enough that browsers no longer treat the
         * eventual `audio.play()` call as part of the original user gesture.
         * Status refreshes can still bypass the cached playback shortcut while
         * sharing the same lightweight lane.
         */
        if (fetchMediaItemPromisesRef.current[requestLane]) {
            fetchMediaItemReplayRequestedRef.current[requestLane] = true;
            return fetchMediaItemPromisesRef.current[requestLane];
        }
        const requestId = fetchMediaItemRequestIdsRef.current[requestLane] + 1;
        fetchMediaItemRequestIdsRef.current[requestLane] = requestId;
        fetchMediaItemReplayRequestedRef.current[requestLane] = false;
        logAudioNotePreviewDebug('fetchMediaItem:start', {
            id,
            requestId,
            requestLane,
            includeTranscript,
            authUserId: auth.currentUser.uid,
            authResolvedUserId,
            readOnly,
            readOnlyUsesPreviewSource,
            postContentDictationEnabled,
            shouldUsePreviewDictationTranscript,
            postId: postId || null,
            chapterId: chapterId || null,
            sectionId: sectionId || null,
            normalizedPostContentTranscriptLength: normalizedPostContentTranscriptText.length,
        });
        const requestPromise = getEditorAudioNoteItem({
            test: isTestMode(),
            mediaId: id,
            includeTranscript,
            previewSourceText: shouldUsePreviewDictationTranscript
                ? normalizedPostContentTranscriptText
                : undefined,
            postId,
            chapterId,
            sectionId,
        }).then((audioNoteItem) => {
            if (
                !isMountedRef.current
                || fetchMediaItemRequestIdsRef.current[requestLane] !== requestId
            ) {
                return null;
            }
            const latestResolvedMediaItem = audioNoteMediaItemRef.current;
            let nextTranscript = latestResolvedMediaItem?.transcript;
            if (includeTranscript && audioNoteItem?.transcript !== undefined) {
                nextTranscript = audioNoteItem.transcript;
            }
            // Playback-only preview refreshes can intentionally omit duration
            // while still carrying a fresh playback URL. Preserve the last
            // authoritative duration instead of blanking the timer again.
            let nextDurationMs = latestResolvedMediaItem?.durationMs;
            const incomingDurationMs = sanitizeAudioNoteDurationMs(audioNoteItem?.durationMs);
            if (incomingDurationMs !== undefined) {
                nextDurationMs = incomingDurationMs;
            }
            const mergedTranscriptionJob = resolveMostRecentAudioNoteTranscriptionJob({
                current: latestResolvedMediaItem?.transcriptionJob,
                incoming: audioNoteItem?.transcriptionJob,
            });
            const nextAudioNoteItem = audioNoteItem
                ? {
                    ...(latestResolvedMediaItem || {}),
                    ...audioNoteItem,
                    ...(nextDurationMs !== undefined ? { durationMs: nextDurationMs } : {}),
                    ...(nextTranscript !== undefined ? { transcript: nextTranscript } : {}),
                    ...(mergedTranscriptionJob !== undefined
                        ? { transcriptionJob: mergedTranscriptionJob }
                        : {}),
                }
                : null;
            const effectiveTranscript = shouldUsePreviewDictationTranscript
                ? trimAudioNoteTranscriptToSourceText(
                    nextAudioNoteItem?.transcript,
                    normalizedPostContentTranscriptText,
                )
                : nextAudioNoteItem?.transcript;
            const transcriptWordCount = getTranscriptWordCount(effectiveTranscript);
            if (
                nextAudioNoteItem
                && effectiveTranscript != null
                && transcriptWordCount > 0
                && transcriptWordCount <= TRANSCRIPT_AUTO_REVEAL_WORD_LIMIT
                && !showTranscriptRef.current
            ) {
                broadcastHeightAdjusted();
                // show transcript now that we have the data
                showTranscriptRef.current = true;
                setShowTranscript(true);
            }
            if (nextAudioNoteItem) {
                audioNoteMediaItemRef.current = nextAudioNoteItem;
                setAudioNoteMediaItem(nextAudioNoteItem);
            }
            logAudioNotePreviewDebug('fetchMediaItem:success', {
                id,
                requestId,
                requestLane,
                includeTranscript,
                authUserId: auth.currentUser?.uid || null,
                returnedFilePath: nextAudioNoteItem?.filePath || null,
                returnedResolvedFileURL: nextAudioNoteItem?.resolvedFileURL || null,
                returnedIsPostContentDictation: nextAudioNoteItem?.isPostContentDictation === true,
                transcriptWordCount,
                showTranscriptBeforeAutoReveal: cur.showTranscript,
            });
            return nextAudioNoteItem;
        }).catch((error) => {
            logAudioNotePreviewDebug('fetchMediaItem:error', {
                id,
                requestId,
                requestLane,
                includeTranscript,
                authUserId: auth.currentUser?.uid || null,
                error: error instanceof Error ? error.message : String(error),
            });
            return null;
        }).finally(() => {
            // During auth transitions (e.g., sign-out -> anonymous),
            // this callable can briefly return unauthenticated.
            // A later auth change reruns the effect below.
            if (fetchMediaItemPromisesRef.current[requestLane] === requestPromise) {
                fetchMediaItemPromisesRef.current[requestLane] = null;
            }
            /**
             * Ping updates can land while an older media fetch is still in
             * flight. Queue one follow-up fetch so terminal status changes do
             * not wait for a full page reload.
             */
            if (fetchMediaItemReplayRequestedRef.current[requestLane]) {
                fetchMediaItemReplayRequestedRef.current[requestLane] = false;
                fetchMediaItem({
                    includeTranscript,
                }).catch(() => undefined);
            }
        });

        fetchMediaItemPromisesRef.current[requestLane] = requestPromise;
        return requestPromise;
    }, [
        authResolvedUserId,
        audioLength,
        chapterId,
        hydratedPostContentDictationEnabled,
        id,
        normalizedPostContentTranscriptText,
        postContentDictationEnabled,
        postId,
        readOnly,
        readOnlyUsesPreviewSource,
        requiresAuthoritativePreviewMediaItem,
        requiresBackendIssuedPlaybackUrl,
        sectionId,
        shouldUsePreviewDictationTranscript,
        status,
        fetchMediaItemLatest,
    ]);

    /**
     * Audio notes can mount before Firebase restores the anonymous session.
     * Track auth changes locally so preview fetches rerun as soon as auth is ready.
     */
    useEffect(() => onAuthStateChanged(auth, (nextUser) => {
        logAudioNotePreviewDebug('authStateChanged', {
            id,
            nextUserId: nextUser?.uid || null,
            isAnonymous: nextUser?.isAnonymous === true,
        });
        setAuthResolvedUserId(nextUser?.uid || null);
    }), []);

    const hasPostContentDictationTranscript = useMemo(() => {
        const candidateTranscript = shouldUsePreviewDictationTranscript
            ? trimAudioNoteTranscriptToSourceText(
                audioNoteMediaItem?.transcript,
                normalizedPostContentTranscriptText,
            )
            : (audioNoteMediaItem?.transcript || null);

        return getTranscriptWordCount(candidateTranscript) > 0;
    }, [
        audioNoteMediaItem?.transcript,
        normalizedPostContentTranscriptText,
        shouldUsePreviewDictationTranscript,
    ]);
    const hasPersistedPostContentDictationTranscript = useMemo(() => (
        String(audioNoteMediaItem?.transcriptStoragePath || '').trim().length > 0
    ), [audioNoteMediaItem?.transcriptStoragePath]);
    const hasSerializedPostContentDictationTranscript = normalizedPostContentTranscriptText.length > 0;
    /**
     * Playback-only fetches intentionally skip large transcript payloads. Once
     * metadata says a transcript is already persisted, keep the read-along hint
     * visible while the transcript blob is still hydrating in the background.
     */
    const isPostContentDictationTranscriptFetchPending = useMemo(() => {
        if (!hasPersistedPostContentDictationTranscript) {
            return false;
        }

        return typeof audioNoteMediaItem?.transcript === 'undefined';
    }, [
        audioNoteMediaItem?.transcript,
        hasPersistedPostContentDictationTranscript,
    ]);
    /**
     * Ping-driven refreshes should stay lightweight so long-running transcript
     * hydration does not pin the write-mode progress badge on an older
     * percentage. Once metadata says a transcript exists, hydrate that larger
     * payload separately in the background.
     */
    useEffect(() => {
        if (
            isNewAudioNote
            || !audioNoteMediaItem
            || audioNoteMediaItem.transcript != null
            || String(audioNoteMediaItem.transcriptStoragePath || '').trim().length === 0
        ) {
            return;
        }

        lastRequestedTranscriptStorageHydrationRef.current = audioNoteMediaItem.transcriptStoragePath || null;
        fetchMediaItem({
            includeTranscript: true,
        }).catch(() => undefined);
    }, [
        audioNoteMediaItem,
        fetchMediaItem,
        isNewAudioNote,
    ]);

    /**
     * Keep uploaded post-content dictation audio notes linked to the latest
     * post text. Generated-speech notes must use the explicit Generate Speech
     * save path because source text changes require new audio, not just a
     * transcription refresh against the old file.
     */
    useEffect(() => {
        if (postContentDictationSourceSyncTimerRef.current) {
            clearTimeout(postContentDictationSourceSyncTimerRef.current);
            postContentDictationSourceSyncTimerRef.current = null;
        }

        const sourceTextToSync = normalizedPostContentTranscriptText;
        if (
            readOnly
            || editorType !== EDITOR_CONTEXT_TYPE.post
            || !postContentDictationEnabled
            || isNewAudioNote
            || !audioNoteMediaItem
            || audioNoteMediaItem.generatedSpeech === true
            || sourceTextToSync.length === 0
            || String(filePath || '').trim().length === 0
        ) {
            return undefined;
        }

        if (lastRequestedPostContentDictationSourceTextRef.current === sourceTextToSync) {
            return undefined;
        }

        postContentDictationSourceSyncTimerRef.current = setTimeout(() => {
            lastRequestedPostContentDictationSourceTextRef.current = sourceTextToSync;
            postContentDictationSourceSyncTimerRef.current = null;

            updateMedia({
                test: isTestMode(),
                mediaId: id,
                type: MEDIA_TYPE.audioNote,
                isPostContentDictation: true,
                dictationSourceText: sourceTextToSync,
            }).catch((error) => {
                lastRequestedPostContentDictationSourceTextRef.current = null;
                console.warn('Failed to sync post dictation source text for audio note.', error);
            });
        }, POST_CONTENT_DICTATION_SOURCE_SYNC_DEBOUNCE_MS);

        return () => {
            if (!postContentDictationSourceSyncTimerRef.current) {
                return;
            }

            clearTimeout(postContentDictationSourceSyncTimerRef.current);
            postContentDictationSourceSyncTimerRef.current = null;
        };
    }, [
        audioNoteMediaItem,
        editorType,
        filePath,
        id,
        isNewAudioNote,
        normalizedPostContentTranscriptText,
        postContentDictationEnabled,
        readOnly,
    ]);
    const shouldShowPostContentDictationHint = (
        (postContentDictationEnabled || postContentSpeechGenerationRequested)
        && (
            hasSerializedPostContentDictationTranscript
            || hasPostContentDictationTranscript
            || isPostContentDictationTranscriptFetchPending
        )
    );

    const startRecord = (): void => {
        if (hasRecordingPermissions) {
            setPostContentSpeechGenerationRequested(false);
            // Starting AudioNote
            navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: false,
                    autoGainControl: false,
                },
            }).then((stream) => {
                // Modify recording state
                setStatus(AUDIO_NOTE_RECORDING_STATE.recording);
                // Notify parent editor that recording is taking place
                setIsRecordingAudioNote(true);
                mediaRecorderRef.current = new MediaRecorder(stream, {
                    audioBitsPerSecond: 128000,
                });
                recordedAudioChunksRef.current = [];
                recordedWaveformLevelSamplesRef.current = [];
                recordedWaveformDisplayBarsRef.current = [];
                persistedDurationMsRef.current = null;
                persistedWaveformBarsRef.current = null;
                recordingWaveformLastSampleAtRef.current = null;
                // Start recording
                mediaRecorderRef.current.start(AUDIO_NOTE_RECORDING_WAVEFORM_CONFIG.chunkTimesliceMs);
                // Cache timestamp started recording
                setStartTime(Date.now());
                // Update Recording timestamp
                setIsUpdatingTime(true);
                waveformSourceKeyRef.current = null;
                connectRecordingWaveformStream(stream).catch(() => {
                    setWaveformBars(buildSilentVisibleWaveformBars());
                });
            }).catch((e) => {
                setStatus(AUDIO_NOTE_RECORDING_STATE.idle);
                switch (e.name) {
                case 'NotAllowedError':
                    setErrorMessage(AUDIO_NOTE_PERMISSION_DENIED_ERROR);
                    if (hasRecordingPermissions) {
                        setHasRecordingPermissions(false);
                    }
                    break;
                case 'NotFoundError':
                case 'DevicesNotFoundError':
                    setErrorMessage(AUDIO_NOTE_RECORDING_DEVICE_NOT_FOUND);
                    if (hasRecordingPermissions) {
                        setHasRecordingPermissions(false);
                    }
                    break;
                case 'SourceUnavailableError':
                    setErrorMessage(AUDIO_NOTE_RECORDING_DEVICE_BUSY);
                    if (hasRecordingPermissions) {
                        setHasRecordingPermissions(false);
                    }
                    break;
                case 'PermissionDeniedError':
                case 'SecurityError':
                    setErrorMessage(AUDIO_NOTE_PERMISSION_DENIED_ERROR);
                    if (hasRecordingPermissions) {
                        setHasRecordingPermissions(false);
                    }
                    break;
                default:
                    setErrorMessage(AUDIO_NOTE_GENERAL_ERROR);
                    if (hasRecordingPermissions) {
                        setHasRecordingPermissions(false);
                    }
                }
            });
        }
    };

    const stopRecord = (): void => {
        // Modidy recording state
        setStatus(AUDIO_NOTE_RECORDING_STATE.pausedComplete);
        setIsPreparingRecordedPreview(true);
        shouldFocusDescriptionAfterRecordingRef.current = editorType === EDITOR_CONTEXT_TYPE.annotation;
        // Notify parent editor that recording is not taking place
        setIsRecordingAudioNote(false);
        setTimeElapsed(0);
        // Stop Recording
        mediaRecorderRef.current?.stop();
        // Stop Updating Recording Timestamp
        setIsUpdatingTime(false);
        stopRecordingWaveformAnimation();
        // Clear initial elapsed start time timestamp
        setStartTime(null);
        setSavedTime(null);
    };

    const pauseRecord = (): void => {
        setStatus(AUDIO_NOTE_RECORDING_STATE.pausedRecording);
        // Pause Recording
        mediaRecorderRef.current?.pause();
        // Stop Updating Recording Timestamp
        setIsUpdatingTime(false);
        stopRecordingWaveformAnimation();
        // Collect and save elapsed time
        const difference = computeTimeElapsed();
        setSavedTime(difference);
    };

    const resumeRecord = (): void => {
        setStatus(AUDIO_NOTE_RECORDING_STATE.recording);
        // Resume Recording
        mediaRecorderRef.current?.resume();

        // Update Recording timestamp
        setIsUpdatingTime(true);
        // Cache timestamp started recording
        setStartTime(Date.now());
        startRecordingWaveformAnimation();
    };

    /**
     * Start or stop playback for the current note. Playback sources are
     * recreated on demand so previously used notes do not keep media players
     * mounted after the user moves on to another note.
     */
    const playAudioNote = async (play: boolean): Promise<void> => {
        cancelPreviewAudioEndPrompt();

        if (!play) {
            if (!audioNodeRef.current) return;

            const pausedElapsed = audioNodeRef.current.currentTime * MILLISECONDS_IN_A_SECOND;
            if (!Number.isNaN(pausedElapsed)) {
                setTimeElapsed(pausedElapsed);
            }

            stopPlaybackAndRelease({ resetElapsed: false });

            if (readOnly && postId && currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.pauseAudioNote,
                    sessionId: currentSessionId,
                    payload: { id, editorType, postId },
                });
            }

            broadcastHeightAdjusted();
            return;
        }

        if (isPreparingRecordedPreview) {
            return;
        }

        if (isStartingPlayback) return;

        playbackEndHandledRef.current = false;
        cancelInactivePlaybackSourceRelease();
        logAudioNotePreviewDebug('playAudioNote:start', {
            id,
            authResolvedUserId,
            authCurrentUserId: auth.currentUser?.uid || null,
            requiresAuthoritativePreviewMediaItem,
            requiresBackendIssuedPlaybackUrl,
            existingAudioURL: audioURL,
            existingRemoteFilePath: remoteFilePath,
            existingPlayableFilePath: playableFilePath,
            mediaItemFilePath: audioNoteMediaItem?.filePath || null,
            mediaItemResolvedFileURL: audioNoteMediaItem?.resolvedFileURL || null,
            transcriptWordCount: getTranscriptWordCount(effectiveTranscript),
        });

        if (
            requiresBackendIssuedPlaybackUrl
            && !authResolvedUserId
            && !auth.currentUser?.uid
        ) {
            logAudioNotePreviewDebug('playAudioNote:skip-no-authoritative-auth', {
                id,
                authResolvedUserId,
                authCurrentUserId: auth.currentUser?.uid || null,
            });
            return;
        }

        let playbackResolvedFileURL = (
            isHttpAudioUrl(audioNoteMediaItem?.resolvedFileURL)
        )
            ? (audioNoteMediaItem?.resolvedFileURL || null)
            : null;
        let playbackFilePath = playableFilePath;
        const requiresTranscriptBeforePlayback = (
            readOnly
            && postContentDictationEnabled
            && shouldUsePreviewDictationTranscript
        );
        const shouldWaitForTranscriptBeforePlayback = (
            requiresTranscriptBeforePlayback
            && !hasPostContentDictationTranscript
        );

        /**
         * Post-content dictation playback is also a read-along experience.
         * Wait for one transcript hydration attempt before starting, then fall
         * back to audio-only when the transcript is genuinely unavailable.
         */
        if (
            shouldWaitForTranscriptBeforePlayback
            && !hasPostContentDictationTranscript
        ) {
            setIsStartingPlayback(true);
            const transcriptMediaItem = await fetchMediaItem({
                includeTranscript: true,
            });
            const candidateTranscript = shouldUsePreviewDictationTranscript
                ? trimAudioNoteTranscriptToSourceText(
                    transcriptMediaItem?.transcript,
                    normalizedPostContentTranscriptText,
                )
                : (transcriptMediaItem?.transcript || null);
            const transcriptWordCount = getTranscriptWordCount(candidateTranscript);

            playbackResolvedFileURL = isHttpAudioUrl(transcriptMediaItem?.resolvedFileURL)
                ? (transcriptMediaItem?.resolvedFileURL || null)
                : playbackResolvedFileURL;
            playbackFilePath = typeof transcriptMediaItem?.filePath === 'string'
                && transcriptMediaItem.filePath.trim().length > 0
                ? transcriptMediaItem.filePath
                : playbackFilePath;
            logAudioNotePreviewDebug('playAudioNote:fetched-transcript-before-playback', {
                id,
                fetchedFilePath: transcriptMediaItem?.filePath || null,
                fetchedResolvedFileURL: transcriptMediaItem?.resolvedFileURL || null,
                transcriptWordCount,
            });
            if (transcriptWordCount > 0) {
                await waitForTranscriptHydrationPaint();
            }
            if (transcriptWordCount <= 0) {
                logAudioNotePreviewDebug('playAudioNote:continue-without-transcript-after-fetch', {
                    id,
                    shouldUsePreviewDictationTranscript,
                    normalizedPostContentTranscriptLength: normalizedPostContentTranscriptText.length,
                    fetchedHasTranscript: transcriptMediaItem?.transcript != null,
                });
            }
        }
        const needsAuthoritativePlaybackFetch = (
            requiresBackendIssuedPlaybackUrl
            && !playbackResolvedFileURL
        );

        if (needsAuthoritativePlaybackFetch) {
            setIsStartingPlayback(true);
            const fetchedMediaItem = await fetchMediaItem({
                includeTranscript: false,
            });
            playbackResolvedFileURL = isHttpAudioUrl(fetchedMediaItem?.resolvedFileURL)
                ? (fetchedMediaItem?.resolvedFileURL || null)
                : null;
            playbackFilePath = typeof fetchedMediaItem?.filePath === 'string'
                && fetchedMediaItem.filePath.trim().length > 0
                ? fetchedMediaItem.filePath
                : null;
            logAudioNotePreviewDebug('playAudioNote:fetched-authoritative-media', {
                id,
                fetchedFilePath: fetchedMediaItem?.filePath || null,
                fetchedResolvedFileURL: fetchedMediaItem?.resolvedFileURL || null,
                fetchedTranscriptWordCount: getTranscriptWordCount(fetchedMediaItem?.transcript),
            });
        }

        if (
            requiresBackendIssuedPlaybackUrl
            && !playbackResolvedFileURL
        ) {
            setIsStartingPlayback(false);
            setStatus(AUDIO_NOTE_RECORDING_STATE.idle);
            setErrorMessage(audioNotePlaybackFetchErrorMessage);
            logAudioNotePreviewDebug('playAudioNote:fail-no-authoritative-playback-url', {
                id,
                requiresAuthoritativePreviewMediaItem,
                requiresBackendIssuedPlaybackUrl,
                mediaItemFilePath: audioNoteMediaItem?.filePath || null,
                mediaItemResolvedFileURL: audioNoteMediaItem?.resolvedFileURL || null,
            });
            return;
        }

        const shouldResolveRemotePlayback = shouldResolveRemotePlaybackUrl({
            audioBlob,
            audioUrl: playbackResolvedFileURL || audioURL,
            playableFilePath: playbackFilePath,
        });
        const shouldSyncResolvedRemotePlayback = (
            !!playbackResolvedFileURL
            && playbackResolvedFileURL !== audioURL
        );
        const audioElementNeedsKnownSource = (
            !!audioURL
            && getAudioElementSourceUrl() !== audioURL
        );
        const shouldWarmPlaybackSource = (
            needsAuthoritativePlaybackFetch
            || shouldSyncResolvedRemotePlayback
            || shouldResolveRemotePlayback
            || audioElementNeedsKnownSource
            || (!audioURL && !!audioBlob)
        );
        // Only show loader while we still need to resolve a playable streaming
        // URL or recreate a released local preview source. If the current
        // source is still warm, avoid a spinner flash.
        const shouldShowStartupSpinner = shouldWarmPlaybackSource;
        if (shouldShowStartupSpinner) {
            setIsStartingPlayback(true);
            const mountedAudioElement = await waitForMountedAudioElement();
            await primeAudioElementForAsyncPlaybackStart({
                audioElement: mountedAudioElement,
                syncAudioSource: syncAudioElementSource,
            });
        }

        const startPlayback = async (): Promise<void> => {
            try {
                if (!shouldResolveRemotePlayback) {
                    const localPlaybackUrl = ensureLocalPlaybackUrl();
                    const shouldSyncLocalPlaybackSource = (
                        !!localPlaybackUrl
                        && getAudioElementSourceUrl() !== localPlaybackUrl
                    );
                    if (shouldSyncLocalPlaybackSource) {
                        await waitForRenderedAudioSource();
                        syncAudioElementSource(
                            localPlaybackUrl,
                            normalizeRecordedAudioBlobMimeType(audioBlob?.type || mimeType),
                            { forceReload: true },
                        );
                    }
                }

                if (shouldSyncResolvedRemotePlayback && playbackResolvedFileURL) {
                    setAudioURL(playbackResolvedFileURL);
                    await waitForRenderedAudioSource();
                    syncAudioElementSource(
                        playbackResolvedFileURL,
                        inferAudioMimeTypeFromUrl(playbackResolvedFileURL)
                        || inferAudioMimeTypeFromUrl(playbackFilePath),
                        { forceReload: true },
                    );
                }

                if (
                    audioElementNeedsKnownSource
                    && audioURL
                    && !shouldSyncResolvedRemotePlayback
                    && !shouldResolveRemotePlayback
                    && getAudioElementSourceUrl() !== audioURL
                ) {
                    await waitForRenderedAudioSource();
                    syncAudioElementSource(
                        audioURL,
                        playbackSourceMimeType,
                        { forceReload: true },
                    );
                }

                if (shouldResolveRemotePlayback) {
                    const url = await resolveStreamingURL({
                        audioUrl: playbackResolvedFileURL || null,
                        playableFilePath: playbackFilePath,
                        resolvedFileURL: playbackResolvedFileURL,
                    });

                    if (!url) {
                        setIsStartingPlayback(false);
                        setStatus(AUDIO_NOTE_RECORDING_STATE.idle);
                        setErrorMessage(audioNotePlaybackFetchErrorMessage);
                        logAudioNotePreviewDebug('playAudioNote:fail-no-streaming-url', {
                            id,
                            playbackFilePath,
                            playbackResolvedFileURL,
                        });
                        return;
                    }

                    setAudioURL(url);
                    logAudioNotePreviewDebug('playAudioNote:resolved-streaming-url', {
                        id,
                        playbackFilePath,
                        playbackResolvedFileURL,
                        resolvedStreamingURL: url,
                    });
                    await waitForRenderedAudioSource();
                    syncAudioElementSource(
                        url,
                        inferAudioMimeTypeFromUrl(url) || inferAudioMimeTypeFromUrl(playbackFilePath),
                        { forceReload: true },
                    );
                }

                await waitForRenderedAudioSource();
                const playbackAudioElement = await waitForMountedAudioElement();
                if (!playbackAudioElement) {
                    throw new Error('Audio element was not available for playback.');
                }

                playbackAudioElement.playbackRate = playbackSpeed;
                await restorePlaybackPosition();

                setIsStartingPlayback(true);
                await waitForAudioPlaybackStart({
                    audioElement: playbackAudioElement,
                    playPromise: playbackAudioElement.play(),
                });

                setHasStartedPlayback(true);
                setIsStartingPlayback(false);
                setStatus(AUDIO_NOTE_RECORDING_STATE.playing);
                setIsUpdatingTime(true);
                setStartTime(Date.now());
                logAudioNotePreviewDebug('playAudioNote:playing', {
                    id,
                    audioCurrentSrc: playbackAudioElement.currentSrc || null,
                    playbackRate: playbackAudioElement.playbackRate || null,
                });

                if (readOnly && postId && currentSessionId) {
                    const actionId = uuidv4();
                    recordUserAction({
                        test: isTestMode(),
                        type: USER_ACTION_TYPE.playAudioNote,
                        actionId,
                        sessionId: currentSessionId,
                        payload: { id, editorType, postId },
                    });
                    updateMedia({
                        test: isTestMode(),
                        mediaId: id,
                        type: MEDIA_TYPE.audioNote,
                        playsToAdd: [actionId],
                    });
                }

                broadcastHeightAdjusted();
            } catch (error) {
                playbackEndHandledRef.current = false;
                setIsStartingPlayback(false);
                setStatus(AUDIO_NOTE_RECORDING_STATE.idle);
                setErrorMessage(
                    shouldResolveRemotePlayback
                        ? audioNotePlaybackFetchErrorMessage
                        : AUDIO_NOTE_GENERAL_ERROR,
                );
                setIsUpdatingTime(false);
                setStartTime(null);
                setSavedTime(null);
                releaseInactivePlaybackSource();
                logAudioNotePreviewDebug('playAudioNote:error', {
                    id,
                    shouldResolveRemotePlayback,
                    playbackFilePath,
                    playbackResolvedFileURL,
                    error: error instanceof Error ? error.message : String(error),
                });
            }
        };

        startPlayback();
    };
    const playAudioNoteLatest = useLatest(playAudioNote);

    /**
     * ReaderView's floating mini-player needs an imperative way to resume or
     * pause the currently active note without remounting the note itself.
     */
    const setReadOnlyPlaybackState = useCallback((play: boolean): void => {
        playAudioNoteLatest.current(play).catch(() => undefined);
    }, [playAudioNoteLatest]);

    const handleCreateAudioNote = useCallback((chunks: Blob[]): void => {
        const recordedChunkType = chunks.find((chunk) => chunk.type)?.type;
        const recorderMimeType = mediaRecorderRef.current?.mimeType;
        const blobMimeType = normalizeRecordedAudioBlobMimeType(recordedChunkType || recorderMimeType);
        const blob = new Blob(chunks, blobMimeType ? {
            type: blobMimeType,
        } : undefined);
        // Create audio url
        const noteURL = URL.createObjectURL(blob);
        const recordedWaveformBars = buildAudioWaveformBarsFromSampleLevels({
            sampleLevels: recordedWaveformLevelSamplesRef.current,
            barCount: DEFAULT_AUDIO_WAVEFORM_BAR_COUNT,
            ...COMPLETED_AUDIO_NOTE_WAVEFORM_BUILD_OPTIONS,
        });

        if (!audioUploaded && audioTitle.trim().length === 0) {
            setAudioTitle(title || getDefaultAudioTitle());
        }

        // Save blob and url
        setAudioBlob(blob);
        setMimeType(blob.type);
        setLocalPreviewUrl(noteURL);
        waveformSourceKeyRef.current = null;
        persistedWaveformBarsRef.current = hasMeaningfulAudioWaveformBars(recordedWaveformBars)
            ? recordedWaveformBars
            : null;
        setWaveformBars(() => {
            if (hasMeaningfulAudioWaveformBars(recordedWaveformBars)) {
                return buildRecordedVisibleWaveformBars(recordedWaveformLevelSamplesRef.current);
            }

            return buildSilentVisibleWaveformBars();
        });

        setIsPreparingRecordedPreview(false);
    }, [
        audioTitle,
        audioUploaded,
        buildRecordedVisibleWaveformBars,
        buildSilentVisibleWaveformBars,
        setLocalPreviewUrl,
        title,
    ]);

    const saveAudioNoteLatest = useLatest({
        id,
        audioTitle,
        audioUploaded,
        audioDescription: normalizedAudioDescription,
        isPostContentDictation: postContentDictationEnabled,
        filePath,
        waveformBars: preferredStoredWaveformBars,
        isSavingAudioNote,
        isNewAudioNote,
        isEditing,
    });

    /**
     * New audio notes finish saving in two independent async steps:
     * the media document write and the file upload.
     */
    const beginPendingNewAudioNoteSave = (): void => {
        pendingNewAudioNoteSaveRef.current = createPendingAudioNoteSaveState();
        setIsSavingAudioNote(true);
    };

    /**
     * Clear any pending new-note save state and remove the inline spinner.
     */
    const clearPendingNewAudioNoteSave = (): void => {
        pendingNewAudioNoteSaveRef.current = null;
        setIsSavingAudioNote(false);
    };

    /**
     * Keep the inline spinner visible until both save steps have completed.
     */
    const completePendingNewAudioNoteSaveStep = (
        step: AUDIO_NOTE_PENDING_SAVE_STEP,
    ): void => {
        const currentPendingSave = pendingNewAudioNoteSaveRef.current;
        if (!currentPendingSave) {
            setIsSavingAudioNote(false);
            return;
        }

        const nextPendingSave = markPendingAudioNoteSaveStepComplete({
            state: currentPendingSave,
            step,
        });
        pendingNewAudioNoteSaveRef.current = nextPendingSave;

        if (!isPendingAudioNoteSaveComplete(nextPendingSave)) {
            return;
        }

        pendingNewAudioNoteSaveRef.current = null;
        setIsSavingAudioNote(false);
    };

    const saveAudioNoteAsync = async (e?: React.MouseEvent | React.TouchEvent): Promise<void> => {
        if (e) e.nativeEvent.stopPropagation();
        if (!user) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem saving audio note. User data not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`There was a problem saving audio note. User data not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }

        // Focus Audio Note
        audioNodeRef.current?.parentElement?.click();
        const waveformBarsForPersistence = await resolveWaveformBarsForPersistence();
        const durationMsForPersistence = resolveAudioNoteDurationMsForPersistence();
        if (durationMsForPersistence != null) {
            persistedDurationMsRef.current = durationMsForPersistence;
            setAudioLength(durationMsForPersistence);
        }
        if (waveformBarsForPersistence) {
            persistedWaveformBarsRef.current = waveformBarsForPersistence;
            setWaveformBars(fitAudioWaveformBarsToBarCount({
                bars: waveformBarsForPersistence,
                barCount: visibleWaveformBarCountRef.current,
            }));
        }
        if (postContentSpeechGenerationRequested && postId) {
            if (generatedSpeechSourceTextForSave.length === 0) {
                setSnackbarData({
                    visible: true,
                    duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                    text: 'Post text is required before speech can be generated.',
                    icon: SpeakerIcon,
                    hasFailure: true,
                });
                return;
            }

            setIsSavingAudioNote(true);
            const uniqueId = new ShortUniqueId({ length: 6 })();
            const normalizedTitle = audioTitle.trim() || 'Generated Post Dictation';
            const fileName = `${normalizedTitle
                .toLowerCase()
                .split('.')[0]
                .replace(/[^a-z0-9-_]+/g, '_')}-${uniqueId}`;
            const result = await requestGeneratedPostAudioNoteSpeech({
                test: isTestMode(),
                mediaId: id,
                postId,
                fileName,
                title: normalizedTitle,
                description: normalizedAudioDescription,
                dictationSourceText: generatedSpeechSourceTextForSave,
                generateTranscript: shouldPersistPostContentDictation,
            });
            const generatedMediaItem = result.mediaItem;
            audioNoteMediaItemRef.current = generatedMediaItem;
            setAudioNoteMediaItem(generatedMediaItem);
            setAudioTitle(generatedMediaItem.title || normalizedTitle);
            setAudioDescription(sanitizeAudioNoteDescription(generatedMediaItem.description));
            setPostContentDictationEnabled(generatedMediaItem.isPostContentDictation === true);
            setPostContentSpeechGenerationRequested(false);
            setAudioUploaded(false);
            setAudioBlob(null);
            setMimeType('audio/mpeg');
            setRemoteFilePath(generatedMediaItem.filePath);
            if (generatedMediaItem.durationMs != null) {
                hydratePersistedAudioDurationMs(generatedMediaItem.durationMs);
            }
            if (Array.isArray(generatedMediaItem.waveformBars)) {
                persistedWaveformBarsRef.current = generatedMediaItem.waveformBars;
                setWaveformBars(fitAudioWaveformBarsToBarCount({
                    bars: generatedMediaItem.waveformBars,
                    barCount: visibleWaveformBarCountRef.current,
                }));
            }

            updateAudioNote({
                id,
                title: generatedMediaItem.title || normalizedTitle,
                uploaded: false,
                description: sanitizeAudioNoteDescription(generatedMediaItem.description),
                filePath: generatedMediaItem.filePath,
                isPostContentDictation: generatedMediaItem.isPostContentDictation === true,
                durationMs: generatedMediaItem.durationMs,
                waveformBars: generatedMediaItem.waveformBars,
            });

            setIsNewAudioNote(false);
            setIsEditing(false);
            setIsSavingAudioNote(false);
            broadcastHeightAdjusted();
        } else if (isNewAudioNote && audioBlob && postId) {
            beginPendingNewAudioNoteSave();

            const mediaType = MEDIA_TYPE.audioNote;
            const uniqueId = new ShortUniqueId({ length: 6 })(); // avoid file name collisions
            const mediaBucket = getMediaStorageBucket(mediaType);
            const normalizedTitle = audioTitle === getDefaultAudioTitle()
                ? getDefaultAudioTitle()
                : audioTitle;
            const fileName = normalizedTitle.toLowerCase().split('.')[0].replace(' ', '_');
            let storageEntity: STORAGE_ENTITY;
            if (editorType === EDITOR_CONTEXT_TYPE.post) {
                storageEntity = isTestMode()
                    ? STORAGE_ENTITY.stagingPosts
                    : STORAGE_ENTITY.posts;
            } else {
                storageEntity = isTestMode()
                    ? STORAGE_ENTITY.stagingAnnotations
                    : STORAGE_ENTITY.annotations;
            }
            const placeholderFile = new File(
                [audioBlob],
                normalizedTitle,
                {
                    type: audioBlob.type,
                    lastModified: Date.now(),
                },
            );

            let mediaItem: IMediaItem = {
                id,
                userId: user.id,
                file: placeholderFile,
                title: audioTitle,
                description: normalizedAudioDescription,
                filePath: `${storageEntity}/${mediaBucket}/${id}/${fileName}-${uniqueId}.processing`,
                type: mediaType,
                uploadProgress: 0,
            };
            setUploadingMedia(mediaItem);

            let blobToUpload = audioBlob;
            let mimeTypeToUpload = audioBlob.type;
            // Test-only client flag to force browser transcode even for MP3 uploads.
            const forceBrowserTranscode = window.localStorage.getItem('forceAudioBrowserTranscode') === 'true';
            try {
                const transcodeResult = await transcodeAudioForUpload({
                    blob: audioBlob,
                    mimeType: audioBlob.type,
                    forceTranscode: forceBrowserTranscode,
                    onProgress: (progress: number) => {
                        // Reserve the first 60% of the bar for browser transcode work.
                        const mappedProgress = Math.max(1, Math.min(60, (progress * 60) / 100));
                        updateUploadingMedia({
                            ...mediaItem,
                            uploadProgress: mappedProgress,
                        });
                    },
                });
                blobToUpload = transcodeResult.blob;
                mimeTypeToUpload = transcodeResult.mimeType;
                if (transcodeResult.transcoded) {
                    setMimeType(mimeTypeToUpload);
                }
            } catch (error) {
                // On client transcode failure, preserve old behavior by uploading original blob.
                // eslint-disable-next-line no-console
                console.warn('Audio browser transcode failed. Falling back to original upload.', error);
            }

            // Prepare File
            const file = new File(
                [blobToUpload],
                normalizedTitle,
                {
                    type: mimeTypeToUpload,
                    lastModified: Date.now(),
                },
            );
            const normalizedContentType = (file.type || '').toLowerCase();
            const isMP3ContentType = (
                normalizedContentType === 'audio/mpeg'
                || normalizedContentType === 'audio/mp3'
                || normalizedContentType === 'audio/mpeg3'
                || normalizedContentType === 'audio/x-mp3'
                || normalizedContentType === 'audio/x-mpeg'
                || normalizedContentType === 'audio/x-mpeg3'
            );
            // Prefer .mp3 extension for MPEG audio for consistency in storage paths.
            const fileExtension = isMP3ContentType
                ? 'mp3'
                : mime.getExtension(file.type);
            if (fileExtension == null) {
                setSnackbarData({
                    visible: true,
                    duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                    text: `Unable to determine file extension. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    icon: FileIcon,
                    hasFailure: true,
                });
                removeUploadingMedia(id);
                clearPendingNewAudioNoteSave();
                return;
            }
            const path = `${storageEntity}/${mediaBucket}/${id}/${fileName}-${uniqueId}.${fileExtension}`;
            mediaItem = {
                ...mediaItem,
                file,
                filePath: path,
                uploadProgress: 60,
            };
            updateUploadingMedia(mediaItem);

            // make db entry of media
            // execute before file upload so upload cloud function has a place to write to
            createMedia({
                test: isTestMode(),
                postId,
                mediaId: id,
                type: mediaType,
                storageEntity,
                fileName: `${fileName}-${uniqueId}`,
                fileExtension: fileExtension.toString(),
                title: audioTitle,
                description: normalizedAudioDescription,
                isPostContentDictation: shouldPersistPostContentDictation,
                dictationSourceText: dictationSourceTextForSave,
                durationMs: durationMsForPersistence,
                waveformBars: waveformBarsForPersistence,
            }).then((success) => {
                const cur = saveAudioNoteLatest.current;
                if (success) {
                    completePendingNewAudioNoteSaveStep(AUDIO_NOTE_PENDING_SAVE_STEP.mediaDocSaved);

                    // Turn of isEditing
                    if (cur.isEditing) {
                        setIsEditing(false);
                    }

                    // Set no not new audio note
                    if (cur.isNewAudioNote) {
                        setIsNewAudioNote(false);
                    }
                } else {
                    setSnackbarData({
                        visible: true,
                        duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_AUDIO_NOTE_SAVE_FAILURE,
                        icon: MicrophoneIcon,
                        hasFailure: true,
                    });
                    clearPendingNewAudioNoteSave();
                }
            });

            // Upload file to Cloud Storage; backend workers write normalized playback URL/transcript fields.
            const isPreTranscodedMP3 = (
                normalizedContentType === 'audio/mpeg'
                || normalizedContentType === 'audio/mp3'
                || normalizedContentType === 'audio/mpeg3'
                || normalizedContentType === 'audio/x-mp3'
                || normalizedContentType === 'audio/x-mpeg'
                || normalizedContentType === 'audio/x-mpeg3'
            );
            const preTranscodedMetadata: Record<string, string> = {};
            if (isPreTranscodedMP3) {
                // Marks this object as already MP3 so backend can skip redundant conversion work/cost.
                preTranscodedMetadata.preTranscoded = 'true';
                preTranscodedMetadata.sourceEncoding = 'MP3';
            }
            const uploadTask = uploadToCloudStorage(
                file,
                path,
                user.id,
                preTranscodedMetadata,
            );

            uploadTask.on(
                'state_changed',
                (snapshot: UploadTaskSnapshot) => {
                    // Observe state change events such as progress, pause, and resume
                    // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                    const uploadProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    // Upload phase maps to the remaining 40% so UI shows one continuous bar.
                    const progress = 60 + ((uploadProgress * 40) / 100);
                    setUploadingStateChangeEvent({
                        mediaItem,
                        snapshot,
                        progress,
                    });
                },
                (error: StorageError) => {
                    clearPendingNewAudioNoteSave();
                    removeUploadingMedia(mediaItem.id);
                    setSnackbarData({
                        visible: true,
                        duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                        text: getStorageErrorMessage(error.code as STORAGE_ERROR_CODE),
                        icon: MicrophoneIcon,
                        hasFailure: true,
                    });
                    if (currentSessionId) {
                        recordUserAction({
                            test: isTestMode(),
                            type: USER_ACTION_TYPE.error,
                            sessionId: currentSessionId,
                            payload: {
                                // eslint-disable-next-line max-len
                                error: getStorageErrorMessage(error.code as STORAGE_ERROR_CODE),
                            },
                        });
                    }
                    throw Error(getStorageErrorMessage(error.code as STORAGE_ERROR_CODE));
                },
                () => {
                    setUploadingCompleteEvent({
                        mediaItem,
                        storageRef: uploadTask.snapshot.ref,
                    });
                },
            );
        } else if (filePath) {
            setIsSavingAudioNote(true);
            const existingAudioNoteDictationSettingsChanged = postContentDictationEnabled !== isPostContentDictation;

            // Update DB
            updateMedia({
                test: isTestMode(),
                mediaId: id,
                type: MEDIA_TYPE.audioNote,
                title: audioTitle,
                description: normalizedAudioDescription,
                filePath,
                // Existing title and description edits keep dictation fields out of the payload
                // so metadata-only saves do not request another transcription.
                ...(existingAudioNoteDictationSettingsChanged
                    ? {
                        isPostContentDictation: shouldPersistPostContentDictation,
                        ...(audioNoteMediaItem?.generatedSpeech === true
                            ? {}
                            : { dictationSourceText: dictationSourceTextForSave }
                        ),
                    }
                    : {}
                ),
                durationMs: durationMsForPersistence,
                waveformBars: waveformBarsForPersistence,
            }).then((success) => {
                const cur = saveAudioNoteLatest.current;
                if (!success) {
                    setSnackbarData({
                        visible: true,
                        duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_AUDIO_NOTE_LOCAL_FILE_UPLOAD_FAILURE,
                        icon: MicrophoneIcon,
                        hasFailure: true,
                    });
                    return;
                }
                // Update Existing Audio Note
                updateAudioNote({
                    id: cur.id,
                    title: cur.audioTitle,
                    uploaded: cur.audioUploaded,
                    description: cur.audioDescription,
                    filePath: cur.filePath!,
                    isPostContentDictation: cur.isPostContentDictation,
                    durationMs: durationMsForPersistence,
                    waveformBars: waveformBarsForPersistence || cur.waveformBars,
                });

                // Remove saving tooltip
                if (cur.isSavingAudioNote) {
                    setIsSavingAudioNote(false);
                }

                // Turn of isEditing
                if (cur.isEditing) {
                    setIsEditing(false);
                }

                broadcastHeightAdjusted();
            });
        } else {
            // We should never really hit this
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem saving audio note. Audio note is neither new or has a prior URL. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`There was a problem saving audio note. Audio note is neither new or has a prior URL. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
    };

    const saveAudioNote = (e?: React.MouseEvent | React.TouchEvent): void => {
        saveAudioNoteAsync(e).catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error);
            clearPendingNewAudioNoteSave();
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_AUDIO_NOTE_SAVE_FAILURE,
                icon: MicrophoneIcon,
                hasFailure: true,
            });
        });
    };

    const discardAudioNote = (): void => {
        if (
            audioNodeRef.current
            && audioNodeRef.current.duration > 0
            && !audioNodeRef.current.paused
        ) {
            // Pause music if playing when discard
            audioNodeRef.current.pause();
        }
        // Make sure to revoke the data uris to avoid memory leaks
        // Set when we use temporary URL from blob
        if (lastBlobUrlRef.current) {
            setLocalPreviewUrl(null);
        }
        if (audioNoteMediaItem) {
            audioNoteMediaItemRef.current = null;
            setAudioNoteMediaItem(null);
        }
        clearAudioElementSource();
        setIsNewAudioNote(true);
        setStatus(AUDIO_NOTE_RECORDING_STATE.idle);
        recordedAudioChunksRef.current = [];
        recordedWaveformLevelSamplesRef.current = [];
        recordedWaveformDisplayBarsRef.current = [];
        setHasStartedPlayback(false);
        persistedDurationMsRef.current = null;
        setAudioLength(0);
        setAudioBlob(null);
        setAudioURL(null);
        setTimeElapsed(0);
        setSavedTime(null);
        setStartTime(null);
        setPotentialElapsed(null);
        setIsUpdatingTime(false);
        setIsEditing(true);
        setAudioTitle('');
        setAudioDescription('');
        setAudioUploaded(false);
        setPostContentSpeechGenerationRequested(false);
        persistedWaveformBarsRef.current = null;
        waveformSourceKeyRef.current = null;
        setWaveformBars(buildDeterministicVisibleWaveformBars(fallbackWaveformSeed));
        releaseRecordingWaveformResources({ stopStream: true }).catch(() => undefined);
        setMimeType('');
        setRemoteFilePath(null);
        setIsPreparingRecordedPreview(false);
        setPrevIsRecordingAudioNote(null);

        broadcastHeightAdjusted();
    };

    const handleOpenAudioRemovalModal = useCallback((
        mode: AUDIO_NOTE_REMOVAL_MODAL_MODE,
    ): void => {
        setAudioRemovalModalMode(mode);
    }, []);

    const handleCloseAudioRemovalModal = useCallback((): void => {
        setAudioRemovalModalMode(null);
    }, []);

    const handleConfirmAudioRemoval = useCallback((): void => {
        if (audioRemovalModalMode === AUDIO_NOTE_REMOVAL_MODAL_MODE.redo) {
            discardAudioNote();
            startRecord();
        } else if (audioRemovalModalMode === AUDIO_NOTE_REMOVAL_MODAL_MODE.discard) {
            discardAudioNote();
        }
        setAudioRemovalModalMode(null);
    }, [
        audioRemovalModalMode,
        discardAudioNote,
        startRecord,
    ]);

    const importAudioNote = (): void => {
        openFileSelector();
    };

    /**
     * This only prepares the editor state. The actual TTS/alignment worker is
     * queued from `saveAudioNoteAsync` after the audio note is saved.
     */
    const requestPostContentSpeechGeneration = useCallback((e: React.MouseEvent | React.TouchEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopPropagation();

        if (normalizedPostContentTranscriptText.length === 0) {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: 'Post text is required before speech can be generated.',
                icon: SpeakerIcon,
                hasFailure: true,
            });
            return;
        }

        if (lastBlobUrlRef.current) {
            setLocalPreviewUrl(null);
        }
        clearAudioElementSource();
        setPostContentDictationEnabled(false);
        setPostContentSpeechGenerationRequested(true);
        setIsNewAudioNote(true);
        setIsEditing(true);
        setStatus(AUDIO_NOTE_RECORDING_STATE.pausedComplete);
        setErrorMessage(null);
        setAudioBlob(null);
        setAudioURL(null);
        setRemoteFilePath(null);
        setAudioUploaded(false);
        setMimeType('audio/mpeg');
        setHasStartedPlayback(false);
        setIsPreparingRecordedPreview(false);
        setTimeElapsed(0);
        setSavedTime(null);
        setStartTime(null);
        setPotentialElapsed(null);
        setIsUpdatingTime(false);
        persistedDurationMsRef.current = null;
        persistedWaveformBarsRef.current = null;
        waveformSourceKeyRef.current = null;
        setWaveformBars(buildDeterministicVisibleWaveformBars(`${fallbackWaveformSeed}:generated-speech`));
        if (audioTitle.trim().length === 0) {
            setAudioTitle('Generated Post Dictation');
        }
        broadcastHeightAdjusted();
    }, [
        audioTitle,
        broadcastHeightAdjusted,
        buildDeterministicVisibleWaveformBars,
        clearAudioElementSource,
        fallbackWaveformSeed,
        normalizedPostContentTranscriptText,
        setSnackbarData,
        setLocalPreviewUrl,
    ]);

    const computeTimeElapsed = (): number => {
        // Collect and save elapsed time
        // We are likely recording audio, so need to time it the elapsed time
        let currentTime = Date.now();
        if (
            status === AUDIO_NOTE_RECORDING_STATE.playing
            && audioLength === 0
            && startTime
        ) {
            // We havent loaded track data yet
            currentTime = startTime;
        } else if (
            (
                status === AUDIO_NOTE_RECORDING_STATE.playing
                || status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
            )
            && audioLength > 0
            && audioNodeRef.current
            && startTime
        ) {
            // We have track data, so compute normally
            currentTime = (audioNodeRef.current.currentTime * MILLISECONDS_IN_A_SECOND) + startTime;
        }

        let difference;
        if (
            status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
            && startTime
        ) {
            difference = currentTime - startTime;
        } else if (savedTime && startTime) {
            // If we've paused, factor in previous time
            difference = (currentTime - startTime) + savedTime;
        } else {
            // Ternary makes sure that we don't begin with a large
            // timestamp
            difference = startTime ? currentTime - startTime : 0;
        }
        return difference;
    };

    const formatTimestamp = useCallback((timestamp: number): string => formatAudioTimestamp({
        timestampMs: timestamp,
        playbackSpeed,
        padHours: true,
    }), [playbackSpeed]);

    /**
     * Progress-bar interactions must be measured against the full container,
     * not whichever child happened to receive the event, so clicks on the
     * filled scrubber still seek to the expected absolute timestamp.
     */
    const resolveProgressBarInteractionMetrics = useCallback((e: React.MouseEvent): {
        elapsed: number,
        percent: number,
    } | null => {
        const progressBarRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const totalLengthTarget = progressBarRect.right - progressBarRect.left;

        if (totalLengthTarget <= 0 || audioLength <= 0) {
            return null;
        }

        const lengthLeftSideTarget = Math.min(
            Math.max(e.clientX - progressBarRect.left, 0),
            totalLengthTarget,
        );
        const percent = lengthLeftSideTarget / totalLengthTarget;

        return {
            elapsed: percent * audioLength,
            percent,
        };
    }, [audioLength]);

    const moveScrubber = (e: React.MouseEvent): void => {
        const progressBarInteractionMetrics = resolveProgressBarInteractionMetrics(e);
        if (!progressBarInteractionMetrics) {
            return;
        }

        const {
            elapsed,
        } = progressBarInteractionMetrics;

        seekToTimestampMs(elapsed);

        if (
            readOnly
            && postId
            && currentSessionId
        ) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.scrubAudioNoteProgressBar,
                sessionId: currentSessionId,
                payload: {
                    id,
                    editorType,
                    postId,
                },
            });
        }
    };

    const getIdleMessage = (): string => {
        if (errorMessage) {
            return errorMessage;
        }

        if (!readOnly) {
            return 'Record/Upload Audio';
        }

        return 'No audio available.';
    };

    const handleProgressMouseEnter = (e: React.MouseEvent): void => {
        const progressBarInteractionMetrics = resolveProgressBarInteractionMetrics(e);
        if (!progressBarInteractionMetrics) {
            return;
        }

        setPotentialElapsed(progressBarInteractionMetrics.elapsed);
    };

    const handleProgressMouseLeave = (): void => {
        clearDelayContractProgressIndicator();
        delayContractProgressIndicator();
    };

    const {
        start: delayContractProgressIndicator,
        clear: clearDelayContractProgressIndicator,
    } = useTimeout(() => {
        setPotentialElapsed(null);
    }, PROGRESS_INDICATOR_DELAY_DURATION);

    const handleTitleChange = (): void => {
        if (titleInputRef.current) setAudioTitle(titleInputRef.current.value);
    };

    const onTitleInputMouseEnter = (e: React.MouseEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.input,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onTitleInputMouseLeave = (e: React.MouseEvent): void => {
        onCursorLeave(e);
    };

    const onTitleInputFocus = (): void => {
        setInputFocused(true);

        // Play Sound
        const clickClip = getInputClickClip();
        if (clickClip) {
            clickClip.pause();
            clickClip.currentTime = 0;
            playAudio(clickClip);
        }
    };

    const onTitleInputBlur = (): void => {
        setInputFocused(false);
    };

    const onDescriptionTextareaMouseEnter = (e: React.MouseEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.input,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onDescriptionTextareaMouseLeave = (e: React.MouseEvent): void => {
        onCursorLeave(e);
    };

    const onDescriptionTextareaFocus = (): void => {
        setInputFocused(true);
        setDescriptionInputFocused(true);

        // Play Sound
        const clickClip = getInputClickClip();
        if (clickClip) {
            clickClip.pause();
            clickClip.currentTime = 0;
            playAudio(clickClip);
        }
    };

    const onDescriptionTextareaBlur = (): void => {
        setInputFocused(false);
        setDescriptionInputFocused(false);

        if (!descriptionInputRef.current) {
            return;
        }

        const nextAudioDescription = sanitizeAudioNoteDescription(descriptionInputRef.current.innerText);
        if (descriptionInputRef.current.innerText !== nextAudioDescription) {
            descriptionInputRef.current.innerText = nextAudioDescription;
        }
    };

    /**
     * Stop the parent Slate surface from claiming the first press before the
     * portal-backed description editor can place its own caret.
     */
    const handleDescriptionInputPress = useCallback((
        e: React.MouseEvent | React.TouchEvent,
    ): void => {
        if (document.activeElement !== descriptionInputRef.current) {
            e.preventDefault();
            if (descriptionInputRef.current) {
                descriptionInputRef.current.focus();
                moveCursorToEnd(descriptionInputRef.current);
            }
        }
        e.stopPropagation();
    }, []);

    const onButtonMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.editorElementButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onButtonMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onContainerMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.editorElement,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onContainerMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onTranscriptWordMouseEnter = useCallback((e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.audioNoteTranscriptWord,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    }, [onCursorEnter]);

    const onTranscriptWordMouseLeave = useCallback((e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    }, [onCursorLeave]);

    const onProgressBarMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.editorElement,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onProgressBarMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    // ✅ CHANGED: handleDownload that works for BOTH:
    // 1) local audioBlob (recorded/imported)  -> download from blob
    // 2) readOnly streaming (signed Firebase URL) -> download/open via signed URL
    const handleDownload = async (e: React.MouseEvent | React.TouchEvent): Promise<void> => {
        e.nativeEvent.stopPropagation();

        // Helper to build a safe filename
        const getFileName = (ext: string): string => {
            const base = (audioTitle && audioTitle.trim().length > 0)
                ? audioTitle.trim()
                : 'audio-note';
            return `${base}.${ext}`;
        };

        // Case A: we have a local blob (recording/import) -> same behavior as before
        if (audioBlob) {
            const fileExtension = mime.getExtension(mimeType) ?? 'mp3';
            const link = document.createElement('a');
            const tempURL = URL.createObjectURL(audioBlob);

            link.href = tempURL;
            link.download = getFileName(fileExtension);
            link.target = '_blank';
            link.rel = 'noopener noreferrer';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(tempURL);
            return;
        }

        // Case B: streaming / readOnly -> use signed URL (no blob download in JS)
        // Ensure we have a signed URL (resolveStreamingURL is the helper from the streaming patch)
        let urlToDownload = audioURL;

        if (!isHttpAudioUrl(urlToDownload)) {
            // audioURL is null, blob:, OR a Firebase storage path
            urlToDownload = await resolveStreamingURL();
        }

        if (!urlToDownload) {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: AUDIO_NOTE_FETCH_FILE_ERROR,
                icon: FileIcon,
                hasFailure: true,
            });
            return;
        }

        // Use mp3 extension for download naming consistency.
        const inferredExt = 'mp3';

        // ✅ NEW: attempt download via <a download>.
        // Some browsers may ignore download for cross-origin URLs; if so, it will just open.
        const link = document.createElement('a');
        link.href = urlToDownload;
        link.download = getFileName(inferredExt);
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    /**
     * Inline controls and the floating reader mini-player share the same
     * playback-speed path so the visible speed indicator never drifts from the
     * actual audio element.
     */
    const applyPlaybackSpeed = useCallback((speed: number): void => {
        if (speed === playbackSpeed) {
            return;
        }

        setPlaybackSpeed(speed);
        if (audioNodeRef.current) {
            audioNodeRef.current.playbackRate = speed;
        }

        if (
            readOnly
            && postId
            && currentSessionId
        ) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.adjustAudioNotePlaybackSpeed,
                sessionId: currentSessionId,
                payload: {
                    id,
                    speed,
                    editorType,
                    postId,
                },
            });
        }
    }, [
        currentSessionId,
        editorType,
        id,
        playbackSpeed,
        postId,
        readOnly,
    ]);

    const handlePlaybackSpeed = useCallback((
        speed: number,
        e: React.MouseEvent | React.TouchEvent,
    ): void => {
        e.nativeEvent.stopPropagation();
        applyPlaybackSpeed(speed);
    }, [applyPlaybackSpeed]);

    /**
     * Keep the inline audio-note speed control aligned with ReaderView's
     * floating mini-player so playback-speed changes feel identical everywhere.
     */
    const handleCyclePlaybackSpeed = useCallback((
        e: React.MouseEvent | React.TouchEvent,
    ): void => {
        handlePlaybackSpeed(
            getNextAudioPlaybackSpeed(playbackSpeed),
            e,
        );
    }, [
        handlePlaybackSpeed,
        playbackSpeed,
    ]);

    const disableIfAudioNotSupported = (): void => {
        supportsAudioType(mimeType).then((audioIsSupported) => {
            if (!audioIsSupported) {
                setStatus(AUDIO_NOTE_RECORDING_STATE.idle);
                setErrorMessage(AUDIO_NOTE_UNSUPPORTED_FILE_TYPE(mime.getExtension(mimeType)));
            }
        });
    };

    const supportsAudioType = async (type: string): Promise<boolean> => {
        // "probably" : The media type appears to be playable
        // "maybe": Cannot tell if the media type is playable without playing it
        // "": The media type is not playable
        let audio;

        if (!audio) {
            audio = document.createElement('audio');
        }
        let canPlay = audio.canPlayType(type) === 'probably' || audio.canPlayType(type) === 'maybe';
        if (!canPlay && audioURL) {
            canPlay = await canActuallyPlay(audioURL);
            return canPlay;
        }

        return canPlay;
    };

    const canActuallyPlay = async (url: string, timeout = 5000): Promise<boolean> => new Promise((resolve) => {
        const a = new Audio();
        let timer: NodeJS.Timeout | undefined;

        const cleanup = (): void => {
            clearTimeout(timer);
            a.src = '';
        };

        // If we can load metadata or reach a playable state, call it good
        const ok = (): void => { cleanup(); resolve(true); };
        const bad = (): void => { cleanup(); resolve(false); };

        a.preload = 'metadata';
        a.addEventListener('loadedmetadata', ok, { once: true });
        a.addEventListener('canplay', ok, { once: true });
        a.addEventListener('error', bad, { once: true });

        // Give up after a bit (network/CORS/unsupported)
        timer = setTimeout(bad, timeout);

        a.src = url;   // Must be CORS-allowed if cross-origin
        a.load();
    });

    const handleModifyDescriptionInputWidth = (): void => {
        if (descriptionNodeRef.current && isEditing) {
            const rect = descriptionNodeRef.current.getBoundingClientRect();
            if (parentRef) {
                setDescriptionPos({
                    top: rect.top - parentRef.getBoundingClientRect().top,
                    left: rect.left - parentRef.getBoundingClientRect().left,
                });
            }
            setDescriptionWidth(descriptionNodeRef.current.clientWidth);
        }
    };

    const handleModifyTitleInputWidth = (): void => {
        if (titleNodeRef.current && isEditing) {
            const rect = titleNodeRef.current.getBoundingClientRect();
            if (parentRef) {
                setTitlePos({
                    x: rect.left - parentRef.getBoundingClientRect().left,
                    y: rect.top - parentRef.getBoundingClientRect().top,
                });
            }
            setTitleWidth(titleNodeRef.current.clientWidth);
        }
    };

    const handleViewportResize = (): void => {
        // Modify width of Title Input
        handleModifyTitleInputWidth();
        // Modify width of Description Input
        handleModifyDescriptionInputWidth();
    };

    const handleUploadingMediaCompleteLatest = useLatest({
        audioURL,
        id,
        audioTitle,
        audioUploaded,
        audioDescription,
        isPostContentDictation: postContentDictationEnabled,
        waveformBars: preferredStoredWaveformBars,
    });
    const handleUploadingMediaComplete = (
        mediaItem: IMediaItem,
    ): void => {
        const cur = handleUploadingMediaCompleteLatest.current;
        removeUploadingMedia(mediaItem.id);
        completePendingNewAudioNoteSaveStep(AUDIO_NOTE_PENDING_SAVE_STEP.uploadComplete);

        if (cur.audioURL?.startsWith('blob:')) {
            setLocalPreviewUrl(null);
        }
        setAudioBlob(null);
        setRemoteFilePath(mediaItem.filePath);

        if (audioNodeRef.current?.parentElement) {
            // Focus Audio Note so that it applies
            // update to it
            audioNodeRef.current.parentElement.click();
        }

        // Update Audio Note
        updateAudioNote({
            id: cur.id,
            title: cur.audioTitle,
            uploaded: cur.audioUploaded,
            description: cur.audioDescription,
            filePath: mediaItem.filePath,
            isPostContentDictation: cur.isPostContentDictation,
            durationMs: persistedDurationMsRef.current || undefined,
            waveformBars: persistedWaveformBarsRef.current || cur.waveformBars,
        });
    };

    const handleUploadingMediaStateChange = (
        mediaItem: IMediaItem,
        _snapshot: UploadTaskSnapshot,
        progress: number,
    ): void => {
        if (uploadingMedia.has(mediaItem.id)) {
            updateUploadingMedia({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                ...uploadingMedia.get(mediaItem.id)!,
                uploadProgress: progress,
            });
        } else {
            setUploadingMedia(mediaItem);
        }
    };

    const toggleTranscript = (e: React.MouseEvent | React.TouchEvent): void => {
        e.nativeEvent.stopPropagation();
        const nextShowTranscript = !showTranscriptRef.current;
        showTranscriptRef.current = nextShowTranscript;
        setShowTranscript(nextShowTranscript);

        // Removes Cursor Lock caused by Small Cursor on Buttons
        onCursorLeave();

        if (readOnly
            && postId
            && currentSessionId
        ) {
            // Show Transcript User Action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.toggleAudioNoteTranscript,
                sessionId: currentSessionId,
                payload: {
                    id,
                    visible: !showTranscript,
                    editorType,
                    postId,
                },
            });
        }

        broadcastHeightAdjusted();
    };

    /**
     * Keep the post-dictation toggle in the action rail so it reads like an
     * audio-note setting instead of extra body content under the note details.
     */
    const togglePostContentDictation = useCallback((e: React.MouseEvent | React.TouchEvent): void => {
        e.preventDefault();
        e.nativeEvent.stopPropagation();
        hasLocallyToggledPostContentDictationRef.current = true;
        setPostContentDictationEnabled((currentEnabled) => !currentEnabled);
    }, []);

    /**
     * All read-only seeking routes use the same timestamp jump helper so they
     * preserve play/pause state and analytics consistently.
     */
    const seekToTimestampMs = useCallback((timestampMs: number): void => {
        let timestamp = timestampMs;

        if (audioLength && timestamp > audioLength) timestamp = audioLength;
        if (timestamp < 0) timestamp = 0;

        setTimeElapsed(timestamp);

        if (audioNodeRef.current) {
            audioNodeRef.current.currentTime = timestamp / MILLISECONDS_IN_A_SECOND; // must be in seconds
        }

        if (
            audioNodeRef.current
            && status === AUDIO_NOTE_RECORDING_STATE.playing
        ) {
            // if was playing, continue playing
            audioNodeRef.current.play();
        }
    }, [
        audioLength,
        status,
    ]);

    /**
     * Skip controls should use the live audio element timestamp when it exists,
     * but still work after paused playback has released the source.
     */
    const getCurrentPlaybackTimestampMs = useCallback((): number => {
        if (!audioNodeRef.current) {
            return timeElapsed;
        }

        const liveTimestampMs = audioNodeRef.current.currentTime * MILLISECONDS_IN_A_SECOND;
        if (Number.isNaN(liveTimestampMs) || liveTimestampMs < 0) {
            return timeElapsed;
        }

        return liveTimestampMs;
    }, [timeElapsed]);

    const handleSkip = useCallback((direction: AUDIO_NOTE_SKIP_DIRECTION): void => {
        const fifteenSeconds = 15 * MILLISECONDS_IN_A_SECOND;
        const currentTimestampMs = getCurrentPlaybackTimestampMs();
        const nextTimestampMs = direction === AUDIO_NOTE_SKIP_DIRECTION.forward
            ? Math.min(currentTimestampMs + fifteenSeconds, audioLength || currentTimestampMs + fifteenSeconds)
            : Math.max(currentTimestampMs - fifteenSeconds, 0);

        seekToTimestampMs(nextTimestampMs);

        if (
            readOnly
            && postId
            && currentSessionId
            && direction === AUDIO_NOTE_SKIP_DIRECTION.forward
        ) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.skipForwardAudioNote,
                sessionId: currentSessionId,
                payload: {
                    id,
                    editorType,
                    postId,
                },
            });
            return;
        }

        if (
            readOnly
            && postId
            && currentSessionId
            && direction === AUDIO_NOTE_SKIP_DIRECTION.backward
        ) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.skipBackwardAudioNote,
                sessionId: currentSessionId,
                payload: {
                    id,
                    editorType,
                    postId,
                },
            });
        }
    }, [
        audioLength,
        currentSessionId,
        editorType,
        getCurrentPlaybackTimestampMs,
        id,
        postId,
        readOnly,
        seekToTimestampMs,
    ]);

    const skipTranscriptWord = useCallback((word: ITranscriptionWord): void => {
        seekToTimestampMs(getTranscriptionWordTimestampMs({
            word,
            edge: 'start',
        }));

        if (
            readOnly
            && postId
            && currentSessionId
        ) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.scrubAudioNoteTranscriptWord,
                sessionId: currentSessionId,
                payload: {
                    id,
                    word,
                    editorType,
                    postId,
                },
            });
        }
    }, [
        currentSessionId,
        editorType,
        id,
        postId,
        readOnly,
        seekToTimestampMs,
    ]);
    const setReadOnlyPlaybackSpeed = useCallback((speed: number): void => {
        applyPlaybackSpeed(speed);
    }, [applyPlaybackSpeed]);
    const seekReadOnlyPlaybackToTime = useCallback((timestampMs: number): void => {
        seekToTimestampMs(timestampMs);
    }, [seekToTimestampMs]);

    // ===== Side Effects =====

    /**
     * Track mount state and release any lazily created sound clips on unmount.
     */
    useEffect(() => {
        isMountedRef.current = true;

        return function cleanup() {
            isMountedRef.current = false;
            fetchMediaItemRequestIdsRef.current.includeTranscript += 1;
            fetchMediaItemRequestIdsRef.current.playbackOnly += 1;
            fetchMediaItemPromisesRef.current.includeTranscript = null;
            fetchMediaItemPromisesRef.current.playbackOnly = null;
            cancelInactivePlaybackSourceRelease();
            if (inputClickClip.current) {
                inputClickClip.current.pause();
                inputClickClip.current.remove();
                inputClickClip.current = null;
            }
        };
    }, [cancelInactivePlaybackSourceRelease]);

    /**
     * Fetch associated editor audio note item from database
     */
    useEffect(() => {
        if (
            !isNewAudioNote
            && !audioNoteMediaItem
            && id != null
        ) {
            fetchMediaItem({
                includeTranscript: !shouldFetchPlaybackOnlyMediaItemFirst,
            }).then(() => {
                if (!shouldHydrateTranscriptAfterPlaybackOnlyFetch) {
                    return;
                }

                fetchMediaItem({
                    includeTranscript: true,
                }).catch(() => undefined);
            }).catch(() => undefined);
        }
    }, [
        isNewAudioNote,
        audioNoteMediaItem,
        authResolvedUserId,
        fetchMediaItem,
        id,
        shouldFetchPlaybackOnlyMediaItemFirst,
        shouldHydrateTranscriptAfterPlaybackOnlyFetch,
    ]);

    /**
     * Keep the saved-note duration available before playback starts, but let
     * preview-backed read-only notes fail closed until the authoritative
     * preview media item supplies a trusted duration.
     */
    useEffect(() => {
        hydratePersistedAudioDurationMs(persistedHydrationDurationMs);
    }, [
        hydratePersistedAudioDurationMs,
        persistedHydrationDurationMs,
    ]);

    /**
     * Read-only notes report whether they remain in view so the reader can
     * surface a compact floating player once the inline note scrolls away.
     */
    useEffect(() => {
        const queueVisibilityMeasurement = (): void => {
            if (typeof window === 'undefined') {
                return;
            }

            if (readOnlyVisibilityMeasurementFrameRef.current != null) {
                window.cancelAnimationFrame(readOnlyVisibilityMeasurementFrameRef.current);
            }

            readOnlyVisibilityMeasurementFrameRef.current = window.requestAnimationFrame(() => {
                readOnlyVisibilityMeasurementFrameRef.current = null;

                if (!containerRef.current) {
                    return;
                }

                const noteRect = containerRef.current.getBoundingClientRect();
                const viewportRect = viewportRootRef?.current?.getBoundingClientRect();
                const nextIsVisibleInViewport = resolveReadOnlyAudioNoteVisibilityFromRects({
                    noteTop: noteRect.top,
                    noteBottom: noteRect.bottom,
                    noteHeight: noteRect.height,
                    rootTop: viewportRect?.top ?? 0,
                    rootBottom: viewportRect?.bottom ?? window.innerHeight,
                });

                setIsVisibleInViewport((currentIsVisible) => (
                    currentIsVisible === nextIsVisibleInViewport
                        ? currentIsVisible
                        : nextIsVisibleInViewport
                ));
            });
        };

        if (!readOnly || !containerRef.current) {
            setIsVisibleInViewport(true);
            return undefined;
        }

        queueVisibilityMeasurement();

        if (typeof IntersectionObserver === 'undefined') {
            const scrollTarget = viewportRootRef?.current;
            const measurementTarget: Window | HTMLDivElement = scrollTarget || window;

            measurementTarget.addEventListener('scroll', queueVisibilityMeasurement, {
                passive: true,
            });
            window.addEventListener('resize', queueVisibilityMeasurement, {
                passive: true,
            });

            return () => {
                if (readOnlyVisibilityMeasurementFrameRef.current != null) {
                    window.cancelAnimationFrame(readOnlyVisibilityMeasurementFrameRef.current);
                    readOnlyVisibilityMeasurementFrameRef.current = null;
                }

                measurementTarget.removeEventListener('scroll', queueVisibilityMeasurement);
                window.removeEventListener('resize', queueVisibilityMeasurement);
            };
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (!entry) {
                    return;
                }

                setIsVisibleInViewport(resolveReadOnlyAudioNoteVisibility(entry));
            },
            {
                root: viewportRootRef?.current || null,
                threshold: [0, 0.25, 0.5, 0.75, 1],
            },
        );
        const scrollTarget = viewportRootRef?.current;
        const measurementTarget: Window | HTMLDivElement = scrollTarget || window;

        observer.observe(containerRef.current);
        measurementTarget.addEventListener('scroll', queueVisibilityMeasurement, {
            passive: true,
        });
        window.addEventListener('resize', queueVisibilityMeasurement, {
            passive: true,
        });
        return () => {
            if (readOnlyVisibilityMeasurementFrameRef.current != null) {
                window.cancelAnimationFrame(readOnlyVisibilityMeasurementFrameRef.current);
                readOnlyVisibilityMeasurementFrameRef.current = null;
            }

            observer.disconnect();
            measurementTarget.removeEventListener('scroll', queueVisibilityMeasurement);
            window.removeEventListener('resize', queueVisibilityMeasurement);
        };
    }, [
        readOnly,
        viewportRootRef,
        viewportRootRef?.current,
    ]);

    // Update Audio Details
    useEffect(() => {
        const incomingAudioTitle = audioNoteDetailsSource.title || '';
        const incomingAudioDescription = normalizedIncomingAudioDescription;
        const previousAudioNoteDetailsSource = lastAudioNoteDetailsSourceRef.current;
        const titleSourceChanged = previousAudioNoteDetailsSource.title !== incomingAudioTitle;
        const descriptionSourceChanged = previousAudioNoteDetailsSource.description !== incomingAudioDescription;

        if (
            titleSourceChanged
            && (
                !isEditing
                || audioTitle === previousAudioNoteDetailsSource.title
            )
        ) {
            setAudioTitle(incomingAudioTitle);
        }

        if (
            descriptionSourceChanged
            && (
                !isEditing
                || audioDescription === previousAudioNoteDetailsSource.description
            )
        ) {
            setAudioDescription(incomingAudioDescription);
        }

        lastAudioNoteDetailsSourceRef.current = {
            title: incomingAudioTitle,
            description: incomingAudioDescription,
        };

        if (uploaded !== audioUploaded) {
            setAudioUploaded(uploaded);
        }

        if (
            !hasLocallyToggledPostContentDictationRef.current
            && incomingPostContentDictationEnabled !== postContentDictationEnabled
        ) {
            setPostContentDictationEnabled(incomingPostContentDictationEnabled);
        }
    }, [
        audioDescription,
        audioNoteDetailsSource.description,
        audioNoteDetailsSource.title,
        audioTitle,
        audioUploaded,
        incomingPostContentDictationEnabled,
        isEditing,
        uploaded,
        normalizedIncomingAudioDescription,
        postContentDictationEnabled,
    ]);

    /**
     * The visible description editor is a portal-backed contentEditable node,
     * so keep its DOM text aligned when remote media details hydrate.
     */
    useEffect(() => {
        if (
            !descriptionInputRef.current
            || document.activeElement === descriptionInputRef.current
        ) {
            return;
        }

        if (descriptionInputRef.current.innerText !== audioDescription) {
            descriptionInputRef.current.innerText = audioDescription;
        }
    }, [audioDescription]);

    /**
     * Editable post notes should trust an already-saved media doc over a legacy
     * node missing the dictation flag. This prevents routine note edits from
     * downgrading true dictation media back to false.
     */
    useEffect(() => {
        if (
            readOnly
            || editorType !== EDITOR_CONTEXT_TYPE.post
            || postContentDictationEnabled
            || audioNoteMediaItem?.isPostContentDictation !== true
            || hasLocallyToggledPostContentDictationRef.current
            || hasRepairedPostContentDictationFromMediaRef.current
        ) {
            return;
        }

        hasRepairedPostContentDictationFromMediaRef.current = true;
        setPostContentDictationEnabled(true);
        updateAudioNote({
            id,
            title: audioTitle,
            uploaded: audioUploaded,
            description: audioDescription,
            filePath: filePath || '',
            isPostContentDictation: true,
        });
    }, [
        audioDescription,
        audioNoteMediaItem?.isPostContentDictation,
        audioTitle,
        audioUploaded,
        editorType,
        filePath,
        id,
        postContentDictationEnabled,
        readOnly,
        updateAudioNote,
    ]);

    // Listen for saved audio note
    const latest = useLatest({
        isNewAudioNote,
        id,
        hasUser: !!user,
        lastMediaPingVersion,
        setLastMediaPingVersion,
        fetchMediaItem,
        // these two aren't used for subscribing, but include them
        // so the callback always "sees" the freshest values if needed
        audioNoteMediaItem,
        showTranscript,
    });
    const unsubscribeRef = React.useRef<Unsubscribe | null>(null);
    const mediaDocUnsubscribeRef = React.useRef<Unsubscribe | null>(null);
    // (Re)subscribe when the *conditions* change; do manual teardown inside.
    useEffect(() => {
        const cur = latest.current;

        // If we shouldn't be listening now, tear down and bail.
        if (cur.isNewAudioNote || !cur.hasUser) {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
            return;
        }

        // If already subscribed, reset before creating a new one.
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }

        const db = getFirestore();
        const mediaCollection = isTestMode()
            ? FIRESTORE_COLLECTION.stagingMedia
            : FIRESTORE_COLLECTION.media;

        const pingRef = doc(db, `${mediaCollection}/${cur.id}/ping/current`);

        unsubscribeRef.current = onSnapshot(
            pingRef,
            (pingSnapshot) => {
                const snapshotCur = latest.current;
                if (!pingSnapshot.exists()) {
                    // no media item update yet
                    return;
                }

                // media but no ping item yet (will be made when media is next updated)
                const { version } = pingSnapshot.data() as { version: number };

                // ignore stale/no-op
                if (
                    snapshotCur.lastMediaPingVersion != null
                    && version <= snapshotCur.lastMediaPingVersion
                ) return;

                snapshotCur.setLastMediaPingVersion(version);
                snapshotCur.fetchMediaItem({
                    includeTranscript: false,
                    bypassCachedPlaybackSource: true,
                }).catch(() => undefined);
            },
            (error) => {
                console.warn('Audio note media ping subscription failed.', error);
            },
        );
    }, [
        // Only things that change the *subscription* go here.
        // (The callback reads other values from `latest.current`.)
        isNewAudioNote,
        id,
        user?.id,
        user?.authenticationType,
    ]);
    const mediaDocLatest = useLatest({
        isNewAudioNote,
        id,
        hasUser: !!user,
        fetchMediaItem,
    });
    /**
     * Keep transcription status in sync from the media doc itself.
     *
     * Read-only annotation/comment audio notes can finish transcription after
     * the annotation has already rendered. Merge the lightweight metadata live,
     * then let the transcript-hydration effect fetch the larger transcript blob.
     *
     * The ping-driven callable refresh remains the source for hydrated
     * transcript/playback metadata, but this live merge path avoids requiring a
     * full page reload before readers see the transcript button.
     */
    useEffect(() => {
        const cur = mediaDocLatest.current;

        if (cur.isNewAudioNote || !cur.hasUser) {
            if (mediaDocUnsubscribeRef.current) {
                mediaDocUnsubscribeRef.current();
                mediaDocUnsubscribeRef.current = null;
            }
            return;
        }

        if (mediaDocUnsubscribeRef.current) {
            mediaDocUnsubscribeRef.current();
            mediaDocUnsubscribeRef.current = null;
        }

        const db = getFirestore();
        const mediaCollection = isTestMode()
            ? FIRESTORE_COLLECTION.stagingMedia
            : FIRESTORE_COLLECTION.media;
        const mediaRef = doc(db, `${mediaCollection}/${cur.id}`);

        mediaDocUnsubscribeRef.current = onSnapshot(
            mediaRef,
            (mediaSnapshot) => {
                if (!mediaSnapshot.exists()) {
                    return;
                }

                const latestMediaItem = audioNoteMediaItemRef.current;
                if (!latestMediaItem) {
                    mediaDocLatest.current.fetchMediaItem({
                        includeTranscript: false,
                    }).catch(() => undefined);
                    return;
                }

                const liveMediaItem = mediaSnapshot.data() as IEditorAudioNoteItem;
                const mergedTranscriptionJob = resolveMostRecentAudioNoteTranscriptionJob({
                    current: latestMediaItem.transcriptionJob,
                    incoming: liveMediaItem.transcriptionJob,
                });
                const nextAudioNoteMediaItem: IEditorAudioNoteItem = {
                    ...latestMediaItem,
                    ...liveMediaItem,
                    ...(mergedTranscriptionJob !== undefined
                        ? { transcriptionJob: mergedTranscriptionJob }
                        : {}),
                };
                audioNoteMediaItemRef.current = nextAudioNoteMediaItem;
                setAudioNoteMediaItem(nextAudioNoteMediaItem);

                const liveTranscriptStoragePath = String(
                    liveMediaItem.transcriptStoragePath || '',
                ).trim();
                const shouldHydrateLiveTranscript = (
                    liveTranscriptStoragePath.length > 0
                    && nextAudioNoteMediaItem.transcript == null
                    && lastRequestedTranscriptStorageHydrationRef.current !== liveTranscriptStoragePath
                );
                if (shouldHydrateLiveTranscript) {
                    lastRequestedTranscriptStorageHydrationRef.current = liveTranscriptStoragePath;
                    mediaDocLatest.current.fetchMediaItem({
                        includeTranscript: true,
                    }).catch(() => undefined);
                }
            },
            (error) => {
                console.warn(
                    'Audio note media document subscription failed. Falling back to ping refreshes.',
                    error,
                );
            },
        );
    }, [
        fetchMediaItem,
        id,
        isNewAudioNote,
        user?.id,
        user?.authenticationType,
    ]);
    // Unmount-only cleanup
    useEffect(() => () => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
    }, []);
    useEffect(() => () => {
        if (mediaDocUnsubscribeRef.current) {
            mediaDocUnsubscribeRef.current();
            mediaDocUnsubscribeRef.current = null;
        }
    }, []);

    useEffect(() => () => {
        releaseRecordingWaveformResources({ stopStream: true }).catch(() => undefined);
    }, [releaseRecordingWaveformResources]);

    useEffect(() => {
        if (status !== AUDIO_NOTE_RECORDING_STATE.idle) {
            return;
        }

        if (audioBlob || isHttpAudioUrl(audioURL)) {
            return;
        }

        waveformSourceKeyRef.current = null;
        setWaveformBars(buildDeterministicVisibleWaveformBars(fallbackWaveformSeed));
    }, [
        audioBlob,
        audioURL,
        buildDeterministicVisibleWaveformBars,
        fallbackWaveformSeed,
        status,
        visibleWaveformBarCount,
    ]);

    useEffect(() => {
        const persistableStoredWaveformBars = buildPersistableWaveformBars(preferredStoredWaveformBars);

        if (persistableStoredWaveformBars) {
            persistedWaveformBarsRef.current = persistableStoredWaveformBars;
        }
    }, [
        buildPersistableWaveformBars,
        preferredStoredWaveformBars,
    ]);

    useEffect(() => {
        if (
            status === AUDIO_NOTE_RECORDING_STATE.recording
            || status === AUDIO_NOTE_RECORDING_STATE.pausedRecording
        ) {
            return;
        }
        if (hasMeaningfulAudioWaveformBars(preferredStoredWaveformBars)) {
            waveformSourceKeyRef.current = `stored:${id}:bars:${visibleWaveformBarCount}`;
            setWaveformBars(fitAudioWaveformBarsToBarCount({
                bars: preferredStoredWaveformBars,
                barCount: visibleWaveformBarCount,
            }));
            return;
        }

        const decodeRemoteWaveform = async (sourceUrl: string): Promise<number[] | null> => {
            try {
                const response = await fetch(sourceUrl);
                if (!response.ok) {
                    return null;
                }

                return decodeWaveformBarsFromArrayBuffer(await response.arrayBuffer());
            } catch (error) {
                return null;
            }
        };

        const waveformSourceUrl = isHttpAudioUrl(audioURL)
            ? audioURL
            : null;
        let waveformSourceKey: string | null = null;
        if (audioBlob) {
            if (!shouldDecodeAudioNoteWaveformInBrowser({
                audioBlob,
                audioUploaded,
                durationMs: audioLength,
            })) {
                waveformSourceKey = `blob-skipped:${audioBlob.size}:${audioBlob.type}:${audioLength}:${visibleWaveformBarCount}`;
            } else {
                waveformSourceKey = `blob:${audioBlob.size}:${audioBlob.type}:bars:${visibleWaveformBarCount}`;
            }
        } else if (waveformSourceUrl) {
            if (!shouldDecodeSavedAudioNoteWaveformInBrowser({
                durationMs: audioLength,
            })) {
                waveformSourceKey = `url-skipped:${waveformSourceUrl}:${audioLength}:${visibleWaveformBarCount}`;
            } else {
                waveformSourceKey = `url:${waveformSourceUrl}:bars:${visibleWaveformBarCount}`;
            }
        }

        if (!waveformSourceKey || waveformSourceKeyRef.current === waveformSourceKey) {
            return;
        }

        let cancelled = false;

        const resolveWaveformBars = async (): Promise<void> => {
            let resolvedWaveformBars: number[] | null = null;

            if (audioBlob) {
                if (shouldDecodeAudioNoteWaveformInBrowser({
                    audioBlob,
                    audioUploaded,
                    durationMs: audioLength,
                })) {
                    resolvedWaveformBars = await decodeWaveformBarsFromBlob(audioBlob);
                }
            } else if (waveformSourceUrl) {
                if (shouldDecodeSavedAudioNoteWaveformInBrowser({
                    durationMs: audioLength,
                })) {
                    resolvedWaveformBars = await decodeRemoteWaveform(waveformSourceUrl);
                }
            }

            if (cancelled || !isMountedRef.current) {
                return;
            }

            if (playbackStatusRef.current === AUDIO_NOTE_RECORDING_STATE.playing) {
                return;
            }

            waveformSourceKeyRef.current = waveformSourceKey;
            setWaveformBars((currentBars) => {
                if (
                    resolvedWaveformBars
                    && hasMeaningfulAudioWaveformBars(resolvedWaveformBars)
                ) {
                    return resolvedWaveformBars;
                }

                if (hasMeaningfulAudioWaveformBars(currentBars)) {
                    return currentBars;
                }

                return buildSilentAudioWaveformBars({
                    barCount: visibleWaveformBarCount,
                });
            });
        };

        resolveWaveformBars().catch(() => {
            if (cancelled || !isMountedRef.current) {
                return;
            }

            if (playbackStatusRef.current === AUDIO_NOTE_RECORDING_STATE.playing) {
                return;
            }

            waveformSourceKeyRef.current = waveformSourceKey;
            setWaveformBars((currentBars) => (
                hasMeaningfulAudioWaveformBars(currentBars)
                    ? currentBars
                    : buildSilentAudioWaveformBars({
                        barCount: visibleWaveformBarCount,
                    })
            ));
        });

        return () => {
            cancelled = true;
        };
    }, [
        audioBlob,
        audioLength,
        audioUploaded,
        audioURL,
        decodeWaveformBarsFromArrayBuffer,
        decodeWaveformBarsFromBlob,
        fallbackWaveformSeed,
        id,
        preferredStoredWaveformBars,
        status,
        visibleWaveformBarCount,
    ]);

    /**
     * Existing notes can carry an outdated Slate node filePath after backend
     * derivative generation rewrites the media doc to a new canonical object.
     * Prefer the fetched media doc path for playback, and only fall back to the
     * editor node path while the media doc has not loaded yet.
     */
    useEffect(() => {
        setRemoteFilePath(resolveAudioNotePlaybackFilePath({
            filePath,
            remoteFilePath: audioNoteMediaItem?.filePath || null,
            autoRecordIdentifier,
            readOnlyUsesPreviewSource,
            isPostContentDictation: postContentDictationEnabled,
            requireRemoteFilePath: requiresAuthoritativePreviewMediaItem,
        }));
    }, [
        audioNoteMediaItem?.filePath,
        autoRecordIdentifier,
        filePath,
        readOnlyUsesPreviewSource,
        postContentDictationEnabled,
        requiresAuthoritativePreviewMediaItem,
    ]);

    /**
     * Protected post-audio playback must wait for the backend-authoritative
     * media item so we never keep a stale direct Storage URL alive from
     * earlier state.
     */
    useEffect(() => {
        if (!requiresBackendIssuedPlaybackUrl) {
            return;
        }

        if (
            isHttpAudioUrl(audioNoteMediaItem?.resolvedFileURL)
            || !!audioNoteMediaItem?.filePath
        ) {
            return;
        }

        if (isHttpAudioUrl(audioURL)) {
            setAudioURL(null);
            logAudioNotePreviewDebug('preview-audio-url-cleared-awaiting-authoritative-media', {
                id,
                previousAudioURL: audioURL,
            });
        }
    }, [
        requiresBackendIssuedPlaybackUrl,
        audioNoteMediaItem?.resolvedFileURL,
        audioNoteMediaItem?.filePath,
        audioURL,
    ]);

    /**
     * Reload the audio element every time the active playback source changes.
     * Keep passive source sync out of the active startup/playback path so late
     * metadata hydration does not interrupt a user-initiated play request.
     */
    useEffect(() => {
        if (isStartingPlayback || status === AUDIO_NOTE_RECORDING_STATE.playing) {
            return;
        }

        syncAudioElementSource(audioURL, playbackSourceMimeType);
    }, [
        audioURL,
        isStartingPlayback,
        playbackSourceMimeType,
        status,
        syncAudioElementSource,
    ]);

    /**
     * Pausing a note releases its audio element to conserve browser media
     * resources. Reapply the saved playback speed whenever that element is
     * recreated so the floating mini-player can still change speed while paused.
     */
    useEffect(() => {
        if (!audioNodeRef.current) {
            return;
        }

        if (audioNodeRef.current.playbackRate === playbackSpeed) {
            return;
        }

        audioNodeRef.current.playbackRate = playbackSpeed;
    }, [audioURL, playbackSpeed]);

    // Determine Recording Permissions
    useEffect(() => {
        let cancelled = false;
        let permissionProbeStream: MediaStream | null = null;

        if (!readOnly) {
            if (navigator.mediaDevices.getUserMedia) {
                navigator.mediaDevices.getUserMedia({ audio: true })
                    .then((stream: MediaStream): void => {
                        permissionProbeStream = stream;
                        if (cancelled || !isMountedRef.current) {
                            stream.getTracks().forEach((track) => track.stop());
                            return;
                        }
                        const mediaStreamTrack = stream.getAudioTracks()[0];
                        if (typeof mediaStreamTrack !== 'undefined') {
                            setHasRecordingPermissions(true);
                        }
                        stream.getTracks().forEach((track) => track.stop());
                    })
                    .catch((e) => {
                        if (cancelled || !isMountedRef.current) return;
                        switch (e.name) {
                        case 'NotFoundError':
                        case 'DevicesNotFoundError':
                            setErrorMessage(AUDIO_NOTE_RECORDING_DEVICE_NOT_FOUND);
                            if (hasRecordingPermissions) {
                                setHasRecordingPermissions(false);
                            }
                            break;
                        case 'SourceUnavailableError':
                            setErrorMessage(AUDIO_NOTE_RECORDING_DEVICE_BUSY);
                            if (hasRecordingPermissions) {
                                setHasRecordingPermissions(false);
                            }
                            break;
                        case 'PermissionDeniedError':
                        case 'SecurityError':
                            setErrorMessage(AUDIO_NOTE_PERMISSION_DENIED_ERROR);
                            if (hasRecordingPermissions) {
                                setHasRecordingPermissions(false);
                            }
                            break;
                        default:
                            setErrorMessage(AUDIO_NOTE_GENERAL_ERROR);
                            if (hasRecordingPermissions) {
                                setHasRecordingPermissions(false);
                            }
                        }
                    });
            } else if (!cancelled && isMountedRef.current) {
                setErrorMessage(AUDIO_NOTE_BROWSER_NOT_SUPPORTED);
                if (hasRecordingPermissions) {
                    setHasRecordingPermissions(false);
                }
            }
        } else if (readOnly && mimeType) {
            // We are in read only
            // determine if audio type playable
            disableIfAudioNotSupported();
        }

        return () => {
            cancelled = true;
            if (permissionProbeStream) {
                permissionProbeStream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [readOnly, mimeType]);

    // Detect Auto Record
    useEffect(() => {
        if (
            autoRecord
            && !!handleAutoRecord
            && hasRecordingPermissions
            && autoRecordIdentifier
            && filePath === autoRecordIdentifier
        ) {
            // Notify parent editor to stop other recordings
            setIsRecordingAudioNote(false);
            setIsNewAudioNote(true);
            setIsEditing(true);
            startRecord();
            handleAutoRecord(false);
        }
    }, [hasRecordingPermissions]);

    // Listens to Editor parent on whether to stop recording
    // Triggered when new other audio note created
    useEffect(() => {
        if (
            status === AUDIO_NOTE_RECORDING_STATE.recording
            && !isRecordingAudioNote
            && prevIsRecordingAudioNote
        ) {
            stopRecord();
        }
        setPrevIsRecordingAudioNote(isRecordingAudioNote);
    }, [isRecordingAudioNote]);

    // Handle Play/Pause using Spacebar
    useEffect(() => {
        if (
            spacebarPress
            && selected
            && focused
            && (
                !isEditing
                || (
                    document.activeElement !== titleInputRef.current
                    && document.activeElement !== descriptionInputRef.current
                )
            ) && status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
        ) {
            // Play if paused, and not recording mode
            playAudioNote(true);
        } else if (
            spacebarPress
            && selected
            && focused
            && status === AUDIO_NOTE_RECORDING_STATE.playing
        ) {
            // Pause if playing
            playAudioNote(false);
        } else if (
            spacebarPress
            && selected
            && focused
            && status === AUDIO_NOTE_RECORDING_STATE.recording
        ) {
            // Pause recording
            pauseRecord();
        } else if (
            spacebarPress
            && selected
            && focused
            && status === AUDIO_NOTE_RECORDING_STATE.pausedRecording
        ) {
            // Resume Recording
            resumeRecord();
        }
    }, [spacebarPress]);

    // Handle Move Cursor using Enter
    // when in title
    useEventListener(
        'keydown',
        (e: Event) => {
            if ((e as KeyboardEvent).key === KEYCODE.enter) {
                e.preventDefault();
                // Pressing enter from title to description
                descriptionInputRef.current?.focus();
                if (descriptionInputRef.current) {
                    moveCursorToEnd(descriptionInputRef.current);
                }
            }
        },
        titleInputRef.current as HTMLElement,
    );

    /**
     * Only hydrate the DOM editor from incoming persisted description text.
     * Rewriting `innerText` on every local keystroke can reset the caret and
     * make subsequent typing insert on the wrong side of the current text.
     */
    useEffect(() => {
        if (!isEditing || !descriptionInputRef.current) {
            return;
        }

        if (descriptionInputRef.current.innerText === normalizedIncomingAudioDescription) {
            return;
        }

        if (audioDescription !== normalizedIncomingAudioDescription) {
            return;
        }

        descriptionInputRef.current.innerText = normalizedIncomingAudioDescription;
    }, [
        audioDescription,
        isEditing,
        descriptionInputRef.current,
        normalizedIncomingAudioDescription,
    ]);

    /**
     * Annotation audio notes should be immediately ready for metadata entry
     * after recording stops. Wait until the recorded preview has mounted, then
     * move focus into the description field so the first click is not spent
     * re-targeting the editable surface.
     */
    useEffect(() => {
        if (!shouldFocusDescriptionAfterRecordingRef.current) {
            return;
        }

        if (
            !isEditing
            || isPreparingRecordedPreview
            || status !== AUDIO_NOTE_RECORDING_STATE.pausedComplete
            || !descriptionInputRef.current
        ) {
            return;
        }

        window.requestAnimationFrame(() => {
            if (!descriptionInputRef.current) {
                return;
            }

            descriptionInputRef.current.focus();
            moveCursorToEnd(descriptionInputRef.current);
            shouldFocusDescriptionAfterRecordingRef.current = false;
        });
    }, [
        isEditing,
        isPreparingRecordedPreview,
        status,
    ]);

    // Update the audio length in realtime when recording audio
    useEventListener(
        'timeupdate',
        useCallback(() => {
            if (
                audioNodeRef.current
                && !Number.isNaN(audioNodeRef.current.duration)
            ) {
                hydratePersistedAudioDurationMs(
                    audioNodeRef.current.duration * MILLISECONDS_IN_A_SECOND,
                );
            }
        }, [audioNodeRef.current, hydratePersistedAudioDurationMs]),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        audioNodeRef.current!,
    );

    // Create audio note blob when done recording
    useEventListener(
        'dataavailable',
        useCallback((event) => {
            const nextChunk = (event as BlobEvent).data;
            if (nextChunk?.size > 0) {
                recordedAudioChunksRef.current.push(nextChunk);
            }
        }, []),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        mediaRecorderRef.current!,
    );

    // Update audio length when metadata updated
    useEventListener(
        'loadedmetadata',
        useCallback((): void => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const { duration } = audioNodeRef.current!;
            // Chromium bug condition
            if (audioNodeRef.current && duration === Infinity) {
                // ...set audioNodeRef.current.currentTime to a very large number
                // ...listen to "timeupdate" event
                // ...on "timeupdate", retrieve now-updated duration
                audioNodeRef.current.currentTime = 10000000 * Math.random();
            } else if (!Number.isNaN(duration)) {
                hydratePersistedAudioDurationMs(duration * MILLISECONDS_IN_A_SECOND);
            }
        }, [audioNodeRef.current, hydratePersistedAudioDurationMs]),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        audioNodeRef.current!,
    );

    // Ensure startup spinner clears once playback state changes.
    useEventListener(
        'playing',
        useCallback(() => {
            setIsStartingPlayback(false);
        }, [audioNodeRef.current]),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        audioNodeRef.current!,
    );

    useEventListener(
        'pause',
        useCallback(() => {
            setIsStartingPlayback(false);
        }, [audioNodeRef.current]),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        audioNodeRef.current!,
    );

    useEventListener(
        'ended',
        useCallback(() => {
            setIsStartingPlayback(false);
            handleAudioPlaybackEnded();
        }, [
            audioNodeRef.current,
            handleAudioPlaybackEnded,
        ]),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        audioNodeRef.current!,
    );

    // Create audionote when recording stopped
    useEventListener(
        'stop',
        useCallback(() => {
            const recordedChunks = recordedAudioChunksRef.current;
            recordedAudioChunksRef.current = [];
            releaseRecordingWaveformResources({ stopStream: true }).catch(() => undefined);
            mediaRecorderRef.current = null;

            if (recordedChunks.length > 0) {
                const defaultTitle = title || getDefaultAudioTitle();
                setAudioTitle(defaultTitle);
                handleCreateAudioNote(recordedChunks);
                // Flag as not an upload
                setAudioUploaded(false);
                return;
            }

            setIsPreparingRecordedPreview(false);
        }, [handleCreateAudioNote, releaseRecordingWaveformResources, title]),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        mediaRecorderRef.current!,
    );

    // Update elapsed time
    // Stop audio when reached the end
    useInterval(() => {
        const newElapsed = computeTimeElapsed();
        setTimeElapsed(newElapsed);
        // Stop Playback when reach end of recording
        if (
            audioNodeRef.current
            && status === AUDIO_NOTE_RECORDING_STATE.playing
            && audioLength > 0
            && !Number.isNaN(audioLength)
            && roundToNDecimals(newElapsed, 2) >= roundToNDecimals(audioLength, 2)
        ) {
            handleAudioPlaybackEnded();
        }
        // The alternative occurs when the metadata is not fetched.
        // This results in immediately ending the song.
        // This is an edge case that happens a handful of times.
        // Run the playback clock at a smoother cadence so transcript word flips do
        // not visibly lag behind the audio element.
    }, isUpdatingTime ? MILLISECONDS_IN_A_SECOND / 30 : null);

    // Listen for changes to the selection
    // Used to update the position of the title
    // and description inputs while editing
    useEventListener(
        'keyup',
        () => {
            if (
                (
                    titleNodeRef.current
                    || descriptionNodeRef.current
                ) && isEditing
            ) {
                setSelection((int) => int + 1);
            }
        },
    );

    // Remove Paste Styling for Title
    useRemovePasteStyling(titleInputRef.current!);
    // Remove Paste Styling for Description
    useRemovePasteStyling(descriptionInputRef.current!);
    // Handle changes to description input
    useEventListener(
        'input',
        () => {
            const nextAudioDescription = sanitizeAudioNoteDescription(descriptionInputRef.current!.innerText);
            setAudioDescription(nextAudioDescription);
        },
        descriptionInputRef.current!,
    );

    // Update position of audio title node
    useEffect(() => {
        if (!isEditing) {
            return;
        }

        // Modify width of Title Input
        handleModifyTitleInputWidth();
    }, [
        status,
        audioTitle,
        isEditing,
        selection,
        titleNodeRef.current,
    ]);

    // Update position of audio description node
    useEffect(() => {
        if (!isEditing) {
            return;
        }

        // Modify width of Description Input
        handleModifyDescriptionInputWidth();
    }, [
        status,
        audioDescription,
        isEditing,
        selection,
        descriptionNodeRef.current,
    ]);

    useEffect(() => {
        if (uploadingStateChangeEvent) {
            handleUploadingMediaStateChange(
                uploadingStateChangeEvent.mediaItem,
                uploadingStateChangeEvent.snapshot,
                uploadingStateChangeEvent.progress,
            );
            // setUploadingStateChangeEvent(null);
        }
    }, [uploadingStateChangeEvent]);

    useEffect(() => {
        if (uploadingCompleteEvent) {
            // Handle successful uploads on complete
            getDownloadURL(uploadingCompleteEvent.storageRef).then(() => {
                handleUploadingMediaComplete(uploadingCompleteEvent.mediaItem);
            });
        }
    }, [uploadingCompleteEvent]);

    /**
     * Mirror local audio-note draft state back to the parent editor so submit
     * gating no longer depends on probing child nodes at click time.
     */
    useEffect(() => {
        if (audioNoteIsDirty === reportedDirtyAudioNoteRef.current) {
            return;
        }

        if (setAudioNoteDirtyState) {
            setAudioNoteDirtyState(id, audioNoteIsDirty);
        }
        reportedDirtyAudioNoteRef.current = audioNoteIsDirty;
    }, [
        audioNoteIsDirty,
        id,
        setAudioNoteDirtyState,
    ]);

    useEffect(() => () => {
        if (
            reportedDirtyAudioNoteRef.current
            && setAudioNoteDirtyState
        ) {
            setAudioNoteDirtyState(id, false);
        }
        reportedDirtyAudioNoteRef.current = false;
    }, [
        id,
        setAudioNoteDirtyState,
    ]);

    /**
     * Saving state is tracked separately so the parent editor can surface a
     * more specific disabled reason while persistence is in flight.
     */
    useEffect(() => {
        if (isSavingAudioNote === reportedSavingAudioNoteRef.current) {
            return;
        }

        if (setAudioNoteSavingState) {
            setAudioNoteSavingState(id, isSavingAudioNote);
        }
        reportedSavingAudioNoteRef.current = isSavingAudioNote;
    }, [
        id,
        isSavingAudioNote,
        setAudioNoteSavingState,
    ]);

    useEffect(() => () => {
        if (
            reportedSavingAudioNoteRef.current
            && setAudioNoteSavingState
        ) {
            setAudioNoteSavingState(id, false);
        }
        reportedSavingAudioNoteRef.current = false;
    }, [
        id,
        setAudioNoteSavingState,
    ]);

    // unmount cleanup
    useEffect(() => () => {
        if (lastBlobUrlRef.current) {
            URL.revokeObjectURL(lastBlobUrlRef.current);
            lastBlobUrlRef.current = null;
        }
    }, []);

    useEffect(() => () => {
        cancelPreviewAudioEndPrompt();
    }, [cancelPreviewAudioEndPrompt]);

    useEffect(() => {
        if (shouldPlayReadOnlyPreviewAudioEndPrompt) {
            return;
        }

        cancelPreviewAudioEndPrompt();
    }, [
        cancelPreviewAudioEndPrompt,
        shouldPlayReadOnlyPreviewAudioEndPrompt,
    ]);

    useEffect(() => () => {
        if (onReadOnlyPlaybackStateChange) {
            onReadOnlyPlaybackStateChange(id, null);
        }
    }, [
        id,
        onReadOnlyPlaybackStateChange,
    ]);

    // Handle Resize
    useEventListener(
        'resize',
        () => {
            handleViewportResize();
        },
    );

    useEventListener(
        'scroll',
        () => {
            // Modify width of Title Input
            handleModifyTitleInputWidth();
            // Modify width of Description Input
            handleModifyDescriptionInputWidth();
        },
        parentRef,
    );

    const titleTarget = usePortal(
        `audio-note-title-input-${tempId}`,
        parentRef,
        true,
    );

    const descriptionTarget = usePortal(
        `audio-note-description-input-${tempId}`,
        parentRef,
        true,
    );

    // resolve a streaming URL (signed URL) WITHOUT downloading the file as a Blob
    const resolveStreamingURL = useCallback(async (data: {
        audioUrl?: string | null,
        playableFilePath?: string | null,
        resolvedFileURL?: string | null,
    } = {}): Promise<string | null> => {
        const hasOverrides = Object.keys(data).length > 0;
        const effectivePlayableFilePath = data.playableFilePath !== undefined
            ? data.playableFilePath
            : playableFilePath;
        const effectiveResolvedFileURL = data.resolvedFileURL !== undefined
            ? data.resolvedFileURL
            : (audioNoteMediaItem?.resolvedFileURL || null);
        let effectiveAudioURL = audioURL;
        if (requiresBackendIssuedPlaybackUrl) {
            effectiveAudioURL = effectiveResolvedFileURL;
        }
        if (data.audioUrl !== undefined) {
            effectiveAudioURL = data.audioUrl;
        }

        if (!effectivePlayableFilePath && !isHttpAudioUrl(effectiveResolvedFileURL)) return null;
        logAudioNotePreviewDebug('resolveStreamingURL:start', {
            id,
            effectivePlayableFilePath,
            effectiveResolvedFileURL,
            effectiveAudioURL,
            hasOverrides,
        });

        // If we already have a real remote URL, reuse it
        // (note: local blob URLs start with blob:)
        if (isHttpAudioUrl(effectiveAudioURL)) return effectiveAudioURL || null;
        if (isHttpAudioUrl(effectiveResolvedFileURL)) {
            return effectiveResolvedFileURL || null;
        }

        // Deduplicate concurrent resolves
        if (!hasOverrides && resolvingURLRef.current) return resolvingURLRef.current;

        const resolvePromise = (async () => {
            try {
                const storage = getStorage();
                const storageBucket = app.options.storageBucket || null;
                let authToken: string | null = null;

                if (auth.currentUser?.getIdToken) {
                    authToken = await auth.currentUser.getIdToken().catch(() => null);
                }

                const getStorageMetadata = async (storagePath: string): Promise<{
                    downloadTokens?: string | null,
                } | null> => {
                    if (!storageBucket) {
                        return null;
                    }

                    const response = await fetch(
                        `https://firebasestorage.googleapis.com/v0/b/${
                            encodeURIComponent(storageBucket)
                        }/o/${
                            encodeURIComponent(storagePath)
                        }`,
                        {
                            headers: authToken
                                ? {
                                    Authorization: `Bearer ${authToken}`,
                                }
                                : undefined,
                        },
                    );

                    if (!response.ok) {
                        return null;
                    }

                    const metadata = await response.json() as {
                        downloadTokens?: string | null,
                    };
                    return metadata;
                };

                return await resolveAudioPlaybackUrl({
                    audioUrl: effectiveAudioURL,
                    playableFilePath: effectivePlayableFilePath,
                    storageBucket,
                    getSignedUrl: async (storagePath: string) => getDownloadURL(ref(storage, storagePath)),
                    getStorageMetadata,
                });
            } catch (e) {
                logAudioNotePreviewDebug('resolveStreamingURL:error', {
                    id,
                    effectivePlayableFilePath,
                    effectiveResolvedFileURL,
                    error: e instanceof Error ? e.message : String(e),
                });
                return null;
            } finally {
                if (!hasOverrides) {
                    resolvingURLRef.current = null;
                }
            }
        })();

        if (!hasOverrides) {
            resolvingURLRef.current = resolvePromise;
        }

        return resolvePromise;
    }, [
        playableFilePath,
        audioURL,
        audioNoteMediaItem?.resolvedFileURL,
        auth.currentUser,
        app.options.storageBucket,
        requiresBackendIssuedPlaybackUrl,
    ]);

    // ===== Memoization =====

    /**
     * Indicates whether the audio note has an audio file
     * Important: audioURL is also a proxy for fileURL which an empty
     * value also implies no audio file
     * We use audioURL instead of fileURL to be able to handle case
     * where we have the fileURL but are unable to retreive the audioURL
     * In such a case we set audioURL to null
     */
    const AUDIO_NOTE_EMPTY = useMemo(() => readOnly && !(audioURL || playableFilePath), [
        readOnly,
        audioURL,
        playableFilePath,
    ]);
    /**
     * Keep the waveform lane hidden until the note has either hydrated its
     * backend media metadata or already resolved a browser-usable source. The
     * embedded Slate file path alone is enough to queue playback, but it still
     * renders an odd half-loaded waveform state while the note is loading.
     */
    const hasHydratedPlaybackSource = useMemo(() => (
        !readOnly
        || !!audioBlob
        || !!audioURL
        || hasStartedPlayback
        || hasMeaningfulAudioWaveformBars(preferredStoredWaveformBars)
        || !!audioNoteMediaItem?.filePath
        || isHttpAudioUrl(audioNoteMediaItem?.resolvedFileURL)
    ), [
        audioBlob,
        audioNoteMediaItem?.filePath,
        audioNoteMediaItem?.resolvedFileURL,
        audioURL,
        hasStartedPlayback,
        preferredStoredWaveformBars,
        readOnly,
    ]);
    /**
     * Saved read-only notes can render their title and dictation hint before
     * preview playback metadata arrives. Hide playback-only chrome during that
     * short gap so the card does not render a half-loaded paused state.
     */
    const shouldRenderPlaybackChrome = shouldRenderAudioNotePlaybackChrome({
        audioNoteEmpty: AUDIO_NOTE_EMPTY,
        hasHydratedPlaybackSource,
        readOnly,
        status,
    });
    const shouldRenderPlaybackControls = shouldRenderAudioNotePlaybackControls({
        audioNoteEmpty: AUDIO_NOTE_EMPTY,
        readOnly,
        status,
    });
    const shouldRenderActionRow = shouldRenderAudioNoteActionRow({
        audioNoteEmpty: AUDIO_NOTE_EMPTY,
        readOnly,
        status,
    });
    /**
     * Keep the media element mounted whenever we already know there is a
     * viable playback source. Anonymous preview playback can receive the
     * backend-authoritative URL before `audioURL` has been committed locally,
     * and mounting early avoids racing the play path against a missing node.
     */
    const shouldMountAudioElement = useMemo(() => (
        !!audioURL
        || !!audioBlob
        || !!playableFilePath
        || !!remoteFilePath
        || requiresBackendIssuedPlaybackUrl
        || isHttpAudioUrl(audioNoteMediaItem?.resolvedFileURL)
        || isStartingPlayback
    ), [
        audioBlob,
        audioNoteMediaItem?.resolvedFileURL,
        audioURL,
        isStartingPlayback,
        playableFilePath,
        requiresBackendIssuedPlaybackUrl,
        remoteFilePath,
    ]);
    const audioElementNode = shouldMountAudioElement ? (
        <AudioElement
            ref={audioNodeRef}
            preload="none"
            playsInline
        >
            <AudioSource
                src={audioURL || undefined}
                type={playbackSourceMimeType || undefined}
            />
            <AudioTrack kind="captions" />
        Your browser does not support audio.
        </AudioElement>
    ) : null;

    // ===== Rendering =====

    // We subtract 10px because we have more than one button
    // Therefore they are stacked vertically
    const SMALL_ACTION_BUTTON_LENGTH = useMemo(() => buttonLength - 10, [buttonLength]);
    const propagateBackground = useMemo(() => !isEditing && status !== AUDIO_NOTE_RECORDING_STATE.idle, [
        isEditing,
        status,
    ]);

    let Icon;
    let primaryColor = 'white';
    let handleClick: (e: React.MouseEvent | React.TouchEvent) => void;
    const postContentDictationHintText = (() => {
        if (postContentSpeechGenerationRequested && postContentDictationEnabled) {
            return 'Save to generate speech and word timings from the current post text.';
        }

        if (postContentSpeechGenerationRequested) {
            return 'Save to generate speech from the current post text.';
        }

        return 'This audio is timed to the post text. Hit play to highlight the words as you read.';
    })();
    const postContentDictationHint = shouldShowPostContentDictationHint
        ? (
            <PostContentDictationHint>
                <PostContentDictationHintIcon src={InfoIcon} />
                <PostContentDictationHintText>
                    {postContentDictationHintText}
                </PostContentDictationHintText>
            </PostContentDictationHint>
        )
        : null;
    const preventAudioNoteButtonDefault = (e: React.MouseEvent | React.TouchEvent): void => {
        e.preventDefault();
        e.nativeEvent.stopPropagation();
    };
    switch (status) {
    case AUDIO_NOTE_RECORDING_STATE.pausedComplete:
        handleClick = (e: React.MouseEvent | React.TouchEvent) => {
            preventAudioNoteButtonDefault(e);
            if (postContentSpeechGenerationRequested && isNewAudioNote) {
                setSnackbarData({
                    visible: true,
                    duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                    text: 'Save this audio note to generate speech before playing it.',
                    icon: SpeakerIcon,
                    hasFailure: true,
                });
                return;
            }
            if (isGeneratedPostSpeechPending) {
                setSnackbarData({
                    visible: true,
                    duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                    text: 'Speech generation is still running for this audio note.',
                    icon: SpeakerIcon,
                    hasFailure: true,
                });
                return;
            }
            playAudioNote(true);
        };
        Icon = PlayIcon;
        break;
    case AUDIO_NOTE_RECORDING_STATE.pausedRecording:
        handleClick = (e: React.MouseEvent | React.TouchEvent) => {
            preventAudioNoteButtonDefault(e);
            resumeRecord();
        };
        break;
    case AUDIO_NOTE_RECORDING_STATE.playing:
        handleClick = (e: React.MouseEvent | React.TouchEvent) => {
            preventAudioNoteButtonDefault(e);
            playAudioNote(false);
        };
        Icon = PauseIcon;
        break;
    case AUDIO_NOTE_RECORDING_STATE.recording:
        handleClick = (e: React.MouseEvent | React.TouchEvent) => {
            preventAudioNoteButtonDefault(e);
            pauseRecord();
        };
        Icon = PauseIcon;
        primaryColor = 'red';
        break;
    default:
        handleClick = (e: React.MouseEvent | React.TouchEvent) => {
            preventAudioNoteButtonDefault(e);
            startRecord();
        };
        Icon = MicrophoneIcon;
        break;
    }

    const shouldRenderInlineWaveform = !errorMessage
        && shouldRenderPlaybackChrome;
    const isRecordingInlineWaveform = (
        status === AUDIO_NOTE_RECORDING_STATE.recording
        || status === AUDIO_NOTE_RECORDING_STATE.pausedRecording
    );
    const resolvedAudioNoteLayout = useMemo(() => resolveAudioNoteLayout({
        containerWidth: audioNoteContainerWidth,
        inlineMetaWidth: shouldRenderInlineWaveform
            ? playbackMetaWidth
            : null,
        isRecording: isRecordingInlineWaveform,
    }), [
        audioNoteContainerWidth,
        isRecordingInlineWaveform,
        playbackMetaWidth,
        shouldRenderInlineWaveform,
    ]);
    const resolvedTitleFontSize = resolvedAudioNoteLayout.titleFontSize;

    let caption = (
        <ContentText
            hasError={!!errorMessage}
            small={small && status !== AUDIO_NOTE_RECORDING_STATE.recording}
            fontSize={DESCRIPTION_FONT_SIZE}
            buttonLength={buttonLength}
            statusIconMarginLeft={STATUS_ICON_MARGIN_LEFT}
            numButtons={status === AUDIO_NOTE_RECORDING_STATE.recording
                || status === AUDIO_NOTE_RECORDING_STATE.pausedRecording
                ? 2
                : 1}
            playbackContainerPaddingLeft={PLAYBACK_CONTAINER_PADDING_LEFT}
            {...(status === AUDIO_NOTE_RECORDING_STATE.pausedRecording
                ? {
                    color: themeObj.verascopeColor.red200,
                }
                : {}
            )}
        >
            <RecordingLoader small={small} />
        </ContentText>
    );

    if (status === AUDIO_NOTE_RECORDING_STATE.idle) {
        caption = (
            <IdleCaptionContainer>
                <ContentText
                    hasError={!!errorMessage}
                    small={small}
                    fontSize={DESCRIPTION_FONT_SIZE}
                    buttonLength={buttonLength}
                    numButtons={AUDIO_NOTE_EMPTY ? 0 : 1} // not recording or pausedRecording
                    statusIconMarginLeft={STATUS_ICON_MARGIN_LEFT}
                    playbackContainerPaddingLeft={PLAYBACK_CONTAINER_PADDING_LEFT}
                >
                    {getIdleMessage()}
                </ContentText>
                {postContentDictationHint}
            </IdleCaptionContainer>
        );
    } else if (
        status === AUDIO_NOTE_RECORDING_STATE.playing
        || status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
    ) {
        caption = (
            <DetailsContainer
                readOnly={readOnly}
            >
                {isEditing ? (
                    <>
                        {createPortal(
                            <TitleInput
                                type="text"
                                id={TITLE_INPUT_ID(audioURL)}
                                className={HOVER_TARGET_CLASSNAME}
                                ref={titleInputRef}
                                value={audioTitle}
                                placeholder="Audio Note Title"
                                top={titlePos.y}
                                left={titlePos.x}
                                width={titleWidth}
                                fontSize={resolvedTitleFontSize}
                                onChange={handleTitleChange}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onTitleInputMouseEnter,
                                    onMouseLeave: onTitleInputMouseLeave,
                                })}
                                onFocus={onTitleInputFocus}
                                onBlur={onTitleInputBlur}
                            />,
                            titleTarget,
                        )}
                        <TitleInputNode
                            type="text"
                            ref={titleNodeRef}
                            value={audioTitle}
                            placeholder="Audio Note Title"
                            fontSize={resolvedTitleFontSize}
                        />
                    </>
                ) : (
                    <Title
                        fontSize={resolvedTitleFontSize}
                        title={audioTitle}
                    >
                        {audioTitle.length > 0
                            ? audioTitle
                            : 'Untitled'}
                    </Title>
                )}
                {isEditing ? (
                    <>
                        {createPortal(
                            <DescriptionInput
                                id={DESCRIPTION_INPUT_ID(audioURL)}
                                className={HOVER_TARGET_CLASSNAME}
                                ref={descriptionInputRef}
                                defaultValue={audioDescription}
                                data-placeholder="Description..."
                                contentEditable="true"
                                suppressContentEditableWarning
                                top={descriptionPos.top}
                                left={descriptionPos.left}
                                width={descriptionWidth}
                                fontSize={DESCRIPTION_FONT_SIZE}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onDescriptionTextareaMouseEnter,
                                    onMouseLeave: onDescriptionTextareaMouseLeave,
                                })}
                                {...getPressEventHandlers({
                                    onMouseDown: handleDescriptionInputPress,
                                })}
                                onFocus={onDescriptionTextareaFocus}
                                onBlur={onDescriptionTextareaBlur}
                            />,
                            descriptionTarget,
                        )}
                        <DescriptionInputNode
                            ref={descriptionNodeRef}
                            fontSize={DESCRIPTION_FONT_SIZE}
                            data-placeholder="Description..."
                            aria-hidden="true"
                        >
                            {normalizedAudioDescription}
                        </DescriptionInputNode>
                    </>
                ) : (
                    normalizedAudioDescription.length > 0 && (
                        <Description
                            fontSize={DESCRIPTION_FONT_SIZE}
                        >
                            {normalizedAudioDescription}
                        </Description>
                    )
                )}
            </DetailsContainer>
        );
    } else if (status === AUDIO_NOTE_RECORDING_STATE.pausedRecording) {
        caption = (
            <ContentText
                hasError={!!errorMessage}
                small={small}
                fontSize={DESCRIPTION_FONT_SIZE}
                buttonLength={buttonLength}
                numButtons={2} // Is either recording or pausedRecording
                statusIconMarginLeft={STATUS_ICON_MARGIN_LEFT}
                playbackContainerPaddingLeft={PLAYBACK_CONTAINER_PADDING_LEFT}
                {...(status === AUDIO_NOTE_RECORDING_STATE.pausedRecording
                    ? {
                        color: themeObj.verascopeColor.red200,
                    }
                    : {}
                )}
            >
                Paused
            </ContentText>
        );
    }

    const shouldShowInlinePlaybackMeta = shouldRenderInlineWaveform
        && resolvedAudioNoteLayout.shouldShowInlinePlaybackMeta;

    /**
     * Narrow annotation cards can lose most of their middle playback lane once
     * the leading controls and trailing actions render. Measure both the note
     * container and the middle lane so we can switch to a compact layout before
     * the waveform and duration crowd adjacent controls.
     */
    const measureAudioNoteLayout = useCallback((): void => {
        let fallbackContainerWidth: number | null = null;

        if (editorType === EDITOR_CONTEXT_TYPE.annotation) {
            fallbackContainerWidth = resolveAnnotationAudioNoteWidthPx(readOnly);
        }

        const nextContainerWidth = containerRef.current?.clientWidth || fallbackContainerWidth;
        const nextPlaybackMetaWidth = shouldRenderInlineWaveform
            ? (playbackMetaContainerRef.current?.clientWidth || null)
            : null;

        setAudioNoteContainerWidth((currentContainerWidth) => (
            currentContainerWidth === nextContainerWidth
                ? currentContainerWidth
                : nextContainerWidth
        ));
        setPlaybackMetaWidth((currentPlaybackMetaWidth) => (
            currentPlaybackMetaWidth === nextPlaybackMetaWidth
                ? currentPlaybackMetaWidth
                : nextPlaybackMetaWidth
        ));
    }, [
        editorType,
        readOnly,
        shouldRenderInlineWaveform,
    ]);

    useLayoutEffect(() => {
        /**
         * ResizeObserver runs before paint. Defer React state writes to the next
         * frame so measuring the inline audio layout cannot feed back into the
         * same observer notification loop.
         */
        const queueAudioNoteLayoutMeasurement = (): void => {
            if (typeof window === 'undefined') {
                measureAudioNoteLayout();
                return;
            }

            if (audioNoteLayoutMeasurementFrameRef.current != null) {
                return;
            }

            audioNoteLayoutMeasurementFrameRef.current = window.requestAnimationFrame(() => {
                audioNoteLayoutMeasurementFrameRef.current = null;
                measureAudioNoteLayout();
            });
        };

        measureAudioNoteLayout();

        if (typeof ResizeObserver === 'undefined') {
            if (typeof window === 'undefined') {
                return;
            }

            window.addEventListener('resize', queueAudioNoteLayoutMeasurement);

            return () => {
                if (audioNoteLayoutMeasurementFrameRef.current != null) {
                    window.cancelAnimationFrame(audioNoteLayoutMeasurementFrameRef.current);
                    audioNoteLayoutMeasurementFrameRef.current = null;
                }

                window.removeEventListener('resize', queueAudioNoteLayoutMeasurement);
            };
        }

        const resizeObserver = new ResizeObserver(() => {
            queueAudioNoteLayoutMeasurement();
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        if (playbackMetaContainerRef.current) {
            resizeObserver.observe(playbackMetaContainerRef.current);
        }

        return () => {
            if (audioNoteLayoutMeasurementFrameRef.current != null) {
                window.cancelAnimationFrame(audioNoteLayoutMeasurementFrameRef.current);
                audioNoteLayoutMeasurementFrameRef.current = null;
            }

            resizeObserver.disconnect();
        };
    }, [
        measureAudioNoteLayout,
        shouldRenderInlineWaveform,
    ]);

    /**
     * Measure the actual waveform lane so we can keep bar width and gap fixed
     * while only changing how many bars fit in that space.
     */
    useEffect(() => {
        if (!shouldRenderInlineWaveform || !shouldShowInlinePlaybackMeta) {
            return;
        }

        const waveformLaneElement = waveformLaneRef.current;
        if (!waveformLaneElement) {
            return;
        }

        const updateVisibleBarCount = (): void => {
            const nextVisibleBarCount = resolveAudioWaveformBarCountFromWidth({
                width: waveformLaneElement.clientWidth,
            });

            setVisibleWaveformBarCount((currentVisibleBarCount) => (
                currentVisibleBarCount === nextVisibleBarCount
                    ? currentVisibleBarCount
                    : nextVisibleBarCount
            ));
        };

        /**
         * Waveform bar count changes can resize the lane being observed. Keep
         * that state update out of the ResizeObserver delivery phase.
         */
        const queueVisibleBarCountUpdate = (): void => {
            if (typeof window === 'undefined') {
                updateVisibleBarCount();
                return;
            }

            if (waveformBarMeasurementFrameRef.current != null) {
                return;
            }

            waveformBarMeasurementFrameRef.current = window.requestAnimationFrame(() => {
                waveformBarMeasurementFrameRef.current = null;
                updateVisibleBarCount();
            });
        };

        updateVisibleBarCount();

        if (typeof ResizeObserver === 'undefined') {
            if (typeof window === 'undefined') {
                return;
            }

            window.addEventListener('resize', queueVisibleBarCountUpdate);

            return () => {
                if (waveformBarMeasurementFrameRef.current != null) {
                    window.cancelAnimationFrame(waveformBarMeasurementFrameRef.current);
                    waveformBarMeasurementFrameRef.current = null;
                }

                window.removeEventListener('resize', queueVisibleBarCountUpdate);
            };
        }

        const resizeObserver = new ResizeObserver(() => {
            queueVisibleBarCountUpdate();
        });

        resizeObserver.observe(waveformLaneElement);

        return () => {
            if (waveformBarMeasurementFrameRef.current != null) {
                window.cancelAnimationFrame(waveformBarMeasurementFrameRef.current);
                waveformBarMeasurementFrameRef.current = null;
            }

            resizeObserver.disconnect();
        };
    }, [
        shouldRenderInlineWaveform,
        shouldShowInlinePlaybackMeta,
    ]);

    /**
     * If the lane width changes mid-recording, rebuild the visible bars from
     * the existing captured live-bar history so earlier bars keep the same
     * height they had when they were first recorded.
     */
    useEffect(() => {
        if (
            status !== AUDIO_NOTE_RECORDING_STATE.recording
            && status !== AUDIO_NOTE_RECORDING_STATE.pausedRecording
        ) {
            return;
        }

        setWaveformBars(buildScrollingVisibleCapturedWaveformBars(
            recordedWaveformDisplayBarsRef.current,
        ));
    }, [
        buildScrollingVisibleCapturedWaveformBars,
        buildScrollingVisibleWaveformBars,
        status,
        visibleWaveformBarCount,
    ]);

    /**
     * Saved and completed notes can briefly carry a waveform built for an older
     * lane width. Always fit the rendered bars to the currently measured lane
     * so the waveform uses the full slot without clipping on the right.
     */
    const visibleWaveformBars = useMemo(() => fitAudioWaveformBarsToBarCount({
        bars: waveformBars,
        barCount: visibleWaveformBarCount,
    }), [
        visibleWaveformBarCount,
        waveformBars,
    ]);

    const playedWaveformBarCount = useMemo(() => {
        if (
            visibleWaveformBars.length === 0
            || audioLength <= 0
            || (
                status !== AUDIO_NOTE_RECORDING_STATE.playing
                && status !== AUDIO_NOTE_RECORDING_STATE.pausedComplete
            )
        ) {
            return 0;
        }

        const playbackProgress = Math.min(1, Math.max(0, timeElapsed / audioLength));

        return Math.round(playbackProgress * visibleWaveformBars.length);
    }, [
        audioLength,
        status,
        timeElapsed,
        visibleWaveformBars.length,
    ]);
    const inlinePlaybackTimestampMs = useMemo(() => {
        if (
            status === AUDIO_NOTE_RECORDING_STATE.recording
            || status === AUDIO_NOTE_RECORDING_STATE.pausedRecording
        ) {
            return potentialElapsed || timeElapsed;
        }

        return audioLength;
    }, [
        audioLength,
        potentialElapsed,
        status,
        timeElapsed,
    ]);
    const showInlinePlaybackTimestamp = (
        status === AUDIO_NOTE_RECORDING_STATE.recording
        || status === AUDIO_NOTE_RECORDING_STATE.pausedRecording
        || audioLength > 0
    );
    const shouldShowInlinePlaybackTimestamp = showInlinePlaybackTimestamp
        && (
            !isRecordingInlineWaveform
            || (
                playbackMetaWidth != null
                && playbackMetaWidth >= MIN_AUDIO_NOTE_INLINE_PLAYBACK_META_WIDTH_PX
            )
        );
    const useAnnotationPlaybackRows = (
        editorType === EDITOR_CONTEXT_TYPE.annotation
        && (
            status === AUDIO_NOTE_RECORDING_STATE.playing
            || (
                status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
                && timeElapsed > 0
            )
        )
    );

    const effectiveTranscript = useMemo(() => (
        shouldUsePreviewDictationTranscript
            ? trimAudioNoteTranscriptToSourceText(
                audioNoteMediaItem?.transcript,
                normalizedPostContentTranscriptText,
            )
            : (audioNoteMediaItem?.transcript || null)
    ), [
        audioNoteMediaItem?.transcript,
        normalizedPostContentTranscriptText,
        shouldUsePreviewDictationTranscript,
    ]);
    const transcriptData = effectiveTranscript?.data;
    const transcriptWords = useMemo(() => {
        if (!Array.isArray(transcriptData)) {
            return [];
        }

        const words: ITranscriptionWord[] = [];
        transcriptData.forEach((phrase: any) => {
            const phraseWords = phrase?.alternatives?.[0]?.words;
            if (Array.isArray(phraseWords) && phraseWords.length > 0) {
                words.push(...phraseWords);
            }
        });
        return words;
    }, [transcriptData]);
    const transcriptPhrases = useMemo(() => {
        if (!Array.isArray(transcriptData)) {
            return [];
        }

        const phrases: string[] = [];
        transcriptData.forEach((phrase: any) => {
            const transcriptText = phrase?.alternatives?.[0]?.transcript;
            if (typeof transcriptText === 'string' && transcriptText.trim().length > 0) {
                phrases.push(transcriptText.trim());
            }
        });
        return phrases;
    }, [transcriptData]);
    const activeTranscriptWordIndex = useMemo(() => resolveActiveTranscriptionWordIndex({
        transcriptWords,
        currentTimeMs: timeElapsed,
        isPlaying: (
            status === AUDIO_NOTE_RECORDING_STATE.playing
            || status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
        ),
        upcomingWordLeadMs: status === AUDIO_NOTE_RECORDING_STATE.playing
            ? READ_ONLY_AUDIO_NOTE_TRANSCRIPT_HIGHLIGHT_LEAD_MS
            : 0,
        trailingWordHoldMs: status === AUDIO_NOTE_RECORDING_STATE.playing
            ? READ_ONLY_AUDIO_NOTE_TRANSCRIPT_TRAILING_HOLD_MS
            : 0,
    }), [
        transcriptWords,
        timeElapsed,
        status,
    ]);
    const floatingPlaybackCurrentTimeMs = useMemo(() => (
        Math.max(0, Math.floor(timeElapsed / MILLISECONDS_IN_A_SECOND) * MILLISECONDS_IN_A_SECOND)
    ), [timeElapsed]);
    const hasPlayableReadOnlyAudio = !!audioBlob
        || !!audioURL
        || !!playableFilePath
        || !!remoteFilePath
        || (
            !!filePath
            && !requiresAuthoritativePreviewMediaItem
        );
    const lastReportedReadOnlyPlaybackStateRef = useRef<{
        title: string,
        isPostContentDictation: boolean,
        transcriptWords: ITranscriptionWord[],
        activeTranscriptWordIndex: number | null,
        hasStartedPlayback: boolean,
        isStartingPlayback: boolean,
        playbackSpeed: number,
        currentTimeMs: number,
        durationMs: number,
        isPlaying: boolean,
        isVisibleInViewport: boolean,
    } | null>(null);

    useEffect(() => {
        if (!onReadOnlyPlaybackStateChange) {
            return;
        }

        if (!readOnly || !hasPlayableReadOnlyAudio) {
            lastReportedReadOnlyPlaybackStateRef.current = null;
            onReadOnlyPlaybackStateChange(id, null);
            return;
        }

        const nextPlaybackState = {
            title: audioTitle.length > 0
                ? audioTitle
                : (audioNoteDetailsSource.title || 'Untitled'),
            isPostContentDictation: postContentDictationEnabled,
            transcriptWords,
            activeTranscriptWordIndex,
            hasStartedPlayback,
            isStartingPlayback,
            playbackSpeed,
            currentTimeMs: floatingPlaybackCurrentTimeMs,
            durationMs: audioLength,
            isPlaying: status === AUDIO_NOTE_RECORDING_STATE.playing,
            isVisibleInViewport,
        };
        const lastPlaybackState = lastReportedReadOnlyPlaybackStateRef.current;
        const playbackStateChanged = !lastPlaybackState
            || lastPlaybackState.title !== nextPlaybackState.title
            || lastPlaybackState.isPostContentDictation !== nextPlaybackState.isPostContentDictation
            || lastPlaybackState.transcriptWords !== nextPlaybackState.transcriptWords
            || lastPlaybackState.activeTranscriptWordIndex !== nextPlaybackState.activeTranscriptWordIndex
            || lastPlaybackState.hasStartedPlayback !== nextPlaybackState.hasStartedPlayback
            || lastPlaybackState.isStartingPlayback !== nextPlaybackState.isStartingPlayback
            || lastPlaybackState.playbackSpeed !== nextPlaybackState.playbackSpeed
            || lastPlaybackState.currentTimeMs !== nextPlaybackState.currentTimeMs
            || lastPlaybackState.durationMs !== nextPlaybackState.durationMs
            || lastPlaybackState.isPlaying !== nextPlaybackState.isPlaying
            || lastPlaybackState.isVisibleInViewport !== nextPlaybackState.isVisibleInViewport;

        if (!playbackStateChanged) {
            return;
        }

        lastReportedReadOnlyPlaybackStateRef.current = nextPlaybackState;
        onReadOnlyPlaybackStateChange(id, {
            id,
            ...nextPlaybackState,
            updatedAt: Date.now(),
            setPlaying: setReadOnlyPlaybackState,
            setPlaybackSpeed: setReadOnlyPlaybackSpeed,
            seekToTimeMs: seekReadOnlyPlaybackToTime,
        });
    }, [
        id,
        audioNoteDetailsSource.title,
        readOnly,
        hasPlayableReadOnlyAudio,
        audioTitle,
        postContentDictationEnabled,
        transcriptWords,
        activeTranscriptWordIndex,
        hasStartedPlayback,
        isStartingPlayback,
        playbackSpeed,
        floatingPlaybackCurrentTimeMs,
        audioLength,
        status,
        isVisibleInViewport,
        setReadOnlyPlaybackState,
        setReadOnlyPlaybackSpeed,
        seekReadOnlyPlaybackToTime,
        onReadOnlyPlaybackStateChange,
    ]);
    const transcriptButtonColor = useMemo(() => {
        if (!effectiveTranscript || !showTranscript || !color) {
            return undefined;
        }

        if (!validHexCode(color)) {
            return color;
        }

        const colorRgb = hexToRgb(color);
        const colorHsl = rgbToHsl({
            r: colorRgb?.r ?? 0,
            g: colorRgb?.g ?? 0,
            b: colorRgb?.b ?? 0,
        });

        // Keep the expanded transcript toggle visually distinct from the
        // surrounding editor chrome without requiring hover.
        return setColorLightness(color, Math.max(colorHsl.l - 10, 0));
    }, [
        effectiveTranscript,
        showTranscript,
        color,
    ]);
    const transcriptionJob = audioNoteMediaItem?.transcriptionJob;
    const showTranscriptionStatus = shouldShowAudioNoteTranscriptionStatus({
        transcript: effectiveTranscript,
        transcriptionJob,
    });
    const transcriptionStatusLabel = getAudioNoteTranscriptionStatusLabel(transcriptionJob);
    const shouldRenderTranscriptionStatusBadge = (
        userIsAdmin
        && showTranscriptionStatus
        && !!transcriptionStatusLabel
    );
    const isPendingTranscriptionStatus = isAudioNoteTranscriptionStatusPending(transcriptionJob);
    const isFailedTranscriptionStatus = isAudioNoteTranscriptionStatusFailure(transcriptionJob);
    const isStalledTranscriptionStatus = isAudioNoteTranscriptionStatusStalled(transcriptionJob);
    const isSkippedTranscriptionStatus = transcriptionJob?.status === AUDIO_TRANSCRIPTION_JOB_STATUS.skipped;
    const isGeneratedPostSpeechPending = (
        audioNoteMediaItem?.generatedSpeech === true
        && isPendingTranscriptionStatus
    );
    const postContentDictationTooltipText = postContentDictationEnabled
        ? 'Post dictation is on. Saving will align this audio note against the current post text.'
        : 'Mark this audio note as a dictation of the current post text.';
    const showPlaybackStartingSpinner = status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
        && (isStartingPlayback || isPreparingRecordedPreview);
    const transcriptHighlightingEnabled = (
        status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
        || status === AUDIO_NOTE_RECORDING_STATE.playing
    );
    /**
     * Keep the paused progress bar anchored to saved playback state because
     * the audio element can be released after pause to free browser media
     * resources on long posts.
     */
    const currentPlaybackProgressPercent = useMemo(() => {
        if (audioLength <= 0) {
            return 0;
        }

        return 100 * (timeElapsed / audioLength);
    }, [
        audioLength,
        timeElapsed,
    ]);
    const potentialPlaybackProgressPercent = useMemo(() => {
        if (audioLength <= 0 || potentialElapsed == null) {
            return 0;
        }

        return 100 * (potentialElapsed / audioLength);
    }, [
        audioLength,
        potentialElapsed,
    ]);
    const showPlaybackProgressBar = (
        hasStartedPlayback
        && (
            status === AUDIO_NOTE_RECORDING_STATE.playing
            || (
                status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
                && audioLength > 0
            )
        )
    );
    const audioRemovalModalMessage = useMemo(() => (
        audioRemovalModalMode === AUDIO_NOTE_REMOVAL_MODAL_MODE.redo
            ? {
                icon: RedoIcon,
                title: 'Redo audio note',
                message: 'Are you sure? This will remove the current audio and start a new recording.',
            }
            : {
                icon: TrashIcon,
                title: 'Remove audio note',
                message: 'Are you sure? This will remove the current audio from this audio note.',
            }
    ), [
        audioRemovalModalMode,
    ]);

    return (
        <>
            <Modal
                hasContainer
                annotationEditorOwned={editorType === EDITOR_CONTEXT_TYPE.annotation}
                onCloseInteractionStart={requestEditorDismissalSuppression}
                customCursorActive={customCursorActive}
                hasSound={hasSound}
                isOpen={audioRemovalModalMode != null}
                closeModal={handleCloseAudioRemovalModal}
            >
                <GenericModal
                    customCursorActive={customCursorActive}
                    message={audioRemovalModalMessage}
                    callback={handleConfirmAudioRemoval}
                    handleClose={handleCloseAudioRemovalModal}
                    acceptText={audioRemovalModalMode === AUDIO_NOTE_REMOVAL_MODAL_MODE.redo ? 'Redo' : 'Remove'}
                    hideCancel={false}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                />
            </Modal>
            <Container
                ref={containerRef}
                data-audio-note-id={id}
                data-annotation-interactive="true"
                {...attributes}
                className={HOVER_TARGET_CLASSNAME}
                color={color}
                small={small}
                focused={focused}
                selected={selected}
                descriptionInputFocused={descriptionInputFocused}
                padding={CONTAINER_PADDING_AUDIO_NOTE}
                readOnly={readOnly}
                editorType={editorType}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: onContainerMouseEnter,
                    onMouseLeave: onContainerMouseLeave,
                })}
                // Don't add 'contentEditable={false}' while editing because it prevents backspace to delete
                // Necessary to avoid: Cannot resolve a Slate point from DOM point
                // Reference: https://github.com/ianstormtaylor/slate/issues/3930
                contentEditable={!readOnly}
                suppressContentEditableWarning
            >
                <Transition
                    in={isSavingAudioNote}
                    timeout={{
                        enter: TOOLTIP_TRANSITION_DURATION,
                        exit: TOOLTIP_TRANSITION_DURATION,
                    }}
                    appear
                    mountOnEnter
                >
                    {(transitionState) => (
                        <SavingAudioNoteTooltipContainer
                            style={{
                                ...FADE_IN_DEFAULT_STYLE({
                                    direction: 'up',
                                    offset: 10,
                                    duration: TOOLTIP_TRANSITION_DURATION,
                                    easing: themeObj.motion.eagerEasing,
                                }),
                                ...FADE_IN_TRANSITION_STYLES({
                                    direction: 'up',
                                    offset: 10,
                                })[transitionState],
                            }}
                        >
                            <SavingAudioNoteTooltipText>
                            Saving...
                            </SavingAudioNoteTooltipText>
                            <Spinner />
                        </SavingAudioNoteTooltipContainer>
                    )}
                </Transition>
                <ButtonContainer
                    compactRecordingSpacing={isRecordingInlineWaveform}
                    annotationPlaybackRows={useAnnotationPlaybackRows}
                >
                    {!errorMessage && shouldRenderPlaybackControls && (
                        <PlaybackContainer
                            paddingLeft={PLAYBACK_CONTAINER_PADDING_LEFT}
                        >
                            <PlaybackButtons
                                status={status}
                                buttonLength={buttonLength}
                                noPermissions={!hasRecordingPermissions}
                            >
                                {(!readOnly || filePath || remoteFilePath || requiresAuthoritativePreviewMediaItem)
                            && (
                                <StatusIconContainer
                                    className={HOVER_TARGET_CLASSNAME}
                                    customCursorActive={customCursorActive}
                                    width={buttonLength}
                                    color={themeObj.color[primaryColor]}
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: onButtonMouseEnter,
                                        onMouseLeave: onButtonMouseLeave,
                                    })}
                                    {...getPressEventHandlers({ onMouseDown: handleClick })}
                                >
                                    {status === AUDIO_NOTE_RECORDING_STATE.pausedRecording
                                        ? <RecordingIcon />
                                        : null}
                                    {status !== AUDIO_NOTE_RECORDING_STATE.pausedRecording
                                        && showPlaybackStartingSpinner
                                        && <LoadingIcon src={SpinnerIcon} />}
                                    {status !== AUDIO_NOTE_RECORDING_STATE.pausedRecording
                                        && !showPlaybackStartingSpinner
                                        && (
                                            <ReactSVG
                                                src={Icon}
                                            />
                                        )}
                                </StatusIconContainer>
                            )}
                                {(
                                    status === AUDIO_NOTE_RECORDING_STATE.recording
                                || status === AUDIO_NOTE_RECORDING_STATE.pausedRecording
                                ) && (
                                    <StatusIconContainer
                                        customCursorActive={customCursorActive}
                                        marginLeft={STATUS_ICON_MARGIN_LEFT}
                                        className={HOVER_TARGET_CLASSNAME}
                                        width={buttonLength}
                                        color={themeObj.color[primaryColor]}
                                        {...getTouchMouseEventHandlers({
                                            onMouseEnter: onButtonMouseEnter,
                                            onMouseLeave: onButtonMouseLeave,
                                        })}
                                        {...getPressEventHandlers({ onMouseDown: stopRecord })}
                                    >
                                        <ReactSVG
                                            src={StopIcon}
                                        />
                                    </StatusIconContainer>
                                )}
                            </PlaybackButtons>
                            <SkipButtonLayer>
                                <SkipButton
                                    className={HOVER_TARGET_CLASSNAME}
                                    customCursorActive={customCursorActive}
                                    isPlaying={status === AUDIO_NOTE_RECORDING_STATE.playing}
                                    show={(status === AUDIO_NOTE_RECORDING_STATE.playing
                                || (
                                    status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
                                    && timeElapsed > 0
                                ))}
                                    width={buttonLength}
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: onButtonMouseEnter,
                                        onMouseLeave: onButtonMouseLeave,
                                    })}
                                    {...getPressEventHandlers({ onMouseDown: () => handleSkip(AUDIO_NOTE_SKIP_DIRECTION.backward) })}
                                >
                                    <ReactSVG
                                        src={SkipBackwardIcon}
                                    />
                                </SkipButton>
                                <SkipButton
                                    className={HOVER_TARGET_CLASSNAME}
                                    customCursorActive={customCursorActive}
                                    isPlaying={status === AUDIO_NOTE_RECORDING_STATE.playing}
                                    show={(status === AUDIO_NOTE_RECORDING_STATE.playing
                                || (
                                    status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
                                    && timeElapsed > 0
                                )) && timeElapsed + (15 * MILLISECONDS_IN_A_SECOND) < audioLength}
                                    width={buttonLength}
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: onButtonMouseEnter,
                                        onMouseLeave: onButtonMouseLeave,
                                    })}
                                    {...getPressEventHandlers({ onMouseDown: () => handleSkip(AUDIO_NOTE_SKIP_DIRECTION.forward) })}
                                >
                                    <ReactSVG
                                        src={SkipForwardIcon}
                                    />
                                </SkipButton>
                            </SkipButtonLayer>
                        </PlaybackContainer>
                    )}
                    {status === AUDIO_NOTE_RECORDING_STATE.idle && caption}
                    {shouldRenderInlineWaveform && (
                        <PlaybackMetaContainer
                            ref={playbackMetaContainerRef}
                            compactRecordingSpacing={isRecordingInlineWaveform}
                            annotationPlaybackRows={useAnnotationPlaybackRows}
                        >
                            {shouldShowInlinePlaybackMeta && (
                                <>
                                    <InlineWaveform
                                        bars={visibleWaveformBars}
                                        color={color}
                                        containerRef={waveformLaneRef}
                                        recording={status === AUDIO_NOTE_RECORDING_STATE.recording}
                                        playedBarCount={playedWaveformBarCount}
                                    />
                                    {shouldShowInlinePlaybackTimestamp && (
                                        <InlinePlaybackTimerMask>
                                            <TimerFigure
                                                inlineAutoWidth
                                                right
                                                small={!!small}
                                                recording={
                                                    status === AUDIO_NOTE_RECORDING_STATE.recording
                                                }
                                                width={MAX_TIMESTAMP_WIDTH}
                                                paddingLeft={0}
                                            >
                                                {formatTimestamp(inlinePlaybackTimestampMs)}
                                            </TimerFigure>
                                        </InlinePlaybackTimerMask>
                                    )}
                                </>
                            )}
                        </PlaybackMetaContainer>
                    )}
                    {shouldRenderActionRow && !errorMessage && (
                        <ActionsContainer
                            {...(status === AUDIO_NOTE_RECORDING_STATE.idle && !readOnly
                                ? {
                                    onClick: getRootProps({
                                        onClick: (e) => {
                                            e.nativeEvent.stopPropagation();
                                            openFileSelector();
                                        },
                                        onMouseEnter: onButtonMouseEnter,
                                        onMouseLeave: onButtonMouseLeave,
                                    }).onClick,
                                    onMouseEnter: getRootProps({
                                        onClick: (e) => {
                                            e.nativeEvent.stopPropagation();
                                            openFileSelector();
                                        },
                                        onMouseEnter: onButtonMouseEnter,
                                        onMouseLeave: onButtonMouseLeave,
                                    }).onMouseEnter,
                                    onMouseLeave: getRootProps({
                                        onClick: (e) => {
                                            e.nativeEvent.stopPropagation();
                                            openFileSelector();
                                        },
                                        onMouseEnter: onButtonMouseEnter,
                                        onMouseLeave: onButtonMouseLeave,
                                    }).onMouseLeave,
                                }
                                : {}
                            )}
                            propagateBackground={propagateBackground}
                            buttonLength={SMALL_ACTION_BUTTON_LENGTH}
                            paddingLeft={ACTION_CONTAINER_PADDING_LEFT}
                            paddingRight={ACTION_CONTAINER_PADDING_RIGHT}
                            annotationPlaybackRows={useAnnotationPlaybackRows}
                        >
                            {status !== AUDIO_NOTE_RECORDING_STATE.idle
                        && !isEditing
                        && (
                            <Button
                                center
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={BUTTON_TYPE.floating}
                                height={SMALL_ACTION_BUTTON_LENGTH}
                                width={PLAYBACK_SPEED_BUTTON_WIDTH}
                                textNode={<PlaybackSpeedButtonLabel speed={playbackSpeed} />}
                                background={themeObj.color.neutral800}
                                tooltip={{
                                    active: showAudioNoteTooltips,
                                    text: 'Cycle playback speed',
                                    side: TOOLTIP_TYPE.bottom,
                                }}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                {...getPressEventHandlers({
                                    onTouchStart: handleCyclePlaybackSpeed,
                                    onMouseDown: handleCyclePlaybackSpeed,
                                })}
                            />
                        )}
                            {!isNewAudioNote
                        && shouldRenderTranscriptionStatusBadge
                        && (
                            <TranscriptionStatusBadge
                                data-allow-own-background="true"
                                failure={isFailedTranscriptionStatus}
                                pending={isPendingTranscriptionStatus}
                                stalled={isStalledTranscriptionStatus}
                                skipped={isSkippedTranscriptionStatus}
                                title={transcriptionJob?.error || transcriptionStatusLabel}
                            >
                                {isPendingTranscriptionStatus && <TranscriptionStatusLoadingIcon src={SpinnerIcon} />}
                                <TranscriptionStatusText>
                                    {transcriptionStatusLabel}
                                </TranscriptionStatusText>
                            </TranscriptionStatusBadge>
                        )}
                            {isEditing
                        && editorType === EDITOR_CONTEXT_TYPE.post
                        && status !== AUDIO_NOTE_RECORDING_STATE.idle
                        && (
                            <PostContentDictationActionButton
                                type="button"
                                active={postContentDictationEnabled}
                                accentColor={color}
                                customCursorActive={customCursorActive}
                                aria-label="Toggle Post Dictation"
                                aria-pressed={postContentDictationEnabled}
                                title={showAudioNoteTooltips ? postContentDictationTooltipText : undefined}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                {...getPressEventHandlers({
                                    onTouchStart: togglePostContentDictation,
                                    onMouseDown: togglePostContentDictation,
                                })}
                            >
                                {showAudioNoteTooltips && (
                                    <Tooltip
                                        text={postContentDictationTooltipText}
                                        side={TOOLTIP_TYPE.top}
                                        large
                                    />
                                )}
                                <PostContentDictationActionIndicator
                                    active={postContentDictationEnabled}
                                    accentColor={color}
                                />
                                <PostContentDictationActionLabel>
                                    Post Dictation
                                </PostContentDictationActionLabel>
                            </PostContentDictationActionButton>
                        )}
                            {!isNewAudioNote
                        && audioNoteMediaItem?.transcript
                        && (
                            <Button
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={showTranscript
                                && audioNoteMediaItem?.transcript
                                    ? BUTTON_TYPE.solid
                                    : BUTTON_TYPE.secret}
                                height={SMALL_ACTION_BUTTON_LENGTH}
                                background={transcriptButtonColor}
                                icon={TextIcon}
                                iconFillColor={showTranscript ? themeObj.color.white : undefined}
                                tooltip={{
                                    active: showAudioNoteTooltips,
                                    text: 'Transcript',
                                    side: TOOLTIP_TYPE.bottom,
                                }}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                {...getPressEventHandlers({
                                    onTouchStart: toggleTranscript,
                                    onMouseDown: toggleTranscript,
                                })}
                            />
                        )}
                            {readOnly
                        && isAuthor
                        && (filePath || audioURL)
                        && (
                            <Button
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={BUTTON_TYPE.secret}
                                height={SMALL_ACTION_BUTTON_LENGTH}
                                icon={DownloadIcon}
                                tooltip={{
                                    active: showAudioNoteTooltips,
                                    text: 'Download',
                                    side: TOOLTIP_TYPE.left,
                                }}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                {...getPressEventHandlers({
                                    onTouchStart: handleDownload,
                                    onMouseDown: handleDownload,
                                })}
                            />
                        )}
                            {!readOnly
                        && status !== AUDIO_NOTE_RECORDING_STATE.idle
                        && (
                            isEditing
                                ? (
                                    <Button
                                        className={HOVER_TARGET_CLASSNAME}
                                        customCursorActive={customCursorActive}
                                        type={BUTTON_TYPE.secret}
                                        height={SMALL_ACTION_BUTTON_LENGTH}
                                        icon={SaveIcon}
                                        tooltip={{
                                            active: showAudioNoteTooltips,
                                            text: 'Save',
                                            side: TOOLTIP_TYPE.top,
                                        }}
                                        {...getTouchMouseEventHandlers({
                                            onMouseEnter: onButtonMouseEnter,
                                            onMouseLeave: onButtonMouseLeave,
                                        })}
                                        {...getPressEventHandlers({
                                            onTouchStart: saveAudioNote,
                                            onMouseDown: saveAudioNote,
                                        })}
                                    />
                                )
                                : (
                                    <Button
                                        className={HOVER_TARGET_CLASSNAME}
                                        customCursorActive={customCursorActive}
                                        type={BUTTON_TYPE.secret}
                                        height={SMALL_ACTION_BUTTON_LENGTH}
                                        icon={PencilIcon}
                                        tooltip={{
                                            active: showAudioNoteTooltips,
                                            text: 'Edit',
                                            side: TOOLTIP_TYPE.bottom,
                                        }}
                                        {...getTouchMouseEventHandlers({
                                            onMouseEnter: onButtonMouseEnter,
                                            onMouseLeave: onButtonMouseLeave,
                                        })}
                                        {...getPressEventHandlers({
                                            onTouchStart: (e) => {
                                                e.nativeEvent.stopPropagation();
                                                setIsEditing(true);
                                            },
                                            onMouseDown: (e) => {
                                                e.nativeEvent.stopPropagation();
                                                setIsEditing(true);
                                            },
                                        })}
                                    />
                                )
                        )}
                            {!readOnly
                        && status !== AUDIO_NOTE_RECORDING_STATE.idle
                        && isEditing
                        && !(postContentSpeechGenerationRequested && isNewAudioNote)
                        && (
                            <Button
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={BUTTON_TYPE.secret}
                                height={SMALL_ACTION_BUTTON_LENGTH}
                                icon={RedoIcon}
                                tooltip={{
                                    active: showAudioNoteTooltips,
                                    text: 'Redo',
                                    side: TOOLTIP_TYPE.top,
                                }}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                {...getPressEventHandlers({
                                    onMouseDown: () => {
                                        handleOpenAudioRemovalModal(AUDIO_NOTE_REMOVAL_MODAL_MODE.redo);
                                    },
                                })}
                            />
                        )}
                            {!readOnly
                        && status !== AUDIO_NOTE_RECORDING_STATE.idle
                        && (
                            <Button
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={BUTTON_TYPE.secret}
                                height={SMALL_ACTION_BUTTON_LENGTH}
                                icon={TrashIcon}
                                tooltip={{
                                    active: showAudioNoteTooltips,
                                    text: 'Discard',
                                    side: TOOLTIP_TYPE.top,
                                }}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                {...getPressEventHandlers({
                                    onMouseDown: () => handleOpenAudioRemovalModal(AUDIO_NOTE_REMOVAL_MODAL_MODE.discard),
                                })}
                            />
                        )}
                            {!readOnly
                        && status === AUDIO_NOTE_RECORDING_STATE.idle
                        && editorType === EDITOR_CONTEXT_TYPE.post
                        && (
                            <Button
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={BUTTON_TYPE.secret}
                                height={buttonLength}
                                icon={SpeakerIcon}
                                tooltip={{
                                    active: showAudioNoteTooltips,
                                    text: 'Generate Speech',
                                    side: TOOLTIP_TYPE.bottom,
                                }}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                onClick={requestPostContentSpeechGeneration}
                            />
                        )}
                            {!readOnly
                        && status === AUDIO_NOTE_RECORDING_STATE.idle
                        && (
                            <Button
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={BUTTON_TYPE.secret}
                                height={buttonLength}
                                icon={ImportIcon}
                                tooltip={{
                                    active: showAudioNoteTooltips,
                                    text: 'Import Audio',
                                    side: TOOLTIP_TYPE.bottom,
                                }}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                {...getPressEventHandlers({ onMouseDown: importAudioNote })}
                            />
                        )}
                        </ActionsContainer>
                    )}
                </ButtonContainer>
                {(
                    status === AUDIO_NOTE_RECORDING_STATE.playing
                || status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
                ) && caption}
                {(
                    status === AUDIO_NOTE_RECORDING_STATE.playing
                || status === AUDIO_NOTE_RECORDING_STATE.pausedComplete
                ) && postContentDictationHint}
                <TimeFigureContainer>
                    {showPlaybackProgressBar && (
                        <ProgressBarContainer
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: handleProgressMouseEnter,
                                onMouseMove: handleProgressMouseEnter,
                                onMouseLeave: handleProgressMouseLeave,
                            })}
                            {...getPressEventHandlers({ onMouseDown: moveScrubber })}
                        >
                            <ProgressBar
                                id={PROGRESS_INDICATOR_ID(audioURL)}
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onProgressBarMouseEnter,
                                    onMouseLeave: onProgressBarMouseLeave,
                                })}
                            >
                                <ProgressScrubber
                                    id={PROGRESS_SCRUBBER_ID(audioURL)}
                                    color={color}
                                    width={currentPlaybackProgressPercent}
                                />
                                <PotentialScrubber
                                    id={POTENTIAL_SCRUBBER_ID(audioURL)}
                                    width={potentialPlaybackProgressPercent}
                                />
                            </ProgressBar>
                            <ProgressTimeContainer>
                                <TimerFigure
                                    id={LEFT_TIME_FIGURE_ID(audioURL)}
                                    color={color}
                                    small={!!small}
                                    potentialElapsed={!!potentialElapsed}
                                    width={MAX_TIMESTAMP_WIDTH}
                                    paddingLeft={0}
                                >
                                    {formatTimestamp(potentialElapsed || timeElapsed)}
                                </TimerFigure>
                                <TimerFigure
                                    right
                                    small={!!small}
                                    id={RIGHT_TIME_FIGURE_ID(audioURL)}
                                    width={MAX_TIMESTAMP_WIDTH}
                                    paddingLeft={0}
                                >
                                    {formatTimestamp(potentialElapsed
                                        ? potentialElapsed - audioLength
                                        : timeElapsed - audioLength)}
                                </TimerFigure>
                            </ProgressTimeContainer>
                        </ProgressBarContainer>
                    )}
                </TimeFigureContainer>
                {!errorMessage && (
                    <ReadOnlyTranscript
                        visible={showTranscript}
                        hasTranscript={!!effectiveTranscript}
                        transitionDuration={TRANSCRIPT_TRANSITION_DURATION}
                        transcriptWords={transcriptWords}
                        transcriptPhrases={transcriptPhrases}
                        activeTranscriptWordIndex={showTranscript ? activeTranscriptWordIndex : null}
                        highlightingEnabled={showTranscript && transcriptHighlightingEnabled}
                        color={color}
                        onTranscriptWordMouseEnter={onTranscriptWordMouseEnter}
                        onTranscriptWordMouseLeave={onTranscriptWordMouseLeave}
                        onTranscriptWordPress={skipTranscriptWord}
                    />
                )}
                {audioElementNode}
                {children}
                <DropzoneInput {...getInputProps()} />
            </Container>
        </>
    );
}

// ============= Styled Components ==============

interface ContainerProps {
    selected: boolean,
    focused: boolean,
    color: string,
    small: boolean,
    descriptionInputFocused: boolean,
    padding: number,
    readOnly: boolean,
    editorType: EDITOR_CONTEXT_TYPE,
}
const Container = styled.div<ContainerProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: ${({ readOnly, editorType }) => {
        if (editorType === EDITOR_CONTEXT_TYPE.post) {
            return 'auto';
        }

        return `${readOnly
            ? READ_ONLY_ANNOTATION_AUDIO_NOTE_WIDTH_PX
            : ANNOTATION_AUDIO_NOTE_WIDTH_PX}px`;
    }};
    margin: ${`${0.625 * BODY_FONT_SIZE}px auto`};
    margin-left: ${({ editorType }) => (editorType === EDITOR_CONTEXT_TYPE.annotation
        ? 0
        : 'auto')};
    min-height: ${`${4.375 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.9375 * BODY_FONT_SIZE}px`};
    background-color: ${({ theme }) => theme.color.white};
    padding: ${({ padding }) => `${padding}px`};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
    caret-color: ${({ descriptionInputFocused }) => (descriptionInputFocused
        ? 'auto'
        : 'transparent'
    )};
    border: ${({
        selected,
        focused,
        color,
        theme,
    }) => (selected && focused
        ? `${EDITOR_SELECTION_BORDER_THICKNESS}px solid ${color
            ? setColorLightness(
                color,
                EDITOR_SELECTION_LIGHTNESS_VALUE,
            )
            : theme.verascopeColor.blue200
        }`
        : 'none'
    )};
    transition: box-shadow 0.3s;

    &:hover {
        box-shadow: ${({ theme }) => theme.color.boxShadow300};
    }
`;

interface ButtonContainerProps {
    compactRecordingSpacing: boolean,
    annotationPlaybackRows: boolean,
}
const ButtonContainer = styled.div<ButtonContainerProps>`
    display: grid;
    grid-template-columns: ${({ annotationPlaybackRows, compactRecordingSpacing }) => {
        if (annotationPlaybackRows) {
            return '1fr';
        }

        return compactRecordingSpacing
            ? 'auto minmax(0, 1fr)'
            : 'auto minmax(0, 1fr) auto';
    }};
    grid-template-rows: ${({ annotationPlaybackRows }) => (annotationPlaybackRows
        ? 'auto auto'
        : 'auto'
    )};
    row-gap: ${({ annotationPlaybackRows }) => (annotationPlaybackRows
        ? `${0.5 * BODY_FONT_SIZE}px`
        : 0
    )};
    justify-content: ${({ annotationPlaybackRows }) => (annotationPlaybackRows
        ? 'stretch'
        : 'normal'
    )};
    column-gap: ${({ annotationPlaybackRows, compactRecordingSpacing }) => {
        if (annotationPlaybackRows) {
            return 0;
        }

        return compactRecordingSpacing
            ? `${0.2 * BODY_FONT_SIZE}px`
            : `${0.75 * BODY_FONT_SIZE}px`;
    }};
    align-items: center;
    position: relative;
    height: 100%;
    width: 100%;
    min-width: 0;
    // Rather than be none, we want a white background color
    // so that the cursor darkens on hover events
    background: ${({ theme }) => theme.color.white};
    margin: 0;
    padding: 0;
`;

interface PlaybackContainerProps {
    paddingLeft: number,
}
const PlaybackContainer = styled.div<PlaybackContainerProps>`
    grid-column: 1;
    grid-row: 1;
    justify-self: start;
    position: relative;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    overflow: visible;
    z-index: 3;
    background: ${({ theme }) => theme.color.white};
    padding-left: ${({ paddingLeft }) => `${paddingLeft}px`};
`;

interface PlaybackButtonsProps {
    noPermissions: boolean,
    status: AUDIO_NOTE_RECORDING_STATE,
    buttonLength: number,
}
const PlaybackButtons = styled.div<PlaybackButtonsProps>`
    display: inline-flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    pointer-events: ${({ noPermissions, status }) => (noPermissions
        && status === AUDIO_NOTE_RECORDING_STATE.idle
        ? 'none'
        : 'auto'
    )};
    background: ${({ theme }) => theme.color.white};
    border-radius: ${({ buttonLength }) => `${buttonLength / 2}px`};
    opacity: ${({ noPermissions, status }) => (noPermissions
        && status === AUDIO_NOTE_RECORDING_STATE.idle
        ? 0
        : 1
    )};
`;

interface StatusIconContainerProps {
    customCursorActive: boolean,
    width: number,
    marginLeft?: number,
}
const StatusIconContainer = styled.div<StatusIconContainerProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${({ width }) => `${width}px`};
    height: ${({ width }) => `${width}px`};
    border-radius: ${({ width }) => `${width / 2}px`};
    margin-left: ${({ marginLeft }) => `${marginLeft || 0}px`};
    padding: ${`${0.5 * BODY_FONT_SIZE}px`};
    background-color: ${({ theme }) => theme.color.neutral1100};
    box-shadow: ${`0 ${0.0625 * BODY_FONT_SIZE}px ${0.1875 * BODY_FONT_SIZE}px 0 rgba(0,0,0,0.4), 0 ${0.125 * BODY_FONT_SIZE}px ${0.625 * BODY_FONT_SIZE}px 0 rgba(0,0,0,0.12)`};
    vertical-align: middle;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
        fill: ${({ color, theme }) => {
        if (color) {
            return color;
        }

        return theme.color.neutral600;
    }};
    }

    &:hover {
        box-shadow: ${({ theme }) => theme.color.boxShadow300};
    }
`;

interface PlaybackMetaContainerProps {
    compactRecordingSpacing: boolean,
    annotationPlaybackRows: boolean,
}
const PlaybackMetaContainer = styled.div<PlaybackMetaContainerProps>`
    grid-column: 2;
    justify-self: stretch;
    display: ${({ annotationPlaybackRows }) => (annotationPlaybackRows
        ? 'none'
        : 'grid'
    )};
    grid-template-columns: minmax(0, 1fr) max-content;
    align-items: center;
    column-gap: ${({ compactRecordingSpacing }) => (
        compactRecordingSpacing
            ? `${0.25 * BODY_FONT_SIZE}px`
            : `${0.5 * BODY_FONT_SIZE}px`
    )};
    flex: 1;
    width: 100%;
    min-width: 0;
    padding: ${({ compactRecordingSpacing }) => (compactRecordingSpacing
        ? 0
        : `0 ${0.75 * BODY_FONT_SIZE}px`
    )};
    overflow: hidden;
`;

const InlinePlaybackTimerMask = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    justify-self: end;
    width: auto;
    min-width: 0;
    max-width: 100%;
    align-self: stretch;
    padding-left: ${`${0.25 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    position: relative;
    z-index: 1;
`;

interface InlineWaveformViewportProps {
    recording: boolean,
}
const InlineWaveformViewport = styled.div<InlineWaveformViewportProps>`
    display: flex;
    align-items: center;
    justify-self: stretch;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    height: ${`${1.875 * BODY_FONT_SIZE}px`};
    overflow: hidden;
`;

interface InlineWaveformContainerProps {
    recording: boolean,
}
const InlineWaveformContainer = styled.div<InlineWaveformContainerProps>`
    display: inline-flex;
    align-items: center;
    gap: ${`${DEFAULT_AUDIO_WAVEFORM_BAR_GAP_PX}px`};
    width: max-content;
    min-width: max-content;
    height: 100%;
    padding-left: ${({ recording }) => (recording
        ? `${0.6 * BODY_FONT_SIZE}px`
        : 0
    )};
`;

interface InlineWaveformBarProps {
    color: string,
    height: number,
    played: boolean,
    recording: boolean,
}
const InlineWaveformBar = styled.span<InlineWaveformBarProps>`
    display: block;
    flex: 0 0 ${`${DEFAULT_AUDIO_WAVEFORM_BAR_WIDTH_PX}px`};
    width: ${`${DEFAULT_AUDIO_WAVEFORM_BAR_WIDTH_PX}px`};
    min-width: ${`${DEFAULT_AUDIO_WAVEFORM_BAR_WIDTH_PX}px`};
    max-width: ${`${DEFAULT_AUDIO_WAVEFORM_BAR_WIDTH_PX}px`};
    height: ${({ height }) => `${Math.max(14, Math.round(height * 100))}%`};
    border-radius: ${`${0.1875 * BODY_FONT_SIZE}px`};
    background: ${({
        color,
        played,
        recording,
        theme,
    }) => {
        if (recording) {
            return theme.verascopeColor.red200;
        }

        if (played && color) {
            return color;
        }

        return theme.color.neutral500;
    }};
    opacity: ${({ played, recording }) => (recording || played ? 1 : 0.72)};
    transition: height 180ms ease, opacity 180ms ease, background-color 180ms ease;
`;

const TimeFigureContainer = styled.div``;

interface TimerFigureProps {
    center?: boolean,
    inlineAutoWidth?: boolean,
    right?: boolean,
    small: boolean,
    recording?: boolean,
    potentialElapsed?: boolean,
    width: number,
    paddingLeft: number,
}
const TimerFigure = styled.h3<TimerFigureProps>`
    display: inline-flex;
    align-items: center;
    justify-content: ${({ center, right }) => {
        if (center) {
            return 'center';
        }

        if (right) {
            return 'flex-end';
        }

        return 'flex-start';
    }};
    margin: 0;
    padding: 0;
    padding-left: ${({ paddingLeft }) => `${paddingLeft}px`};
    flex: ${({ inlineAutoWidth, width }) => (
        inlineAutoWidth
            ? '0 1 auto'
            : `0 0 ${width}px`
    )};
    min-width: ${({ inlineAutoWidth, width }) => (
        inlineAutoWidth
            ? '0'
            : `${width}px`
    )};
    height: 100%;
    width: ${({ inlineAutoWidth, width }) => (
        inlineAutoWidth
            ? 'auto'
            : `${width}px`
    )};
    max-width: ${({ width }) => `${width}px`};
    max-height: ${`${5.6875 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ small }) => `${small ? 0.9 : 1}em`};
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    font-variant-numeric: tabular-nums;
    vertical-align: middle;
    color: ${({
        recording,
        potentialElapsed,
        color,
        theme,
    }) => {
        if (recording) {
            return theme.verascopeColor.red200;
        }

        if (potentialElapsed && color) {
            return color;
        }

        return theme.color.neutral700;
    }};
    transition: color 300ms;
`;

interface ContentTextProps {
    small: boolean | undefined,
    buttonLength: number,
    numButtons: number,
    statusIconMarginLeft: number,
    playbackContainerPaddingLeft: number,
    fontSize: number,
    hasError: boolean,
}
const ContentText = styled.h3<ContentTextProps>`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ fontSize }) => `${fontSize}em`};
    padding-left: ${({ small, hasError }) => `${small && !hasError ? 0.9375 * BODY_FONT_SIZE : 0}px`};
    font-weight: 500;
    color: ${({ color, theme }) => color || theme.color.neutral700};
    width: ${({
        numButtons,
        buttonLength,
        statusIconMarginLeft,
        playbackContainerPaddingLeft,
    }) => `calc(100% - ${numButtons > 1
        ? numButtons * buttonLength + playbackContainerPaddingLeft + statusIconMarginLeft
        : numButtons * buttonLength + playbackContainerPaddingLeft
    }px)`};
    text-align: center;
    margin: 0;
`;

const IdleCaptionContainer = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    min-width: 0;
`;

interface ActionsContainerProps {
    buttonLength: number,
    paddingLeft: number,
    paddingRight: number,
    propagateBackground: boolean,
    annotationPlaybackRows: boolean,
}
const ActionsContainer = styled.div<ActionsContainerProps>`
    grid-column: ${({ annotationPlaybackRows }) => (annotationPlaybackRows
        ? 1
        : 3
    )};
    grid-row: ${({ annotationPlaybackRows }) => (annotationPlaybackRows
        ? 2
        : 'auto'
    )};
    justify-self: ${({ annotationPlaybackRows }) => (annotationPlaybackRows
        ? 'stretch'
        : 'end'
    )};
    position: relative;
    z-index: 3;
    display: inline-flex;
    flex-direction: row;
    justify-content: ${({ annotationPlaybackRows }) => (annotationPlaybackRows
        ? 'flex-end'
        : 'space-around'
    )};
    align-items: center;
    column-gap: ${`${0.25 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    border-radius: ${({ buttonLength }) => `${buttonLength / 2}px`}; // makes sure background does bleed
    height: 100%;
    padding-left: ${({ paddingLeft }) => `${paddingLeft}px`};
    padding-right: ${({ paddingRight }) => `${paddingRight}px`};
    vertical-align: middle;

    & > * {
        border-radius: ${({ buttonLength }) => `${buttonLength / 2}px`};
    }

    & > :not(button):not([data-allow-own-background='true']) {
        background: ${({ propagateBackground }) => (propagateBackground
        ? 'inherit'
        : themeObj.color.white
    )};
    }
`;

const ProgressBarContainer = styled.div``;

interface ProgressBarProps {
    customCursorActive: boolean,
}
const ProgressBar = styled.div<ProgressBarProps>`
    position: relative;
    height: ${`${0.375 * BODY_FONT_SIZE}px`};
    width: 100%;
    background: ${({ theme }) => theme.color.neutral400};
    border-radius: ${`${0.1875 * BODY_FONT_SIZE}px`};
    margin: ${`${0.625 * BODY_FONT_SIZE}px 0`};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'default')};
    transition: ${({ theme }) => `
        height 300ms ${theme.motion.delayEasing},
        border-radius 300ms ${theme.motion.delayEasing}
    `};

    ${ProgressBarContainer}:hover & {
        height: ${`${1.125 * BODY_FONT_SIZE}px`};
        border-radius: ${`${0.5625 * BODY_FONT_SIZE}px`};
    }
`;

interface ProgressScrubberProps {
    width: number,
}
const ProgressScrubber = styled.div<ProgressScrubberProps>`
    height: 100%;
    width: ${({ width }) => `${width}%`};
    border-radius: ${`${0.1875 * BODY_FONT_SIZE}px`};
    background-color: ${({ color, theme }) => color || theme.color.white};
`;

const PotentialScrubber = styled(ProgressScrubber)`
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0.4;
`;

const ProgressTimeContainer = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;

const RecordingIcon = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: ${({ theme }) => theme.color.white};
`;

const spin = keyframes`
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
`;

const LoadingIcon = styled(ReactSVG)`
    width: 100%;
    height: 100%;

    & svg {
        width: 100%;
        height: 100%;
        animation: ${spin} 1s linear infinite;
    }
`;

interface TranscriptionStatusBadgeProps {
    failure: boolean,
    pending: boolean,
    stalled: boolean,
    skipped: boolean,
}
const TranscriptionStatusBadge = styled.div<TranscriptionStatusBadgeProps>`
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    align-self: center;
    column-gap: ${`${0.25 * BODY_FONT_SIZE}px`};
    min-height: ${`${1.5 * BODY_FONT_SIZE}px`};
    padding: ${`${0.125 * BODY_FONT_SIZE}px ${0.5 * BODY_FONT_SIZE}px`};
    flex-shrink: 0;
    -webkit-text-size-adjust: none;
    text-size-adjust: none;
    color: ${({
        failure,
        pending,
        stalled,
        skipped,
        theme,
    }) => {
        if (pending) {
            return theme.color.white;
        }

        if (failure) {
            return theme.color.white;
        }

        if (stalled) {
            return theme.color.white;
        }

        if (skipped) {
            return theme.color.white;
        }

        return theme.color.white;
    }};
    background: ${({
        failure,
        pending,
        stalled,
        skipped,
        theme,
    }) => {
        if (pending) {
            return theme.verascopeColor.purple400;
        }

        if (failure) {
            return theme.verascopeColor.red200;
        }

        if (stalled) {
            return theme.verascopeColor.orange200;
        }

        if (skipped) {
            return theme.verascopeColor.yellow100;
        }

        return theme.color.green100;
    }};
`;

const TranscriptionStatusLoadingIcon = styled(LoadingIcon)`
    display: inline-flex;
    align-self: center;
    align-items: center;
    justify-content: center;
    width: ${`${0.875 * BODY_FONT_SIZE}px`};
    height: ${`${0.875 * BODY_FONT_SIZE}px`};
    flex-shrink: 0;

    & > div {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    & svg {
        display: block;
    }

    & svg path {
        fill: ${({ theme }) => theme.color.white};
    }
`;

const TranscriptionStatusText = styled.span`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.7 * BODY_FONT_SIZE}px`};
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: 0.01em;
    white-space: nowrap;
    -webkit-text-size-adjust: none;
    text-size-adjust: none;
`;

interface DetailsContainerProps {
    readOnly: boolean,
}
const DetailsContainer = styled.div<DetailsContainerProps>`
    width: 100%;
    margin-top: ${`${0.5 * BODY_FONT_SIZE}px`};
    margin-left: ${({ readOnly }) => `${readOnly ? 0.25 * BODY_FONT_SIZE : 0}px`};
`;

interface TitleProps {
    fontSize: number,
}
const Title = styled.h2<TitleProps>`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ fontSize }) => `${fontSize * BODY_FONT_SIZE}px`};
    font-weight: 700;
    line-height: 1.2;
    color: ${({ theme }) => theme.color.neutral1000};
    width: 100%;
    margin: 0;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
`;

interface DescriptionProps {
    fontSize: number,
}
const Description = styled.p<DescriptionProps>`
    ${basicEditorFontStyles}
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ fontSize }) => `${fontSize}em`};
    color: ${({ theme }) => theme.color.neutral600};
    width: 100%;
    margin: ${`${0.3125 * BODY_FONT_SIZE}px 0`};
    white-space: pre-wrap;
`;

const PostContentDictationHint = styled.div`
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    align-self: flex-start;
    column-gap: ${`${0.5625 * DEFAULT_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.7 * BODY_FONT_SIZE}px`};
    font-weight: 500;
    line-height: 1.55;
    color: ${({ theme }) => theme.color.white};
    width: fit-content;
    max-width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
    margin-top: ${`${DEFAULT_FONT_SIZE}px`};
    margin-bottom: 0;
    padding: ${`${0.25 * DEFAULT_FONT_SIZE}px ${0.75 * DEFAULT_FONT_SIZE}px ${0.25 * DEFAULT_FONT_SIZE}px ${0.5625 * DEFAULT_FONT_SIZE}px`};
    border-radius: 999px;
    background: ${({ theme }) => theme.verascopeColor.purple400};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
    -webkit-text-size-adjust: none;
    text-size-adjust: none;
`;

const PostContentDictationHintIcon = styled(ReactSVG)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${`${0.75 * DEFAULT_FONT_SIZE}px`};
    height: ${`${0.75 * DEFAULT_FONT_SIZE}px`};

    & > div {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
        fill: ${({ theme }) => theme.color.white};
    }
`;

const PostContentDictationHintText = styled.span`
    display: inline;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    -webkit-text-size-adjust: none;
    text-size-adjust: none;
`;

interface PostContentDictationActionButtonProps {
    active: boolean,
    accentColor?: string,
    customCursorActive: boolean,
}
const PostContentDictationActionButton = styled.button<PostContentDictationActionButtonProps>`
    ${basicEditorFontStyles}
    appearance: none;
    position: relative;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    column-gap: ${`${0.375 * BODY_FONT_SIZE}px`};
    min-height: ${`${1.5 * BODY_FONT_SIZE}px`};
    padding: ${`${0.125 * BODY_FONT_SIZE}px ${0.5 * BODY_FONT_SIZE}px`};
    border: ${`${0.0625 * BODY_FONT_SIZE}px solid`};
    border-color: ${({ active, accentColor, theme }) => {
        if (active && accentColor) {
            return setColorLightness(accentColor, BUTTON_CONTAINER_LIGHTNESS_VALUE);
        }

        if (active) {
            return theme.verascopeColor.purple200;
        }

        return theme.color.neutral300;
    }};
    border-radius: ${`${0.75 * BODY_FONT_SIZE}px`};
    background: ${({ active, accentColor, theme }) => {
        if (active && accentColor) {
            return setColorLightness(accentColor, BUTTON_CONTAINER_LIGHTNESS_VALUE);
        }

        if (active) {
            return theme.verascopeColor.purple100;
        }

        return theme.color.neutral100;
    }};
    color: ${({ active, accentColor, theme }) => {
        if (active && accentColor) {
            return fontColorDiscriminator(setColorLightness(accentColor, BUTTON_CONTAINER_LIGHTNESS_VALUE));
        }

        if (active) {
            return theme.color.neutral1000;
        }

        return theme.color.neutral700;
    }};
    white-space: nowrap;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    transition: ${({ theme }) => `
        background-color 150ms ${theme.motion.eagerEasing},
        border-color 150ms ${theme.motion.eagerEasing},
        color 150ms ${theme.motion.eagerEasing},
        transform 150ms ${theme.motion.eagerEasing}
    `};

    &:hover {
        transform: translateY(-1px);
    }

    &:focus-visible {
        outline: none;
        box-shadow: ${({ theme }) => `0 0 0 ${0.125 * BODY_FONT_SIZE}px ${theme.color.neutral300}`};
    }
`;

interface PostContentDictationActionIndicatorProps {
    active: boolean,
    accentColor?: string,
}
const PostContentDictationActionIndicator = styled.span<PostContentDictationActionIndicatorProps>`
    width: ${`${0.5 * BODY_FONT_SIZE}px`};
    height: ${`${0.5 * BODY_FONT_SIZE}px`};
    border-radius: 50%;
    background: ${({ active, accentColor, theme }) => {
        if (active && accentColor) {
            return accentColor;
        }

        if (active) {
            return theme.verascopeColor.purple400;
        }

        return theme.color.neutral400;
    }};
    flex-shrink: 0;
`;

const PostContentDictationActionLabel = styled.span`
    ${basicEditorFontStyles}
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.72 * BODY_FONT_SIZE}px`};
    font-weight: 700;
    letter-spacing: 0.01em;
    line-height: 1;
`;

interface TitleInputProps {
    top?: number,
    left?: number,
    width?: number,
    fontSize: number,
}
const TitleInput = styled.input<TitleInputProps>`
    position: absolute;
    top: ${({ top }) => `${top}px`};
    left: ${({ left }) => `${left}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ fontSize }) => `${fontSize * BODY_FONT_SIZE}px`};
    font-weight: 700;
    line-height: 1.2;
    width: ${({ width }) => `${width}px`};
    margin: 0;
    margin-top: ${`${0.3125 * BODY_FONT_SIZE}px`};
    padding: ${`${0.15625 * BODY_FONT_SIZE}px ${0.5 * BODY_FONT_SIZE}px`};
    text-transform: none;
    border: none;
    text-decoration: none;
    background-color: ${({ theme }) => theme.color.neutral100};
    border-radius: ${`${1.25 * BODY_FONT_SIZE}px`};
    z-index: ${CURSOR_Z_INDEX - 2};
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;

    &::placeholder {
        color: ${({ theme }) => theme.color.neutral600};
    }
`;

interface DescriptionInputProps {
    top?: number,
    left?: number,
    width?: number,
    fontSize: number,
}
const DescriptionInput = styled.div<DescriptionInputProps>`
    ${basicEditorFontStyles}
    position: absolute;
    top: ${({ top }) => `${top}px`};
    left: ${({ left }) => `${left}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ fontSize }) => `${fontSize}em`};
    color: ${({ theme }) => theme.color.neutral700};
    height: auto;
    width: ${({ width }) => `${width}px`};
    margin: ${`${0.3125 * BODY_FONT_SIZE}px 0px`};
    padding: ${`${0.3125 * BODY_FONT_SIZE}px ${0.5 * BODY_FONT_SIZE}px`};
    text-transform: none;
    border: none;
    text-decoration: none;
    resize: none;
    border-radius: ${`${1.25 * BODY_FONT_SIZE}px`};
    z-index: ${CURSOR_Z_INDEX - 2};

    &:empty:not(:focus):before {
        content: attr(data-placeholder);
        line-height: 1em;
        color: ${({ theme }) => theme.color.neutral500};
    }

    &:focus {
        outline: none;
    }
`;

const AudioElement = styled.audio``;
const AudioSource = styled.source``;
const AudioTrack = styled.track``;
const TitleInputNode = styled(TitleInput)`
    position: relative;
    top: auto;
    left: auto;
    width: 100%;
    opacity: 0;
    pointer-events: none;
`;

const DescriptionInputNode = styled(DescriptionInput)`
    position: relative;
    top: auto;
    left: auto;
    width: 100%;
    opacity: 0;
    pointer-events: none;
`;

const SkipButtonLayer = styled.div`
    display: inline-flex;
    align-items: center;
    column-gap: ${`${0.25 * BODY_FONT_SIZE}px`};
    flex-shrink: 0;
    margin-left: ${`${0.25 * BODY_FONT_SIZE}px`};
    pointer-events: auto;
`;

interface SkipButtonProps {
    width: number,
    show: boolean,
    isPlaying: boolean,
    customCursorActive: boolean,
}
const SkipButton = styled.div<SkipButtonProps>`
    display: ${({ show }) => (show
        ? 'inline-block'
        : 'none'
    )};
    width: ${({ width }) => `${width}px`};
    height: ${({ width }) => `${width}px`};
    box-sizing: border-box;
    flex: 0 0 ${({ width }) => `${width}px`};
    border-radius: ${({ width }) => `${width / 2}px`};
    padding: ${`${0.5 * BODY_FONT_SIZE}px`};
    background-color: ${({ theme }) => theme.color.white};
    opacity: ${({ show }) => (show
        ? 1
        : 0
    )};
    visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
    pointer-events: ${({ show }) => (show
        ? 'auto'
        : 'none'
    )};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    transition: background-color 0.3s, opacity 0.3s;

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
        fill: ${({ theme }) => theme.color.neutral600};
    }

    &:hover {
        background-color: ${({ theme }) => theme.color.neutral500};
    }
`;

interface TranscriptContainerProps {
    visible: boolean,
    transitionDuration: number,
}
const TranscriptContainer = styled.div<TranscriptContainerProps>`
    width: 100%;
    height: ${({ visible }) => `${visible ? 100 : 0}%`};
    margin: ${({ visible }) => `${visible ? 0.3125 * BODY_FONT_SIZE : 0}px`};
    margin-left: ${({ visible }) => `${visible ? 0.25 * BODY_FONT_SIZE : 0}px`};
    margin-bottom: 0;
    border-left: ${({ theme }) => `${0.25 * BODY_FONT_SIZE}px solid ${theme.color.neutral500}`};
    padding: ${({ visible }) => `${visible ? `${0.3125 * BODY_FONT_SIZE}px ${0.625 * BODY_FONT_SIZE}px` : '0px'}`};
    padding-bottom: 0;
    background: ${({ theme }) => theme.color.white};
    transition: ${({ theme, transitionDuration }) => `
        height ${transitionDuration}ms ${theme.motion.overshoot},
        padding ${transitionDuration}ms ${theme.motion.overshoot},
        margin ${transitionDuration}ms ${theme.motion.overshoot}
    `};
    user-select: none;
    -webkit-user-select: none;
`;

interface TranscriptWordProps {
    color: string,
}
const TranscriptWord = styled.span<TranscriptWordProps>`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 500;
    font-size: 1em;
    line-height: 1.5em;
    color: ${({ theme }) => theme.color.neutral800};
    background: ${({ theme }) => theme.color.white};
    user-select: none;
    -webkit-user-select: none;

    &[data-highlighted='true'] {
        color: ${({ color }) => fontColorDiscriminator(color)};
        background: ${({ color }) => color};
    }
`;

const SavingAudioNoteTooltipContainer = styled.div`
    display: flex;
    flex-direction: row;
    position: absolute;
    top: ${`${-2 * BODY_FONT_SIZE}px`};
    left: 0;
    border-radius: ${`${0.875 * BODY_FONT_SIZE}px`};
    background-color: ${({ theme }) => theme.color.neutral900};
    padding: ${`${0.25 * BODY_FONT_SIZE}px`};
    padding-left: ${`${0.65 * BODY_FONT_SIZE}px`};
    box-shadow: ${({ theme }) => theme.color.boxShadow300};

    & svg {
        width: ${`${BODY_FONT_SIZE}px`};
        height: ${`${BODY_FONT_SIZE}px`};
    }
`;

const SavingAudioNoteTooltipText = styled.h3`
    ${basicEditorFontStyles}
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 1em;
    font-weight: 400;
    color: ${({ theme }) => theme.color.white};
    margin: 0;
    margin-right: ${`${0.3 * BODY_FONT_SIZE}px`};
    white-space: pre;
`;

export default AudioNote;
